import _extends from '@babel/runtime/helpers/extends';
import React, { useState, useMemo, useRef, useContext } from 'react';
import PropTypes from 'prop-types';
import { Box, Input, utils, IconCalendar, Typography, Button, Icon, IconClock } from '@screentone/core';
import _objectWithoutPropertiesLoose from '@babel/runtime/helpers/objectWithoutPropertiesLoose';
import ReactDOM from 'react-dom';

function requiredArgs(required, args) {
  if (args.length < required) {
    throw new TypeError(required + ' argument' + (required > 1 ? 's' : '') + ' required, but only ' + args.length + ' present');
  }
}

/**
 * @name isDate
 * @category Common Helpers
 * @summary Is the given value a date?
 *
 * @description
 * Returns true if the given value is an instance of Date. The function works for dates transferred across iframes.
 *
 * ### v2.0.0 breaking changes:
 *
 * - [Changes that are common for the whole library](https://github.com/date-fns/date-fns/blob/master/docs/upgradeGuide.md#Common-Changes).
 *
 * @param {*} value - the value to check
 * @returns {boolean} true if the given value is a date
 * @throws {TypeError} 1 arguments required
 *
 * @example
 * // For a valid date:
 * const result = isDate(new Date())
 * //=> true
 *
 * @example
 * // For an invalid date:
 * const result = isDate(new Date(NaN))
 * //=> true
 *
 * @example
 * // For some value:
 * const result = isDate('2014-02-31')
 * //=> false
 *
 * @example
 * // For an object:
 * const result = isDate({})
 * //=> false
 */

function isDate(value) {
  requiredArgs(1, arguments);
  return value instanceof Date || typeof value === 'object' && Object.prototype.toString.call(value) === '[object Date]';
}

/**
 * @name toDate
 * @category Common Helpers
 * @summary Convert the given argument to an instance of Date.
 *
 * @description
 * Convert the given argument to an instance of Date.
 *
 * If the argument is an instance of Date, the function returns its clone.
 *
 * If the argument is a number, it is treated as a timestamp.
 *
 * If the argument is none of the above, the function returns Invalid Date.
 *
 * **Note**: *all* Date arguments passed to any *date-fns* function is processed by `toDate`.
 *
 * @param {Date|Number} argument - the value to convert
 * @returns {Date} the parsed date in the local time zone
 * @throws {TypeError} 1 argument required
 *
 * @example
 * // Clone the date:
 * const result = toDate(new Date(2014, 1, 11, 11, 30, 30))
 * //=> Tue Feb 11 2014 11:30:30
 *
 * @example
 * // Convert the timestamp to date:
 * const result = toDate(1392098430000)
 * //=> Tue Feb 11 2014 11:30:30
 */

function toDate(argument) {
  requiredArgs(1, arguments);
  var argStr = Object.prototype.toString.call(argument); // Clone the date

  if (argument instanceof Date || typeof argument === 'object' && argStr === '[object Date]') {
    // Prevent the date to lose the milliseconds when passed to new Date() in IE10
    return new Date(argument.getTime());
  } else if (typeof argument === 'number' || argStr === '[object Number]') {
    return new Date(argument);
  } else {
    if ((typeof argument === 'string' || argStr === '[object String]') && typeof console !== 'undefined') {
      // eslint-disable-next-line no-console
      console.warn("Starting with v2.0.0-beta.1 date-fns doesn't accept strings as date arguments. Please use `parseISO` to parse strings. See: https://git.io/fjule"); // eslint-disable-next-line no-console

      console.warn(new Error().stack);
    }

    return new Date(NaN);
  }
}

/**
 * @name isValid
 * @category Common Helpers
 * @summary Is the given date valid?
 *
 * @description
 * Returns false if argument is Invalid Date and true otherwise.
 * Argument is converted to Date using `toDate`. See [toDate]{@link https://date-fns.org/docs/toDate}
 * Invalid Date is a Date, whose time value is NaN.
 *
 * Time value of Date: http://es5.github.io/#x15.9.1.1
 *
 * ### v2.0.0 breaking changes:
 *
 * - [Changes that are common for the whole library](https://github.com/date-fns/date-fns/blob/master/docs/upgradeGuide.md#Common-Changes).
 *
 * - Now `isValid` doesn't throw an exception
 *   if the first argument is not an instance of Date.
 *   Instead, argument is converted beforehand using `toDate`.
 *
 *   Examples:
 *
 *   | `isValid` argument        | Before v2.0.0 | v2.0.0 onward |
 *   |---------------------------|---------------|---------------|
 *   | `new Date()`              | `true`        | `true`        |
 *   | `new Date('2016-01-01')`  | `true`        | `true`        |
 *   | `new Date('')`            | `false`       | `false`       |
 *   | `new Date(1488370835081)` | `true`        | `true`        |
 *   | `new Date(NaN)`           | `false`       | `false`       |
 *   | `'2016-01-01'`            | `TypeError`   | `false`       |
 *   | `''`                      | `TypeError`   | `false`       |
 *   | `1488370835081`           | `TypeError`   | `true`        |
 *   | `NaN`                     | `TypeError`   | `false`       |
 *
 *   We introduce this change to make *date-fns* consistent with ECMAScript behavior
 *   that try to coerce arguments to the expected type
 *   (which is also the case with other *date-fns* functions).
 *
 * @param {*} date - the date to check
 * @returns {Boolean} the date is valid
 * @throws {TypeError} 1 argument required
 *
 * @example
 * // For the valid date:
 * const result = isValid(new Date(2014, 1, 31))
 * //=> true
 *
 * @example
 * // For the value, convertable into a date:
 * const result = isValid(1393804800000)
 * //=> true
 *
 * @example
 * // For the invalid date:
 * const result = isValid(new Date(''))
 * //=> false
 */

function isValid(dirtyDate) {
  requiredArgs(1, arguments);

  if (!isDate(dirtyDate) && typeof dirtyDate !== 'number') {
    return false;
  }

  var date = toDate(dirtyDate);
  return !isNaN(Number(date));
}

var formatDistanceLocale = {
  lessThanXSeconds: {
    one: 'less than a second',
    other: 'less than {{count}} seconds'
  },
  xSeconds: {
    one: '1 second',
    other: '{{count}} seconds'
  },
  halfAMinute: 'half a minute',
  lessThanXMinutes: {
    one: 'less than a minute',
    other: 'less than {{count}} minutes'
  },
  xMinutes: {
    one: '1 minute',
    other: '{{count}} minutes'
  },
  aboutXHours: {
    one: 'about 1 hour',
    other: 'about {{count}} hours'
  },
  xHours: {
    one: '1 hour',
    other: '{{count}} hours'
  },
  xDays: {
    one: '1 day',
    other: '{{count}} days'
  },
  aboutXWeeks: {
    one: 'about 1 week',
    other: 'about {{count}} weeks'
  },
  xWeeks: {
    one: '1 week',
    other: '{{count}} weeks'
  },
  aboutXMonths: {
    one: 'about 1 month',
    other: 'about {{count}} months'
  },
  xMonths: {
    one: '1 month',
    other: '{{count}} months'
  },
  aboutXYears: {
    one: 'about 1 year',
    other: 'about {{count}} years'
  },
  xYears: {
    one: '1 year',
    other: '{{count}} years'
  },
  overXYears: {
    one: 'over 1 year',
    other: 'over {{count}} years'
  },
  almostXYears: {
    one: 'almost 1 year',
    other: 'almost {{count}} years'
  }
};

var formatDistance = function (token, count, options) {
  var result;
  var tokenValue = formatDistanceLocale[token];

  if (typeof tokenValue === 'string') {
    result = tokenValue;
  } else if (count === 1) {
    result = tokenValue.one;
  } else {
    result = tokenValue.other.replace('{{count}}', count.toString());
  }

  if (options !== null && options !== void 0 && options.addSuffix) {
    if (options.comparison && options.comparison > 0) {
      return 'in ' + result;
    } else {
      return result + ' ago';
    }
  }

  return result;
};

var formatDistance$1 = formatDistance;

function buildFormatLongFn(args) {
  return function () {
    var options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    // TODO: Remove String()
    var width = options.width ? String(options.width) : args.defaultWidth;
    var format = args.formats[width] || args.formats[args.defaultWidth];
    return format;
  };
}

var dateFormats = {
  full: 'EEEE, MMMM do, y',
  long: 'MMMM do, y',
  medium: 'MMM d, y',
  short: 'MM/dd/yyyy'
};
var timeFormats = {
  full: 'h:mm:ss a zzzz',
  long: 'h:mm:ss a z',
  medium: 'h:mm:ss a',
  short: 'h:mm a'
};
var dateTimeFormats = {
  full: "{{date}} 'at' {{time}}",
  long: "{{date}} 'at' {{time}}",
  medium: '{{date}}, {{time}}',
  short: '{{date}}, {{time}}'
};
var formatLong = {
  date: buildFormatLongFn({
    formats: dateFormats,
    defaultWidth: 'full'
  }),
  time: buildFormatLongFn({
    formats: timeFormats,
    defaultWidth: 'full'
  }),
  dateTime: buildFormatLongFn({
    formats: dateTimeFormats,
    defaultWidth: 'full'
  })
};
var formatLong$1 = formatLong;

var formatRelativeLocale = {
  lastWeek: "'last' eeee 'at' p",
  yesterday: "'yesterday at' p",
  today: "'today at' p",
  tomorrow: "'tomorrow at' p",
  nextWeek: "eeee 'at' p",
  other: 'P'
};

var formatRelative = function (token, _date, _baseDate, _options) {
  return formatRelativeLocale[token];
};

var formatRelative$1 = formatRelative;

function buildLocalizeFn(args) {
  return function (dirtyIndex, dirtyOptions) {
    var options = dirtyOptions || {};
    var context = options.context ? String(options.context) : 'standalone';
    var valuesArray;

    if (context === 'formatting' && args.formattingValues) {
      var defaultWidth = args.defaultFormattingWidth || args.defaultWidth;
      var width = options.width ? String(options.width) : defaultWidth;
      valuesArray = args.formattingValues[width] || args.formattingValues[defaultWidth];
    } else {
      var _defaultWidth = args.defaultWidth;

      var _width = options.width ? String(options.width) : args.defaultWidth;

      valuesArray = args.values[_width] || args.values[_defaultWidth];
    }

    var index = args.argumentCallback ? args.argumentCallback(dirtyIndex) : dirtyIndex; // @ts-ignore: For some reason TypeScript just don't want to match it, no matter how hard we try. I challenge you to try to remove it!

    return valuesArray[index];
  };
}

var eraValues = {
  narrow: ['B', 'A'],
  abbreviated: ['BC', 'AD'],
  wide: ['Before Christ', 'Anno Domini']
};
var quarterValues = {
  narrow: ['1', '2', '3', '4'],
  abbreviated: ['Q1', 'Q2', 'Q3', 'Q4'],
  wide: ['1st quarter', '2nd quarter', '3rd quarter', '4th quarter']
}; // Note: in English, the names of days of the week and months are capitalized.
// If you are making a new locale based on this one, check if the same is true for the language you're working on.
// Generally, formatted dates should look like they are in the middle of a sentence,
// e.g. in Spanish language the weekdays and months should be in the lowercase.

var monthValues = {
  narrow: ['J', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D'],
  abbreviated: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
  wide: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
};
var dayValues = {
  narrow: ['S', 'M', 'T', 'W', 'T', 'F', 'S'],
  short: ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'],
  abbreviated: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
  wide: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
};
var dayPeriodValues = {
  narrow: {
    am: 'a',
    pm: 'p',
    midnight: 'mi',
    noon: 'n',
    morning: 'morning',
    afternoon: 'afternoon',
    evening: 'evening',
    night: 'night'
  },
  abbreviated: {
    am: 'AM',
    pm: 'PM',
    midnight: 'midnight',
    noon: 'noon',
    morning: 'morning',
    afternoon: 'afternoon',
    evening: 'evening',
    night: 'night'
  },
  wide: {
    am: 'a.m.',
    pm: 'p.m.',
    midnight: 'midnight',
    noon: 'noon',
    morning: 'morning',
    afternoon: 'afternoon',
    evening: 'evening',
    night: 'night'
  }
};
var formattingDayPeriodValues = {
  narrow: {
    am: 'a',
    pm: 'p',
    midnight: 'mi',
    noon: 'n',
    morning: 'in the morning',
    afternoon: 'in the afternoon',
    evening: 'in the evening',
    night: 'at night'
  },
  abbreviated: {
    am: 'AM',
    pm: 'PM',
    midnight: 'midnight',
    noon: 'noon',
    morning: 'in the morning',
    afternoon: 'in the afternoon',
    evening: 'in the evening',
    night: 'at night'
  },
  wide: {
    am: 'a.m.',
    pm: 'p.m.',
    midnight: 'midnight',
    noon: 'noon',
    morning: 'in the morning',
    afternoon: 'in the afternoon',
    evening: 'in the evening',
    night: 'at night'
  }
};

var ordinalNumber = function (dirtyNumber, _options) {
  var number = Number(dirtyNumber); // If ordinal numbers depend on context, for example,
  // if they are different for different grammatical genders,
  // use `options.unit`.
  //
  // `unit` can be 'year', 'quarter', 'month', 'week', 'date', 'dayOfYear',
  // 'day', 'hour', 'minute', 'second'.

  var rem100 = number % 100;

  if (rem100 > 20 || rem100 < 10) {
    switch (rem100 % 10) {
      case 1:
        return number + 'st';

      case 2:
        return number + 'nd';

      case 3:
        return number + 'rd';
    }
  }

  return number + 'th';
};

var localize = {
  ordinalNumber: ordinalNumber,
  era: buildLocalizeFn({
    values: eraValues,
    defaultWidth: 'wide'
  }),
  quarter: buildLocalizeFn({
    values: quarterValues,
    defaultWidth: 'wide',
    argumentCallback: function (quarter) {
      return quarter - 1;
    }
  }),
  month: buildLocalizeFn({
    values: monthValues,
    defaultWidth: 'wide'
  }),
  day: buildLocalizeFn({
    values: dayValues,
    defaultWidth: 'wide'
  }),
  dayPeriod: buildLocalizeFn({
    values: dayPeriodValues,
    defaultWidth: 'wide',
    formattingValues: formattingDayPeriodValues,
    defaultFormattingWidth: 'wide'
  })
};
var localize$1 = localize;

function buildMatchFn(args) {
  return function (string) {
    var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    var width = options.width;
    var matchPattern = width && args.matchPatterns[width] || args.matchPatterns[args.defaultMatchWidth];
    var matchResult = string.match(matchPattern);

    if (!matchResult) {
      return null;
    }

    var matchedString = matchResult[0];
    var parsePatterns = width && args.parsePatterns[width] || args.parsePatterns[args.defaultParseWidth];
    var key = Array.isArray(parsePatterns) ? findIndex(parsePatterns, function (pattern) {
      return pattern.test(matchedString);
    }) : findKey(parsePatterns, function (pattern) {
      return pattern.test(matchedString);
    });
    var value;
    value = args.valueCallback ? args.valueCallback(key) : key;
    value = options.valueCallback ? options.valueCallback(value) : value;
    var rest = string.slice(matchedString.length);
    return {
      value: value,
      rest: rest
    };
  };
}

function findKey(object, predicate) {
  for (var key in object) {
    if (object.hasOwnProperty(key) && predicate(object[key])) {
      return key;
    }
  }

  return undefined;
}

function findIndex(array, predicate) {
  for (var key = 0; key < array.length; key++) {
    if (predicate(array[key])) {
      return key;
    }
  }

  return undefined;
}

function buildMatchPatternFn(args) {
  return function (string) {
    var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    var matchResult = string.match(args.matchPattern);
    if (!matchResult) return null;
    var matchedString = matchResult[0];
    var parseResult = string.match(args.parsePattern);
    if (!parseResult) return null;
    var value = args.valueCallback ? args.valueCallback(parseResult[0]) : parseResult[0];
    value = options.valueCallback ? options.valueCallback(value) : value;
    var rest = string.slice(matchedString.length);
    return {
      value: value,
      rest: rest
    };
  };
}

var matchOrdinalNumberPattern = /^(\d+)(th|st|nd|rd)?/i;
var parseOrdinalNumberPattern = /\d+/i;
var matchEraPatterns = {
  narrow: /^(b|a)/i,
  abbreviated: /^(b\.?\s?c\.?|b\.?\s?c\.?\s?e\.?|a\.?\s?d\.?|c\.?\s?e\.?)/i,
  wide: /^(before christ|before common era|anno domini|common era)/i
};
var parseEraPatterns = {
  any: [/^b/i, /^(a|c)/i]
};
var matchQuarterPatterns = {
  narrow: /^[1234]/i,
  abbreviated: /^q[1234]/i,
  wide: /^[1234](th|st|nd|rd)? quarter/i
};
var parseQuarterPatterns = {
  any: [/1/i, /2/i, /3/i, /4/i]
};
var matchMonthPatterns = {
  narrow: /^[jfmasond]/i,
  abbreviated: /^(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)/i,
  wide: /^(january|february|march|april|may|june|july|august|september|october|november|december)/i
};
var parseMonthPatterns = {
  narrow: [/^j/i, /^f/i, /^m/i, /^a/i, /^m/i, /^j/i, /^j/i, /^a/i, /^s/i, /^o/i, /^n/i, /^d/i],
  any: [/^ja/i, /^f/i, /^mar/i, /^ap/i, /^may/i, /^jun/i, /^jul/i, /^au/i, /^s/i, /^o/i, /^n/i, /^d/i]
};
var matchDayPatterns = {
  narrow: /^[smtwf]/i,
  short: /^(su|mo|tu|we|th|fr|sa)/i,
  abbreviated: /^(sun|mon|tue|wed|thu|fri|sat)/i,
  wide: /^(sunday|monday|tuesday|wednesday|thursday|friday|saturday)/i
};
var parseDayPatterns = {
  narrow: [/^s/i, /^m/i, /^t/i, /^w/i, /^t/i, /^f/i, /^s/i],
  any: [/^su/i, /^m/i, /^tu/i, /^w/i, /^th/i, /^f/i, /^sa/i]
};
var matchDayPeriodPatterns = {
  narrow: /^(a|p|mi|n|(in the|at) (morning|afternoon|evening|night))/i,
  any: /^([ap]\.?\s?m\.?|midnight|noon|(in the|at) (morning|afternoon|evening|night))/i
};
var parseDayPeriodPatterns = {
  any: {
    am: /^a/i,
    pm: /^p/i,
    midnight: /^mi/i,
    noon: /^no/i,
    morning: /morning/i,
    afternoon: /afternoon/i,
    evening: /evening/i,
    night: /night/i
  }
};
var match = {
  ordinalNumber: buildMatchPatternFn({
    matchPattern: matchOrdinalNumberPattern,
    parsePattern: parseOrdinalNumberPattern,
    valueCallback: function (value) {
      return parseInt(value, 10);
    }
  }),
  era: buildMatchFn({
    matchPatterns: matchEraPatterns,
    defaultMatchWidth: 'wide',
    parsePatterns: parseEraPatterns,
    defaultParseWidth: 'any'
  }),
  quarter: buildMatchFn({
    matchPatterns: matchQuarterPatterns,
    defaultMatchWidth: 'wide',
    parsePatterns: parseQuarterPatterns,
    defaultParseWidth: 'any',
    valueCallback: function (index) {
      return index + 1;
    }
  }),
  month: buildMatchFn({
    matchPatterns: matchMonthPatterns,
    defaultMatchWidth: 'wide',
    parsePatterns: parseMonthPatterns,
    defaultParseWidth: 'any'
  }),
  day: buildMatchFn({
    matchPatterns: matchDayPatterns,
    defaultMatchWidth: 'wide',
    parsePatterns: parseDayPatterns,
    defaultParseWidth: 'any'
  }),
  dayPeriod: buildMatchFn({
    matchPatterns: matchDayPeriodPatterns,
    defaultMatchWidth: 'any',
    parsePatterns: parseDayPeriodPatterns,
    defaultParseWidth: 'any'
  })
};
var match$1 = match;

/**
 * @type {Locale}
 * @category Locales
 * @summary English locale (United States).
 * @language English
 * @iso-639-2 eng
 * @author Sasha Koss [@kossnocorp]{@link https://github.com/kossnocorp}
 * @author Lesha Koss [@leshakoss]{@link https://github.com/leshakoss}
 */
var locale = {
  code: 'en-US',
  formatDistance: formatDistance$1,
  formatLong: formatLong$1,
  formatRelative: formatRelative$1,
  localize: localize$1,
  match: match$1,
  options: {
    weekStartsOn: 0
    /* Sunday */
    ,
    firstWeekContainsDate: 1
  }
};
var defaultLocale = locale;

function toInteger(dirtyNumber) {
  if (dirtyNumber === null || dirtyNumber === true || dirtyNumber === false) {
    return NaN;
  }

  var number = Number(dirtyNumber);

  if (isNaN(number)) {
    return number;
  }

  return number < 0 ? Math.ceil(number) : Math.floor(number);
}

/**
 * @name addMilliseconds
 * @category Millisecond Helpers
 * @summary Add the specified number of milliseconds to the given date.
 *
 * @description
 * Add the specified number of milliseconds to the given date.
 *
 * ### v2.0.0 breaking changes:
 *
 * - [Changes that are common for the whole library](https://github.com/date-fns/date-fns/blob/master/docs/upgradeGuide.md#Common-Changes).
 *
 * @param {Date|Number} date - the date to be changed
 * @param {Number} amount - the amount of milliseconds to be added. Positive decimals will be rounded using `Math.floor`, decimals less than zero will be rounded using `Math.ceil`.
 * @returns {Date} the new date with the milliseconds added
 * @throws {TypeError} 2 arguments required
 *
 * @example
 * // Add 750 milliseconds to 10 July 2014 12:45:30.000:
 * const result = addMilliseconds(new Date(2014, 6, 10, 12, 45, 30, 0), 750)
 * //=> Thu Jul 10 2014 12:45:30.750
 */

function addMilliseconds(dirtyDate, dirtyAmount) {
  requiredArgs(2, arguments);
  var timestamp = toDate(dirtyDate).getTime();
  var amount = toInteger(dirtyAmount);
  return new Date(timestamp + amount);
}

/**
 * @name subMilliseconds
 * @category Millisecond Helpers
 * @summary Subtract the specified number of milliseconds from the given date.
 *
 * @description
 * Subtract the specified number of milliseconds from the given date.
 *
 * ### v2.0.0 breaking changes:
 *
 * - [Changes that are common for the whole library](https://github.com/date-fns/date-fns/blob/master/docs/upgradeGuide.md#Common-Changes).
 *
 * @param {Date|Number} date - the date to be changed
 * @param {Number} amount - the amount of milliseconds to be subtracted. Positive decimals will be rounded using `Math.floor`, decimals less than zero will be rounded using `Math.ceil`.
 * @returns {Date} the new date with the milliseconds subtracted
 * @throws {TypeError} 2 arguments required
 *
 * @example
 * // Subtract 750 milliseconds from 10 July 2014 12:45:30.000:
 * const result = subMilliseconds(new Date(2014, 6, 10, 12, 45, 30, 0), 750)
 * //=> Thu Jul 10 2014 12:45:29.250
 */

function subMilliseconds(dirtyDate, dirtyAmount) {
  requiredArgs(2, arguments);
  var amount = toInteger(dirtyAmount);
  return addMilliseconds(dirtyDate, -amount);
}

var MILLISECONDS_IN_DAY$1 = 86400000; // This function will be a part of public API when UTC function will be implemented.
// See issue: https://github.com/date-fns/date-fns/issues/376

function getUTCDayOfYear(dirtyDate) {
  requiredArgs(1, arguments);
  var date = toDate(dirtyDate);
  var timestamp = date.getTime();
  date.setUTCMonth(0, 1);
  date.setUTCHours(0, 0, 0, 0);
  var startOfYearTimestamp = date.getTime();
  var difference = timestamp - startOfYearTimestamp;
  return Math.floor(difference / MILLISECONDS_IN_DAY$1) + 1;
}

// See issue: https://github.com/date-fns/date-fns/issues/376

function startOfUTCISOWeek(dirtyDate) {
  requiredArgs(1, arguments);
  var weekStartsOn = 1;
  var date = toDate(dirtyDate);
  var day = date.getUTCDay();
  var diff = (day < weekStartsOn ? 7 : 0) + day - weekStartsOn;
  date.setUTCDate(date.getUTCDate() - diff);
  date.setUTCHours(0, 0, 0, 0);
  return date;
}

// See issue: https://github.com/date-fns/date-fns/issues/376

function getUTCISOWeekYear(dirtyDate) {
  requiredArgs(1, arguments);
  var date = toDate(dirtyDate);
  var year = date.getUTCFullYear();
  var fourthOfJanuaryOfNextYear = new Date(0);
  fourthOfJanuaryOfNextYear.setUTCFullYear(year + 1, 0, 4);
  fourthOfJanuaryOfNextYear.setUTCHours(0, 0, 0, 0);
  var startOfNextYear = startOfUTCISOWeek(fourthOfJanuaryOfNextYear);
  var fourthOfJanuaryOfThisYear = new Date(0);
  fourthOfJanuaryOfThisYear.setUTCFullYear(year, 0, 4);
  fourthOfJanuaryOfThisYear.setUTCHours(0, 0, 0, 0);
  var startOfThisYear = startOfUTCISOWeek(fourthOfJanuaryOfThisYear);

  if (date.getTime() >= startOfNextYear.getTime()) {
    return year + 1;
  } else if (date.getTime() >= startOfThisYear.getTime()) {
    return year;
  } else {
    return year - 1;
  }
}

// See issue: https://github.com/date-fns/date-fns/issues/376

function startOfUTCISOWeekYear(dirtyDate) {
  requiredArgs(1, arguments);
  var year = getUTCISOWeekYear(dirtyDate);
  var fourthOfJanuary = new Date(0);
  fourthOfJanuary.setUTCFullYear(year, 0, 4);
  fourthOfJanuary.setUTCHours(0, 0, 0, 0);
  var date = startOfUTCISOWeek(fourthOfJanuary);
  return date;
}

var MILLISECONDS_IN_WEEK$1 = 604800000; // This function will be a part of public API when UTC function will be implemented.
// See issue: https://github.com/date-fns/date-fns/issues/376

function getUTCISOWeek(dirtyDate) {
  requiredArgs(1, arguments);
  var date = toDate(dirtyDate);
  var diff = startOfUTCISOWeek(date).getTime() - startOfUTCISOWeekYear(date).getTime(); // Round the number of days to the nearest integer
  // because the number of milliseconds in a week is not constant
  // (e.g. it's different in the week of the daylight saving time clock shift)

  return Math.round(diff / MILLISECONDS_IN_WEEK$1) + 1;
}

// See issue: https://github.com/date-fns/date-fns/issues/376

function startOfUTCWeek(dirtyDate, dirtyOptions) {
  requiredArgs(1, arguments);
  var options = dirtyOptions || {};
  var locale = options.locale;
  var localeWeekStartsOn = locale && locale.options && locale.options.weekStartsOn;
  var defaultWeekStartsOn = localeWeekStartsOn == null ? 0 : toInteger(localeWeekStartsOn);
  var weekStartsOn = options.weekStartsOn == null ? defaultWeekStartsOn : toInteger(options.weekStartsOn); // Test if weekStartsOn is between 0 and 6 _and_ is not NaN

  if (!(weekStartsOn >= 0 && weekStartsOn <= 6)) {
    throw new RangeError('weekStartsOn must be between 0 and 6 inclusively');
  }

  var date = toDate(dirtyDate);
  var day = date.getUTCDay();
  var diff = (day < weekStartsOn ? 7 : 0) + day - weekStartsOn;
  date.setUTCDate(date.getUTCDate() - diff);
  date.setUTCHours(0, 0, 0, 0);
  return date;
}

// See issue: https://github.com/date-fns/date-fns/issues/376

function getUTCWeekYear(dirtyDate, dirtyOptions) {
  requiredArgs(1, arguments);
  var date = toDate(dirtyDate);
  var year = date.getUTCFullYear();
  var options = dirtyOptions || {};
  var locale = options.locale;
  var localeFirstWeekContainsDate = locale && locale.options && locale.options.firstWeekContainsDate;
  var defaultFirstWeekContainsDate = localeFirstWeekContainsDate == null ? 1 : toInteger(localeFirstWeekContainsDate);
  var firstWeekContainsDate = options.firstWeekContainsDate == null ? defaultFirstWeekContainsDate : toInteger(options.firstWeekContainsDate); // Test if weekStartsOn is between 1 and 7 _and_ is not NaN

  if (!(firstWeekContainsDate >= 1 && firstWeekContainsDate <= 7)) {
    throw new RangeError('firstWeekContainsDate must be between 1 and 7 inclusively');
  }

  var firstWeekOfNextYear = new Date(0);
  firstWeekOfNextYear.setUTCFullYear(year + 1, 0, firstWeekContainsDate);
  firstWeekOfNextYear.setUTCHours(0, 0, 0, 0);
  var startOfNextYear = startOfUTCWeek(firstWeekOfNextYear, dirtyOptions);
  var firstWeekOfThisYear = new Date(0);
  firstWeekOfThisYear.setUTCFullYear(year, 0, firstWeekContainsDate);
  firstWeekOfThisYear.setUTCHours(0, 0, 0, 0);
  var startOfThisYear = startOfUTCWeek(firstWeekOfThisYear, dirtyOptions);

  if (date.getTime() >= startOfNextYear.getTime()) {
    return year + 1;
  } else if (date.getTime() >= startOfThisYear.getTime()) {
    return year;
  } else {
    return year - 1;
  }
}

// See issue: https://github.com/date-fns/date-fns/issues/376

function startOfUTCWeekYear(dirtyDate, dirtyOptions) {
  requiredArgs(1, arguments);
  var options = dirtyOptions || {};
  var locale = options.locale;
  var localeFirstWeekContainsDate = locale && locale.options && locale.options.firstWeekContainsDate;
  var defaultFirstWeekContainsDate = localeFirstWeekContainsDate == null ? 1 : toInteger(localeFirstWeekContainsDate);
  var firstWeekContainsDate = options.firstWeekContainsDate == null ? defaultFirstWeekContainsDate : toInteger(options.firstWeekContainsDate);
  var year = getUTCWeekYear(dirtyDate, dirtyOptions);
  var firstWeek = new Date(0);
  firstWeek.setUTCFullYear(year, 0, firstWeekContainsDate);
  firstWeek.setUTCHours(0, 0, 0, 0);
  var date = startOfUTCWeek(firstWeek, dirtyOptions);
  return date;
}

var MILLISECONDS_IN_WEEK = 604800000; // This function will be a part of public API when UTC function will be implemented.
// See issue: https://github.com/date-fns/date-fns/issues/376

function getUTCWeek(dirtyDate, options) {
  requiredArgs(1, arguments);
  var date = toDate(dirtyDate);
  var diff = startOfUTCWeek(date, options).getTime() - startOfUTCWeekYear(date, options).getTime(); // Round the number of days to the nearest integer
  // because the number of milliseconds in a week is not constant
  // (e.g. it's different in the week of the daylight saving time clock shift)

  return Math.round(diff / MILLISECONDS_IN_WEEK) + 1;
}

function addLeadingZeros(number, targetLength) {
  var sign = number < 0 ? '-' : '';
  var output = Math.abs(number).toString();

  while (output.length < targetLength) {
    output = '0' + output;
  }

  return sign + output;
}

/*
 * |     | Unit                           |     | Unit                           |
 * |-----|--------------------------------|-----|--------------------------------|
 * |  a  | AM, PM                         |  A* |                                |
 * |  d  | Day of month                   |  D  |                                |
 * |  h  | Hour [1-12]                    |  H  | Hour [0-23]                    |
 * |  m  | Minute                         |  M  | Month                          |
 * |  s  | Second                         |  S  | Fraction of second             |
 * |  y  | Year (abs)                     |  Y  |                                |
 *
 * Letters marked by * are not implemented but reserved by Unicode standard.
 */

var formatters$2 = {
  // Year
  y: function (date, token) {
    // From http://www.unicode.org/reports/tr35/tr35-31/tr35-dates.html#Date_Format_tokens
    // | Year     |     y | yy |   yyy |  yyyy | yyyyy |
    // |----------|-------|----|-------|-------|-------|
    // | AD 1     |     1 | 01 |   001 |  0001 | 00001 |
    // | AD 12    |    12 | 12 |   012 |  0012 | 00012 |
    // | AD 123   |   123 | 23 |   123 |  0123 | 00123 |
    // | AD 1234  |  1234 | 34 |  1234 |  1234 | 01234 |
    // | AD 12345 | 12345 | 45 | 12345 | 12345 | 12345 |
    var signedYear = date.getUTCFullYear(); // Returns 1 for 1 BC (which is year 0 in JavaScript)

    var year = signedYear > 0 ? signedYear : 1 - signedYear;
    return addLeadingZeros(token === 'yy' ? year % 100 : year, token.length);
  },
  // Month
  M: function (date, token) {
    var month = date.getUTCMonth();
    return token === 'M' ? String(month + 1) : addLeadingZeros(month + 1, 2);
  },
  // Day of the month
  d: function (date, token) {
    return addLeadingZeros(date.getUTCDate(), token.length);
  },
  // AM or PM
  a: function (date, token) {
    var dayPeriodEnumValue = date.getUTCHours() / 12 >= 1 ? 'pm' : 'am';

    switch (token) {
      case 'a':
      case 'aa':
        return dayPeriodEnumValue.toUpperCase();

      case 'aaa':
        return dayPeriodEnumValue;

      case 'aaaaa':
        return dayPeriodEnumValue[0];

      case 'aaaa':
      default:
        return dayPeriodEnumValue === 'am' ? 'a.m.' : 'p.m.';
    }
  },
  // Hour [1-12]
  h: function (date, token) {
    return addLeadingZeros(date.getUTCHours() % 12 || 12, token.length);
  },
  // Hour [0-23]
  H: function (date, token) {
    return addLeadingZeros(date.getUTCHours(), token.length);
  },
  // Minute
  m: function (date, token) {
    return addLeadingZeros(date.getUTCMinutes(), token.length);
  },
  // Second
  s: function (date, token) {
    return addLeadingZeros(date.getUTCSeconds(), token.length);
  },
  // Fraction of second
  S: function (date, token) {
    var numberOfDigits = token.length;
    var milliseconds = date.getUTCMilliseconds();
    var fractionalSeconds = Math.floor(milliseconds * Math.pow(10, numberOfDigits - 3));
    return addLeadingZeros(fractionalSeconds, token.length);
  }
};
var lightFormatters = formatters$2;

var dayPeriodEnum = {
  am: 'am',
  pm: 'pm',
  midnight: 'midnight',
  noon: 'noon',
  morning: 'morning',
  afternoon: 'afternoon',
  evening: 'evening',
  night: 'night'
};
/*
 * |     | Unit                           |     | Unit                           |
 * |-----|--------------------------------|-----|--------------------------------|
 * |  a  | AM, PM                         |  A* | Milliseconds in day            |
 * |  b  | AM, PM, noon, midnight         |  B  | Flexible day period            |
 * |  c  | Stand-alone local day of week  |  C* | Localized hour w/ day period   |
 * |  d  | Day of month                   |  D  | Day of year                    |
 * |  e  | Local day of week              |  E  | Day of week                    |
 * |  f  |                                |  F* | Day of week in month           |
 * |  g* | Modified Julian day            |  G  | Era                            |
 * |  h  | Hour [1-12]                    |  H  | Hour [0-23]                    |
 * |  i! | ISO day of week                |  I! | ISO week of year               |
 * |  j* | Localized hour w/ day period   |  J* | Localized hour w/o day period  |
 * |  k  | Hour [1-24]                    |  K  | Hour [0-11]                    |
 * |  l* | (deprecated)                   |  L  | Stand-alone month              |
 * |  m  | Minute                         |  M  | Month                          |
 * |  n  |                                |  N  |                                |
 * |  o! | Ordinal number modifier        |  O  | Timezone (GMT)                 |
 * |  p! | Long localized time            |  P! | Long localized date            |
 * |  q  | Stand-alone quarter            |  Q  | Quarter                        |
 * |  r* | Related Gregorian year         |  R! | ISO week-numbering year        |
 * |  s  | Second                         |  S  | Fraction of second             |
 * |  t! | Seconds timestamp              |  T! | Milliseconds timestamp         |
 * |  u  | Extended year                  |  U* | Cyclic year                    |
 * |  v* | Timezone (generic non-locat.)  |  V* | Timezone (location)            |
 * |  w  | Local week of year             |  W* | Week of month                  |
 * |  x  | Timezone (ISO-8601 w/o Z)      |  X  | Timezone (ISO-8601)            |
 * |  y  | Year (abs)                     |  Y  | Local week-numbering year      |
 * |  z  | Timezone (specific non-locat.) |  Z* | Timezone (aliases)             |
 *
 * Letters marked by * are not implemented but reserved by Unicode standard.
 *
 * Letters marked by ! are non-standard, but implemented by date-fns:
 * - `o` modifies the previous token to turn it into an ordinal (see `format` docs)
 * - `i` is ISO day of week. For `i` and `ii` is returns numeric ISO week days,
 *   i.e. 7 for Sunday, 1 for Monday, etc.
 * - `I` is ISO week of year, as opposed to `w` which is local week of year.
 * - `R` is ISO week-numbering year, as opposed to `Y` which is local week-numbering year.
 *   `R` is supposed to be used in conjunction with `I` and `i`
 *   for universal ISO week-numbering date, whereas
 *   `Y` is supposed to be used in conjunction with `w` and `e`
 *   for week-numbering date specific to the locale.
 * - `P` is long localized date format
 * - `p` is long localized time format
 */

var formatters = {
  // Era
  G: function (date, token, localize) {
    var era = date.getUTCFullYear() > 0 ? 1 : 0;

    switch (token) {
      // AD, BC
      case 'G':
      case 'GG':
      case 'GGG':
        return localize.era(era, {
          width: 'abbreviated'
        });
      // A, B

      case 'GGGGG':
        return localize.era(era, {
          width: 'narrow'
        });
      // Anno Domini, Before Christ

      case 'GGGG':
      default:
        return localize.era(era, {
          width: 'wide'
        });
    }
  },
  // Year
  y: function (date, token, localize) {
    // Ordinal number
    if (token === 'yo') {
      var signedYear = date.getUTCFullYear(); // Returns 1 for 1 BC (which is year 0 in JavaScript)

      var year = signedYear > 0 ? signedYear : 1 - signedYear;
      return localize.ordinalNumber(year, {
        unit: 'year'
      });
    }

    return lightFormatters.y(date, token);
  },
  // Local week-numbering year
  Y: function (date, token, localize, options) {
    var signedWeekYear = getUTCWeekYear(date, options); // Returns 1 for 1 BC (which is year 0 in JavaScript)

    var weekYear = signedWeekYear > 0 ? signedWeekYear : 1 - signedWeekYear; // Two digit year

    if (token === 'YY') {
      var twoDigitYear = weekYear % 100;
      return addLeadingZeros(twoDigitYear, 2);
    } // Ordinal number


    if (token === 'Yo') {
      return localize.ordinalNumber(weekYear, {
        unit: 'year'
      });
    } // Padding


    return addLeadingZeros(weekYear, token.length);
  },
  // ISO week-numbering year
  R: function (date, token) {
    var isoWeekYear = getUTCISOWeekYear(date); // Padding

    return addLeadingZeros(isoWeekYear, token.length);
  },
  // Extended year. This is a single number designating the year of this calendar system.
  // The main difference between `y` and `u` localizers are B.C. years:
  // | Year | `y` | `u` |
  // |------|-----|-----|
  // | AC 1 |   1 |   1 |
  // | BC 1 |   1 |   0 |
  // | BC 2 |   2 |  -1 |
  // Also `yy` always returns the last two digits of a year,
  // while `uu` pads single digit years to 2 characters and returns other years unchanged.
  u: function (date, token) {
    var year = date.getUTCFullYear();
    return addLeadingZeros(year, token.length);
  },
  // Quarter
  Q: function (date, token, localize) {
    var quarter = Math.ceil((date.getUTCMonth() + 1) / 3);

    switch (token) {
      // 1, 2, 3, 4
      case 'Q':
        return String(quarter);
      // 01, 02, 03, 04

      case 'QQ':
        return addLeadingZeros(quarter, 2);
      // 1st, 2nd, 3rd, 4th

      case 'Qo':
        return localize.ordinalNumber(quarter, {
          unit: 'quarter'
        });
      // Q1, Q2, Q3, Q4

      case 'QQQ':
        return localize.quarter(quarter, {
          width: 'abbreviated',
          context: 'formatting'
        });
      // 1, 2, 3, 4 (narrow quarter; could be not numerical)

      case 'QQQQQ':
        return localize.quarter(quarter, {
          width: 'narrow',
          context: 'formatting'
        });
      // 1st quarter, 2nd quarter, ...

      case 'QQQQ':
      default:
        return localize.quarter(quarter, {
          width: 'wide',
          context: 'formatting'
        });
    }
  },
  // Stand-alone quarter
  q: function (date, token, localize) {
    var quarter = Math.ceil((date.getUTCMonth() + 1) / 3);

    switch (token) {
      // 1, 2, 3, 4
      case 'q':
        return String(quarter);
      // 01, 02, 03, 04

      case 'qq':
        return addLeadingZeros(quarter, 2);
      // 1st, 2nd, 3rd, 4th

      case 'qo':
        return localize.ordinalNumber(quarter, {
          unit: 'quarter'
        });
      // Q1, Q2, Q3, Q4

      case 'qqq':
        return localize.quarter(quarter, {
          width: 'abbreviated',
          context: 'standalone'
        });
      // 1, 2, 3, 4 (narrow quarter; could be not numerical)

      case 'qqqqq':
        return localize.quarter(quarter, {
          width: 'narrow',
          context: 'standalone'
        });
      // 1st quarter, 2nd quarter, ...

      case 'qqqq':
      default:
        return localize.quarter(quarter, {
          width: 'wide',
          context: 'standalone'
        });
    }
  },
  // Month
  M: function (date, token, localize) {
    var month = date.getUTCMonth();

    switch (token) {
      case 'M':
      case 'MM':
        return lightFormatters.M(date, token);
      // 1st, 2nd, ..., 12th

      case 'Mo':
        return localize.ordinalNumber(month + 1, {
          unit: 'month'
        });
      // Jan, Feb, ..., Dec

      case 'MMM':
        return localize.month(month, {
          width: 'abbreviated',
          context: 'formatting'
        });
      // J, F, ..., D

      case 'MMMMM':
        return localize.month(month, {
          width: 'narrow',
          context: 'formatting'
        });
      // January, February, ..., December

      case 'MMMM':
      default:
        return localize.month(month, {
          width: 'wide',
          context: 'formatting'
        });
    }
  },
  // Stand-alone month
  L: function (date, token, localize) {
    var month = date.getUTCMonth();

    switch (token) {
      // 1, 2, ..., 12
      case 'L':
        return String(month + 1);
      // 01, 02, ..., 12

      case 'LL':
        return addLeadingZeros(month + 1, 2);
      // 1st, 2nd, ..., 12th

      case 'Lo':
        return localize.ordinalNumber(month + 1, {
          unit: 'month'
        });
      // Jan, Feb, ..., Dec

      case 'LLL':
        return localize.month(month, {
          width: 'abbreviated',
          context: 'standalone'
        });
      // J, F, ..., D

      case 'LLLLL':
        return localize.month(month, {
          width: 'narrow',
          context: 'standalone'
        });
      // January, February, ..., December

      case 'LLLL':
      default:
        return localize.month(month, {
          width: 'wide',
          context: 'standalone'
        });
    }
  },
  // Local week of year
  w: function (date, token, localize, options) {
    var week = getUTCWeek(date, options);

    if (token === 'wo') {
      return localize.ordinalNumber(week, {
        unit: 'week'
      });
    }

    return addLeadingZeros(week, token.length);
  },
  // ISO week of year
  I: function (date, token, localize) {
    var isoWeek = getUTCISOWeek(date);

    if (token === 'Io') {
      return localize.ordinalNumber(isoWeek, {
        unit: 'week'
      });
    }

    return addLeadingZeros(isoWeek, token.length);
  },
  // Day of the month
  d: function (date, token, localize) {
    if (token === 'do') {
      return localize.ordinalNumber(date.getUTCDate(), {
        unit: 'date'
      });
    }

    return lightFormatters.d(date, token);
  },
  // Day of year
  D: function (date, token, localize) {
    var dayOfYear = getUTCDayOfYear(date);

    if (token === 'Do') {
      return localize.ordinalNumber(dayOfYear, {
        unit: 'dayOfYear'
      });
    }

    return addLeadingZeros(dayOfYear, token.length);
  },
  // Day of week
  E: function (date, token, localize) {
    var dayOfWeek = date.getUTCDay();

    switch (token) {
      // Tue
      case 'E':
      case 'EE':
      case 'EEE':
        return localize.day(dayOfWeek, {
          width: 'abbreviated',
          context: 'formatting'
        });
      // T

      case 'EEEEE':
        return localize.day(dayOfWeek, {
          width: 'narrow',
          context: 'formatting'
        });
      // Tu

      case 'EEEEEE':
        return localize.day(dayOfWeek, {
          width: 'short',
          context: 'formatting'
        });
      // Tuesday

      case 'EEEE':
      default:
        return localize.day(dayOfWeek, {
          width: 'wide',
          context: 'formatting'
        });
    }
  },
  // Local day of week
  e: function (date, token, localize, options) {
    var dayOfWeek = date.getUTCDay();
    var localDayOfWeek = (dayOfWeek - options.weekStartsOn + 8) % 7 || 7;

    switch (token) {
      // Numerical value (Nth day of week with current locale or weekStartsOn)
      case 'e':
        return String(localDayOfWeek);
      // Padded numerical value

      case 'ee':
        return addLeadingZeros(localDayOfWeek, 2);
      // 1st, 2nd, ..., 7th

      case 'eo':
        return localize.ordinalNumber(localDayOfWeek, {
          unit: 'day'
        });

      case 'eee':
        return localize.day(dayOfWeek, {
          width: 'abbreviated',
          context: 'formatting'
        });
      // T

      case 'eeeee':
        return localize.day(dayOfWeek, {
          width: 'narrow',
          context: 'formatting'
        });
      // Tu

      case 'eeeeee':
        return localize.day(dayOfWeek, {
          width: 'short',
          context: 'formatting'
        });
      // Tuesday

      case 'eeee':
      default:
        return localize.day(dayOfWeek, {
          width: 'wide',
          context: 'formatting'
        });
    }
  },
  // Stand-alone local day of week
  c: function (date, token, localize, options) {
    var dayOfWeek = date.getUTCDay();
    var localDayOfWeek = (dayOfWeek - options.weekStartsOn + 8) % 7 || 7;

    switch (token) {
      // Numerical value (same as in `e`)
      case 'c':
        return String(localDayOfWeek);
      // Padded numerical value

      case 'cc':
        return addLeadingZeros(localDayOfWeek, token.length);
      // 1st, 2nd, ..., 7th

      case 'co':
        return localize.ordinalNumber(localDayOfWeek, {
          unit: 'day'
        });

      case 'ccc':
        return localize.day(dayOfWeek, {
          width: 'abbreviated',
          context: 'standalone'
        });
      // T

      case 'ccccc':
        return localize.day(dayOfWeek, {
          width: 'narrow',
          context: 'standalone'
        });
      // Tu

      case 'cccccc':
        return localize.day(dayOfWeek, {
          width: 'short',
          context: 'standalone'
        });
      // Tuesday

      case 'cccc':
      default:
        return localize.day(dayOfWeek, {
          width: 'wide',
          context: 'standalone'
        });
    }
  },
  // ISO day of week
  i: function (date, token, localize) {
    var dayOfWeek = date.getUTCDay();
    var isoDayOfWeek = dayOfWeek === 0 ? 7 : dayOfWeek;

    switch (token) {
      // 2
      case 'i':
        return String(isoDayOfWeek);
      // 02

      case 'ii':
        return addLeadingZeros(isoDayOfWeek, token.length);
      // 2nd

      case 'io':
        return localize.ordinalNumber(isoDayOfWeek, {
          unit: 'day'
        });
      // Tue

      case 'iii':
        return localize.day(dayOfWeek, {
          width: 'abbreviated',
          context: 'formatting'
        });
      // T

      case 'iiiii':
        return localize.day(dayOfWeek, {
          width: 'narrow',
          context: 'formatting'
        });
      // Tu

      case 'iiiiii':
        return localize.day(dayOfWeek, {
          width: 'short',
          context: 'formatting'
        });
      // Tuesday

      case 'iiii':
      default:
        return localize.day(dayOfWeek, {
          width: 'wide',
          context: 'formatting'
        });
    }
  },
  // AM or PM
  a: function (date, token, localize) {
    var hours = date.getUTCHours();
    var dayPeriodEnumValue = hours / 12 >= 1 ? 'pm' : 'am';

    switch (token) {
      case 'a':
      case 'aa':
        return localize.dayPeriod(dayPeriodEnumValue, {
          width: 'abbreviated',
          context: 'formatting'
        });

      case 'aaa':
        return localize.dayPeriod(dayPeriodEnumValue, {
          width: 'abbreviated',
          context: 'formatting'
        }).toLowerCase();

      case 'aaaaa':
        return localize.dayPeriod(dayPeriodEnumValue, {
          width: 'narrow',
          context: 'formatting'
        });

      case 'aaaa':
      default:
        return localize.dayPeriod(dayPeriodEnumValue, {
          width: 'wide',
          context: 'formatting'
        });
    }
  },
  // AM, PM, midnight, noon
  b: function (date, token, localize) {
    var hours = date.getUTCHours();
    var dayPeriodEnumValue;

    if (hours === 12) {
      dayPeriodEnumValue = dayPeriodEnum.noon;
    } else if (hours === 0) {
      dayPeriodEnumValue = dayPeriodEnum.midnight;
    } else {
      dayPeriodEnumValue = hours / 12 >= 1 ? 'pm' : 'am';
    }

    switch (token) {
      case 'b':
      case 'bb':
        return localize.dayPeriod(dayPeriodEnumValue, {
          width: 'abbreviated',
          context: 'formatting'
        });

      case 'bbb':
        return localize.dayPeriod(dayPeriodEnumValue, {
          width: 'abbreviated',
          context: 'formatting'
        }).toLowerCase();

      case 'bbbbb':
        return localize.dayPeriod(dayPeriodEnumValue, {
          width: 'narrow',
          context: 'formatting'
        });

      case 'bbbb':
      default:
        return localize.dayPeriod(dayPeriodEnumValue, {
          width: 'wide',
          context: 'formatting'
        });
    }
  },
  // in the morning, in the afternoon, in the evening, at night
  B: function (date, token, localize) {
    var hours = date.getUTCHours();
    var dayPeriodEnumValue;

    if (hours >= 17) {
      dayPeriodEnumValue = dayPeriodEnum.evening;
    } else if (hours >= 12) {
      dayPeriodEnumValue = dayPeriodEnum.afternoon;
    } else if (hours >= 4) {
      dayPeriodEnumValue = dayPeriodEnum.morning;
    } else {
      dayPeriodEnumValue = dayPeriodEnum.night;
    }

    switch (token) {
      case 'B':
      case 'BB':
      case 'BBB':
        return localize.dayPeriod(dayPeriodEnumValue, {
          width: 'abbreviated',
          context: 'formatting'
        });

      case 'BBBBB':
        return localize.dayPeriod(dayPeriodEnumValue, {
          width: 'narrow',
          context: 'formatting'
        });

      case 'BBBB':
      default:
        return localize.dayPeriod(dayPeriodEnumValue, {
          width: 'wide',
          context: 'formatting'
        });
    }
  },
  // Hour [1-12]
  h: function (date, token, localize) {
    if (token === 'ho') {
      var hours = date.getUTCHours() % 12;
      if (hours === 0) hours = 12;
      return localize.ordinalNumber(hours, {
        unit: 'hour'
      });
    }

    return lightFormatters.h(date, token);
  },
  // Hour [0-23]
  H: function (date, token, localize) {
    if (token === 'Ho') {
      return localize.ordinalNumber(date.getUTCHours(), {
        unit: 'hour'
      });
    }

    return lightFormatters.H(date, token);
  },
  // Hour [0-11]
  K: function (date, token, localize) {
    var hours = date.getUTCHours() % 12;

    if (token === 'Ko') {
      return localize.ordinalNumber(hours, {
        unit: 'hour'
      });
    }

    return addLeadingZeros(hours, token.length);
  },
  // Hour [1-24]
  k: function (date, token, localize) {
    var hours = date.getUTCHours();
    if (hours === 0) hours = 24;

    if (token === 'ko') {
      return localize.ordinalNumber(hours, {
        unit: 'hour'
      });
    }

    return addLeadingZeros(hours, token.length);
  },
  // Minute
  m: function (date, token, localize) {
    if (token === 'mo') {
      return localize.ordinalNumber(date.getUTCMinutes(), {
        unit: 'minute'
      });
    }

    return lightFormatters.m(date, token);
  },
  // Second
  s: function (date, token, localize) {
    if (token === 'so') {
      return localize.ordinalNumber(date.getUTCSeconds(), {
        unit: 'second'
      });
    }

    return lightFormatters.s(date, token);
  },
  // Fraction of second
  S: function (date, token) {
    return lightFormatters.S(date, token);
  },
  // Timezone (ISO-8601. If offset is 0, output is always `'Z'`)
  X: function (date, token, _localize, options) {
    var originalDate = options._originalDate || date;
    var timezoneOffset = originalDate.getTimezoneOffset();

    if (timezoneOffset === 0) {
      return 'Z';
    }

    switch (token) {
      // Hours and optional minutes
      case 'X':
        return formatTimezoneWithOptionalMinutes(timezoneOffset);
      // Hours, minutes and optional seconds without `:` delimiter
      // Note: neither ISO-8601 nor JavaScript supports seconds in timezone offsets
      // so this token always has the same output as `XX`

      case 'XXXX':
      case 'XX':
        // Hours and minutes without `:` delimiter
        return formatTimezone(timezoneOffset);
      // Hours, minutes and optional seconds with `:` delimiter
      // Note: neither ISO-8601 nor JavaScript supports seconds in timezone offsets
      // so this token always has the same output as `XXX`

      case 'XXXXX':
      case 'XXX': // Hours and minutes with `:` delimiter

      default:
        return formatTimezone(timezoneOffset, ':');
    }
  },
  // Timezone (ISO-8601. If offset is 0, output is `'+00:00'` or equivalent)
  x: function (date, token, _localize, options) {
    var originalDate = options._originalDate || date;
    var timezoneOffset = originalDate.getTimezoneOffset();

    switch (token) {
      // Hours and optional minutes
      case 'x':
        return formatTimezoneWithOptionalMinutes(timezoneOffset);
      // Hours, minutes and optional seconds without `:` delimiter
      // Note: neither ISO-8601 nor JavaScript supports seconds in timezone offsets
      // so this token always has the same output as `xx`

      case 'xxxx':
      case 'xx':
        // Hours and minutes without `:` delimiter
        return formatTimezone(timezoneOffset);
      // Hours, minutes and optional seconds with `:` delimiter
      // Note: neither ISO-8601 nor JavaScript supports seconds in timezone offsets
      // so this token always has the same output as `xxx`

      case 'xxxxx':
      case 'xxx': // Hours and minutes with `:` delimiter

      default:
        return formatTimezone(timezoneOffset, ':');
    }
  },
  // Timezone (GMT)
  O: function (date, token, _localize, options) {
    var originalDate = options._originalDate || date;
    var timezoneOffset = originalDate.getTimezoneOffset();

    switch (token) {
      // Short
      case 'O':
      case 'OO':
      case 'OOO':
        return 'GMT' + formatTimezoneShort(timezoneOffset, ':');
      // Long

      case 'OOOO':
      default:
        return 'GMT' + formatTimezone(timezoneOffset, ':');
    }
  },
  // Timezone (specific non-location)
  z: function (date, token, _localize, options) {
    var originalDate = options._originalDate || date;
    var timezoneOffset = originalDate.getTimezoneOffset();

    switch (token) {
      // Short
      case 'z':
      case 'zz':
      case 'zzz':
        return 'GMT' + formatTimezoneShort(timezoneOffset, ':');
      // Long

      case 'zzzz':
      default:
        return 'GMT' + formatTimezone(timezoneOffset, ':');
    }
  },
  // Seconds timestamp
  t: function (date, token, _localize, options) {
    var originalDate = options._originalDate || date;
    var timestamp = Math.floor(originalDate.getTime() / 1000);
    return addLeadingZeros(timestamp, token.length);
  },
  // Milliseconds timestamp
  T: function (date, token, _localize, options) {
    var originalDate = options._originalDate || date;
    var timestamp = originalDate.getTime();
    return addLeadingZeros(timestamp, token.length);
  }
};

function formatTimezoneShort(offset, dirtyDelimiter) {
  var sign = offset > 0 ? '-' : '+';
  var absOffset = Math.abs(offset);
  var hours = Math.floor(absOffset / 60);
  var minutes = absOffset % 60;

  if (minutes === 0) {
    return sign + String(hours);
  }

  var delimiter = dirtyDelimiter || '';
  return sign + String(hours) + delimiter + addLeadingZeros(minutes, 2);
}

function formatTimezoneWithOptionalMinutes(offset, dirtyDelimiter) {
  if (offset % 60 === 0) {
    var sign = offset > 0 ? '-' : '+';
    return sign + addLeadingZeros(Math.abs(offset) / 60, 2);
  }

  return formatTimezone(offset, dirtyDelimiter);
}

function formatTimezone(offset, dirtyDelimiter) {
  var delimiter = dirtyDelimiter || '';
  var sign = offset > 0 ? '-' : '+';
  var absOffset = Math.abs(offset);
  var hours = addLeadingZeros(Math.floor(absOffset / 60), 2);
  var minutes = addLeadingZeros(absOffset % 60, 2);
  return sign + hours + delimiter + minutes;
}

var formatters$1 = formatters;

function dateLongFormatter(pattern, formatLong) {
  switch (pattern) {
    case 'P':
      return formatLong.date({
        width: 'short'
      });

    case 'PP':
      return formatLong.date({
        width: 'medium'
      });

    case 'PPP':
      return formatLong.date({
        width: 'long'
      });

    case 'PPPP':
    default:
      return formatLong.date({
        width: 'full'
      });
  }
}

function timeLongFormatter(pattern, formatLong) {
  switch (pattern) {
    case 'p':
      return formatLong.time({
        width: 'short'
      });

    case 'pp':
      return formatLong.time({
        width: 'medium'
      });

    case 'ppp':
      return formatLong.time({
        width: 'long'
      });

    case 'pppp':
    default:
      return formatLong.time({
        width: 'full'
      });
  }
}

function dateTimeLongFormatter(pattern, formatLong) {
  var matchResult = pattern.match(/(P+)(p+)?/) || [];
  var datePattern = matchResult[1];
  var timePattern = matchResult[2];

  if (!timePattern) {
    return dateLongFormatter(pattern, formatLong);
  }

  var dateTimeFormat;

  switch (datePattern) {
    case 'P':
      dateTimeFormat = formatLong.dateTime({
        width: 'short'
      });
      break;

    case 'PP':
      dateTimeFormat = formatLong.dateTime({
        width: 'medium'
      });
      break;

    case 'PPP':
      dateTimeFormat = formatLong.dateTime({
        width: 'long'
      });
      break;

    case 'PPPP':
    default:
      dateTimeFormat = formatLong.dateTime({
        width: 'full'
      });
      break;
  }

  return dateTimeFormat.replace('{{date}}', dateLongFormatter(datePattern, formatLong)).replace('{{time}}', timeLongFormatter(timePattern, formatLong));
}

var longFormatters = {
  p: timeLongFormatter,
  P: dateTimeLongFormatter
};
var longFormatters$1 = longFormatters;

/**
 * Google Chrome as of 67.0.3396.87 introduced timezones with offset that includes seconds.
 * They usually appear for dates that denote time before the timezones were introduced
 * (e.g. for 'Europe/Prague' timezone the offset is GMT+00:57:44 before 1 October 1891
 * and GMT+01:00:00 after that date)
 *
 * Date#getTimezoneOffset returns the offset in minutes and would return 57 for the example above,
 * which would lead to incorrect calculations.
 *
 * This function returns the timezone offset in milliseconds that takes seconds in account.
 */
function getTimezoneOffsetInMilliseconds(date) {
  var utcDate = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), date.getHours(), date.getMinutes(), date.getSeconds(), date.getMilliseconds()));
  utcDate.setUTCFullYear(date.getFullYear());
  return date.getTime() - utcDate.getTime();
}

var protectedDayOfYearTokens = ['D', 'DD'];
var protectedWeekYearTokens = ['YY', 'YYYY'];
function isProtectedDayOfYearToken(token) {
  return protectedDayOfYearTokens.indexOf(token) !== -1;
}
function isProtectedWeekYearToken(token) {
  return protectedWeekYearTokens.indexOf(token) !== -1;
}
function throwProtectedError(token, format, input) {
  if (token === 'YYYY') {
    throw new RangeError("Use `yyyy` instead of `YYYY` (in `".concat(format, "`) for formatting years to the input `").concat(input, "`; see: https://git.io/fxCyr"));
  } else if (token === 'YY') {
    throw new RangeError("Use `yy` instead of `YY` (in `".concat(format, "`) for formatting years to the input `").concat(input, "`; see: https://git.io/fxCyr"));
  } else if (token === 'D') {
    throw new RangeError("Use `d` instead of `D` (in `".concat(format, "`) for formatting days of the month to the input `").concat(input, "`; see: https://git.io/fxCyr"));
  } else if (token === 'DD') {
    throw new RangeError("Use `dd` instead of `DD` (in `".concat(format, "`) for formatting days of the month to the input `").concat(input, "`; see: https://git.io/fxCyr"));
  }
}

// - [yYQqMLwIdDecihHKkms]o matches any available ordinal number token
//   (one of the certain letters followed by `o`)
// - (\w)\1* matches any sequences of the same letter
// - '' matches two quote characters in a row
// - '(''|[^'])+('|$) matches anything surrounded by two quote characters ('),
//   except a single quote symbol, which ends the sequence.
//   Two quote characters do not end the sequence.
//   If there is no matching single quote
//   then the sequence will continue until the end of the string.
// - . matches any single character unmatched by previous parts of the RegExps

var formattingTokensRegExp$1 = /[yYQqMLwIdDecihHKkms]o|(\w)\1*|''|'(''|[^'])+('|$)|./g; // This RegExp catches symbols escaped by quotes, and also
// sequences of symbols P, p, and the combinations like `PPPPPPPppppp`

var longFormattingTokensRegExp$1 = /P+p+|P+|p+|''|'(''|[^'])+('|$)|./g;
var escapedStringRegExp$1 = /^'([^]*?)'?$/;
var doubleQuoteRegExp$1 = /''/g;
var unescapedLatinCharacterRegExp$1 = /[a-zA-Z]/;
/**
 * @name format
 * @category Common Helpers
 * @summary Format the date.
 *
 * @description
 * Return the formatted date string in the given format. The result may vary by locale.
 *
 * > ⚠️ Please note that the `format` tokens differ from Moment.js and other libraries.
 * > See: https://git.io/fxCyr
 *
 * The characters wrapped between two single quotes characters (') are escaped.
 * Two single quotes in a row, whether inside or outside a quoted sequence, represent a 'real' single quote.
 * (see the last example)
 *
 * Format of the string is based on Unicode Technical Standard #35:
 * https://www.unicode.org/reports/tr35/tr35-dates.html#Date_Field_Symbol_Table
 * with a few additions (see note 7 below the table).
 *
 * Accepted patterns:
 * | Unit                            | Pattern | Result examples                   | Notes |
 * |---------------------------------|---------|-----------------------------------|-------|
 * | Era                             | G..GGG  | AD, BC                            |       |
 * |                                 | GGGG    | Anno Domini, Before Christ        | 2     |
 * |                                 | GGGGG   | A, B                              |       |
 * | Calendar year                   | y       | 44, 1, 1900, 2017                 | 5     |
 * |                                 | yo      | 44th, 1st, 0th, 17th              | 5,7   |
 * |                                 | yy      | 44, 01, 00, 17                    | 5     |
 * |                                 | yyy     | 044, 001, 1900, 2017              | 5     |
 * |                                 | yyyy    | 0044, 0001, 1900, 2017            | 5     |
 * |                                 | yyyyy   | ...                               | 3,5   |
 * | Local week-numbering year       | Y       | 44, 1, 1900, 2017                 | 5     |
 * |                                 | Yo      | 44th, 1st, 1900th, 2017th         | 5,7   |
 * |                                 | YY      | 44, 01, 00, 17                    | 5,8   |
 * |                                 | YYY     | 044, 001, 1900, 2017              | 5     |
 * |                                 | YYYY    | 0044, 0001, 1900, 2017            | 5,8   |
 * |                                 | YYYYY   | ...                               | 3,5   |
 * | ISO week-numbering year         | R       | -43, 0, 1, 1900, 2017             | 5,7   |
 * |                                 | RR      | -43, 00, 01, 1900, 2017           | 5,7   |
 * |                                 | RRR     | -043, 000, 001, 1900, 2017        | 5,7   |
 * |                                 | RRRR    | -0043, 0000, 0001, 1900, 2017     | 5,7   |
 * |                                 | RRRRR   | ...                               | 3,5,7 |
 * | Extended year                   | u       | -43, 0, 1, 1900, 2017             | 5     |
 * |                                 | uu      | -43, 01, 1900, 2017               | 5     |
 * |                                 | uuu     | -043, 001, 1900, 2017             | 5     |
 * |                                 | uuuu    | -0043, 0001, 1900, 2017           | 5     |
 * |                                 | uuuuu   | ...                               | 3,5   |
 * | Quarter (formatting)            | Q       | 1, 2, 3, 4                        |       |
 * |                                 | Qo      | 1st, 2nd, 3rd, 4th                | 7     |
 * |                                 | QQ      | 01, 02, 03, 04                    |       |
 * |                                 | QQQ     | Q1, Q2, Q3, Q4                    |       |
 * |                                 | QQQQ    | 1st quarter, 2nd quarter, ...     | 2     |
 * |                                 | QQQQQ   | 1, 2, 3, 4                        | 4     |
 * | Quarter (stand-alone)           | q       | 1, 2, 3, 4                        |       |
 * |                                 | qo      | 1st, 2nd, 3rd, 4th                | 7     |
 * |                                 | qq      | 01, 02, 03, 04                    |       |
 * |                                 | qqq     | Q1, Q2, Q3, Q4                    |       |
 * |                                 | qqqq    | 1st quarter, 2nd quarter, ...     | 2     |
 * |                                 | qqqqq   | 1, 2, 3, 4                        | 4     |
 * | Month (formatting)              | M       | 1, 2, ..., 12                     |       |
 * |                                 | Mo      | 1st, 2nd, ..., 12th               | 7     |
 * |                                 | MM      | 01, 02, ..., 12                   |       |
 * |                                 | MMM     | Jan, Feb, ..., Dec                |       |
 * |                                 | MMMM    | January, February, ..., December  | 2     |
 * |                                 | MMMMM   | J, F, ..., D                      |       |
 * | Month (stand-alone)             | L       | 1, 2, ..., 12                     |       |
 * |                                 | Lo      | 1st, 2nd, ..., 12th               | 7     |
 * |                                 | LL      | 01, 02, ..., 12                   |       |
 * |                                 | LLL     | Jan, Feb, ..., Dec                |       |
 * |                                 | LLLL    | January, February, ..., December  | 2     |
 * |                                 | LLLLL   | J, F, ..., D                      |       |
 * | Local week of year              | w       | 1, 2, ..., 53                     |       |
 * |                                 | wo      | 1st, 2nd, ..., 53th               | 7     |
 * |                                 | ww      | 01, 02, ..., 53                   |       |
 * | ISO week of year                | I       | 1, 2, ..., 53                     | 7     |
 * |                                 | Io      | 1st, 2nd, ..., 53th               | 7     |
 * |                                 | II      | 01, 02, ..., 53                   | 7     |
 * | Day of month                    | d       | 1, 2, ..., 31                     |       |
 * |                                 | do      | 1st, 2nd, ..., 31st               | 7     |
 * |                                 | dd      | 01, 02, ..., 31                   |       |
 * | Day of year                     | D       | 1, 2, ..., 365, 366               | 9     |
 * |                                 | Do      | 1st, 2nd, ..., 365th, 366th       | 7     |
 * |                                 | DD      | 01, 02, ..., 365, 366             | 9     |
 * |                                 | DDD     | 001, 002, ..., 365, 366           |       |
 * |                                 | DDDD    | ...                               | 3     |
 * | Day of week (formatting)        | E..EEE  | Mon, Tue, Wed, ..., Sun           |       |
 * |                                 | EEEE    | Monday, Tuesday, ..., Sunday      | 2     |
 * |                                 | EEEEE   | M, T, W, T, F, S, S               |       |
 * |                                 | EEEEEE  | Mo, Tu, We, Th, Fr, Sa, Su        |       |
 * | ISO day of week (formatting)    | i       | 1, 2, 3, ..., 7                   | 7     |
 * |                                 | io      | 1st, 2nd, ..., 7th                | 7     |
 * |                                 | ii      | 01, 02, ..., 07                   | 7     |
 * |                                 | iii     | Mon, Tue, Wed, ..., Sun           | 7     |
 * |                                 | iiii    | Monday, Tuesday, ..., Sunday      | 2,7   |
 * |                                 | iiiii   | M, T, W, T, F, S, S               | 7     |
 * |                                 | iiiiii  | Mo, Tu, We, Th, Fr, Sa, Su        | 7     |
 * | Local day of week (formatting)  | e       | 2, 3, 4, ..., 1                   |       |
 * |                                 | eo      | 2nd, 3rd, ..., 1st                | 7     |
 * |                                 | ee      | 02, 03, ..., 01                   |       |
 * |                                 | eee     | Mon, Tue, Wed, ..., Sun           |       |
 * |                                 | eeee    | Monday, Tuesday, ..., Sunday      | 2     |
 * |                                 | eeeee   | M, T, W, T, F, S, S               |       |
 * |                                 | eeeeee  | Mo, Tu, We, Th, Fr, Sa, Su        |       |
 * | Local day of week (stand-alone) | c       | 2, 3, 4, ..., 1                   |       |
 * |                                 | co      | 2nd, 3rd, ..., 1st                | 7     |
 * |                                 | cc      | 02, 03, ..., 01                   |       |
 * |                                 | ccc     | Mon, Tue, Wed, ..., Sun           |       |
 * |                                 | cccc    | Monday, Tuesday, ..., Sunday      | 2     |
 * |                                 | ccccc   | M, T, W, T, F, S, S               |       |
 * |                                 | cccccc  | Mo, Tu, We, Th, Fr, Sa, Su        |       |
 * | AM, PM                          | a..aa   | AM, PM                            |       |
 * |                                 | aaa     | am, pm                            |       |
 * |                                 | aaaa    | a.m., p.m.                        | 2     |
 * |                                 | aaaaa   | a, p                              |       |
 * | AM, PM, noon, midnight          | b..bb   | AM, PM, noon, midnight            |       |
 * |                                 | bbb     | am, pm, noon, midnight            |       |
 * |                                 | bbbb    | a.m., p.m., noon, midnight        | 2     |
 * |                                 | bbbbb   | a, p, n, mi                       |       |
 * | Flexible day period             | B..BBB  | at night, in the morning, ...     |       |
 * |                                 | BBBB    | at night, in the morning, ...     | 2     |
 * |                                 | BBBBB   | at night, in the morning, ...     |       |
 * | Hour [1-12]                     | h       | 1, 2, ..., 11, 12                 |       |
 * |                                 | ho      | 1st, 2nd, ..., 11th, 12th         | 7     |
 * |                                 | hh      | 01, 02, ..., 11, 12               |       |
 * | Hour [0-23]                     | H       | 0, 1, 2, ..., 23                  |       |
 * |                                 | Ho      | 0th, 1st, 2nd, ..., 23rd          | 7     |
 * |                                 | HH      | 00, 01, 02, ..., 23               |       |
 * | Hour [0-11]                     | K       | 1, 2, ..., 11, 0                  |       |
 * |                                 | Ko      | 1st, 2nd, ..., 11th, 0th          | 7     |
 * |                                 | KK      | 01, 02, ..., 11, 00               |       |
 * | Hour [1-24]                     | k       | 24, 1, 2, ..., 23                 |       |
 * |                                 | ko      | 24th, 1st, 2nd, ..., 23rd         | 7     |
 * |                                 | kk      | 24, 01, 02, ..., 23               |       |
 * | Minute                          | m       | 0, 1, ..., 59                     |       |
 * |                                 | mo      | 0th, 1st, ..., 59th               | 7     |
 * |                                 | mm      | 00, 01, ..., 59                   |       |
 * | Second                          | s       | 0, 1, ..., 59                     |       |
 * |                                 | so      | 0th, 1st, ..., 59th               | 7     |
 * |                                 | ss      | 00, 01, ..., 59                   |       |
 * | Fraction of second              | S       | 0, 1, ..., 9                      |       |
 * |                                 | SS      | 00, 01, ..., 99                   |       |
 * |                                 | SSS     | 000, 001, ..., 999                |       |
 * |                                 | SSSS    | ...                               | 3     |
 * | Timezone (ISO-8601 w/ Z)        | X       | -08, +0530, Z                     |       |
 * |                                 | XX      | -0800, +0530, Z                   |       |
 * |                                 | XXX     | -08:00, +05:30, Z                 |       |
 * |                                 | XXXX    | -0800, +0530, Z, +123456          | 2     |
 * |                                 | XXXXX   | -08:00, +05:30, Z, +12:34:56      |       |
 * | Timezone (ISO-8601 w/o Z)       | x       | -08, +0530, +00                   |       |
 * |                                 | xx      | -0800, +0530, +0000               |       |
 * |                                 | xxx     | -08:00, +05:30, +00:00            | 2     |
 * |                                 | xxxx    | -0800, +0530, +0000, +123456      |       |
 * |                                 | xxxxx   | -08:00, +05:30, +00:00, +12:34:56 |       |
 * | Timezone (GMT)                  | O...OOO | GMT-8, GMT+5:30, GMT+0            |       |
 * |                                 | OOOO    | GMT-08:00, GMT+05:30, GMT+00:00   | 2     |
 * | Timezone (specific non-locat.)  | z...zzz | GMT-8, GMT+5:30, GMT+0            | 6     |
 * |                                 | zzzz    | GMT-08:00, GMT+05:30, GMT+00:00   | 2,6   |
 * | Seconds timestamp               | t       | 512969520                         | 7     |
 * |                                 | tt      | ...                               | 3,7   |
 * | Milliseconds timestamp          | T       | 512969520900                      | 7     |
 * |                                 | TT      | ...                               | 3,7   |
 * | Long localized date             | P       | 04/29/1453                        | 7     |
 * |                                 | PP      | Apr 29, 1453                      | 7     |
 * |                                 | PPP     | April 29th, 1453                  | 7     |
 * |                                 | PPPP    | Friday, April 29th, 1453          | 2,7   |
 * | Long localized time             | p       | 12:00 AM                          | 7     |
 * |                                 | pp      | 12:00:00 AM                       | 7     |
 * |                                 | ppp     | 12:00:00 AM GMT+2                 | 7     |
 * |                                 | pppp    | 12:00:00 AM GMT+02:00             | 2,7   |
 * | Combination of date and time    | Pp      | 04/29/1453, 12:00 AM              | 7     |
 * |                                 | PPpp    | Apr 29, 1453, 12:00:00 AM         | 7     |
 * |                                 | PPPppp  | April 29th, 1453 at ...           | 7     |
 * |                                 | PPPPpppp| Friday, April 29th, 1453 at ...   | 2,7   |
 * Notes:
 * 1. "Formatting" units (e.g. formatting quarter) in the default en-US locale
 *    are the same as "stand-alone" units, but are different in some languages.
 *    "Formatting" units are declined according to the rules of the language
 *    in the context of a date. "Stand-alone" units are always nominative singular:
 *
 *    `format(new Date(2017, 10, 6), 'do LLLL', {locale: cs}) //=> '6. listopad'`
 *
 *    `format(new Date(2017, 10, 6), 'do MMMM', {locale: cs}) //=> '6. listopadu'`
 *
 * 2. Any sequence of the identical letters is a pattern, unless it is escaped by
 *    the single quote characters (see below).
 *    If the sequence is longer than listed in table (e.g. `EEEEEEEEEEE`)
 *    the output will be the same as default pattern for this unit, usually
 *    the longest one (in case of ISO weekdays, `EEEE`). Default patterns for units
 *    are marked with "2" in the last column of the table.
 *
 *    `format(new Date(2017, 10, 6), 'MMM') //=> 'Nov'`
 *
 *    `format(new Date(2017, 10, 6), 'MMMM') //=> 'November'`
 *
 *    `format(new Date(2017, 10, 6), 'MMMMM') //=> 'N'`
 *
 *    `format(new Date(2017, 10, 6), 'MMMMMM') //=> 'November'`
 *
 *    `format(new Date(2017, 10, 6), 'MMMMMMM') //=> 'November'`
 *
 * 3. Some patterns could be unlimited length (such as `yyyyyyyy`).
 *    The output will be padded with zeros to match the length of the pattern.
 *
 *    `format(new Date(2017, 10, 6), 'yyyyyyyy') //=> '00002017'`
 *
 * 4. `QQQQQ` and `qqqqq` could be not strictly numerical in some locales.
 *    These tokens represent the shortest form of the quarter.
 *
 * 5. The main difference between `y` and `u` patterns are B.C. years:
 *
 *    | Year | `y` | `u` |
 *    |------|-----|-----|
 *    | AC 1 |   1 |   1 |
 *    | BC 1 |   1 |   0 |
 *    | BC 2 |   2 |  -1 |
 *
 *    Also `yy` always returns the last two digits of a year,
 *    while `uu` pads single digit years to 2 characters and returns other years unchanged:
 *
 *    | Year | `yy` | `uu` |
 *    |------|------|------|
 *    | 1    |   01 |   01 |
 *    | 14   |   14 |   14 |
 *    | 376  |   76 |  376 |
 *    | 1453 |   53 | 1453 |
 *
 *    The same difference is true for local and ISO week-numbering years (`Y` and `R`),
 *    except local week-numbering years are dependent on `options.weekStartsOn`
 *    and `options.firstWeekContainsDate` (compare [getISOWeekYear]{@link https://date-fns.org/docs/getISOWeekYear}
 *    and [getWeekYear]{@link https://date-fns.org/docs/getWeekYear}).
 *
 * 6. Specific non-location timezones are currently unavailable in `date-fns`,
 *    so right now these tokens fall back to GMT timezones.
 *
 * 7. These patterns are not in the Unicode Technical Standard #35:
 *    - `i`: ISO day of week
 *    - `I`: ISO week of year
 *    - `R`: ISO week-numbering year
 *    - `t`: seconds timestamp
 *    - `T`: milliseconds timestamp
 *    - `o`: ordinal number modifier
 *    - `P`: long localized date
 *    - `p`: long localized time
 *
 * 8. `YY` and `YYYY` tokens represent week-numbering years but they are often confused with years.
 *    You should enable `options.useAdditionalWeekYearTokens` to use them. See: https://git.io/fxCyr
 *
 * 9. `D` and `DD` tokens represent days of the year but they are often confused with days of the month.
 *    You should enable `options.useAdditionalDayOfYearTokens` to use them. See: https://git.io/fxCyr
 *
 * ### v2.0.0 breaking changes:
 *
 * - [Changes that are common for the whole library](https://github.com/date-fns/date-fns/blob/master/docs/upgradeGuide.md#Common-Changes).
 *
 * - The second argument is now required for the sake of explicitness.
 *
 *   ```javascript
 *   // Before v2.0.0
 *   format(new Date(2016, 0, 1))
 *
 *   // v2.0.0 onward
 *   format(new Date(2016, 0, 1), "yyyy-MM-dd'T'HH:mm:ss.SSSxxx")
 *   ```
 *
 * - New format string API for `format` function
 *   which is based on [Unicode Technical Standard #35](https://www.unicode.org/reports/tr35/tr35-dates.html#Date_Field_Symbol_Table).
 *   See [this post](https://blog.date-fns.org/post/unicode-tokens-in-date-fns-v2-sreatyki91jg) for more details.
 *
 * - Characters are now escaped using single quote symbols (`'`) instead of square brackets.
 *
 * @param {Date|Number} date - the original date
 * @param {String} format - the string of tokens
 * @param {Object} [options] - an object with options.
 * @param {Locale} [options.locale=defaultLocale] - the locale object. See [Locale]{@link https://date-fns.org/docs/Locale}
 * @param {0|1|2|3|4|5|6} [options.weekStartsOn=0] - the index of the first day of the week (0 - Sunday)
 * @param {Number} [options.firstWeekContainsDate=1] - the day of January, which is
 * @param {Boolean} [options.useAdditionalWeekYearTokens=false] - if true, allows usage of the week-numbering year tokens `YY` and `YYYY`;
 *   see: https://git.io/fxCyr
 * @param {Boolean} [options.useAdditionalDayOfYearTokens=false] - if true, allows usage of the day of year tokens `D` and `DD`;
 *   see: https://git.io/fxCyr
 * @returns {String} the formatted date string
 * @throws {TypeError} 2 arguments required
 * @throws {RangeError} `date` must not be Invalid Date
 * @throws {RangeError} `options.locale` must contain `localize` property
 * @throws {RangeError} `options.locale` must contain `formatLong` property
 * @throws {RangeError} `options.weekStartsOn` must be between 0 and 6
 * @throws {RangeError} `options.firstWeekContainsDate` must be between 1 and 7
 * @throws {RangeError} use `yyyy` instead of `YYYY` for formatting years using [format provided] to the input [input provided]; see: https://git.io/fxCyr
 * @throws {RangeError} use `yy` instead of `YY` for formatting years using [format provided] to the input [input provided]; see: https://git.io/fxCyr
 * @throws {RangeError} use `d` instead of `D` for formatting days of the month using [format provided] to the input [input provided]; see: https://git.io/fxCyr
 * @throws {RangeError} use `dd` instead of `DD` for formatting days of the month using [format provided] to the input [input provided]; see: https://git.io/fxCyr
 * @throws {RangeError} format string contains an unescaped latin alphabet character
 *
 * @example
 * // Represent 11 February 2014 in middle-endian format:
 * var result = format(new Date(2014, 1, 11), 'MM/dd/yyyy')
 * //=> '02/11/2014'
 *
 * @example
 * // Represent 2 July 2014 in Esperanto:
 * import { eoLocale } from 'date-fns/locale/eo'
 * var result = format(new Date(2014, 6, 2), "do 'de' MMMM yyyy", {
 *   locale: eoLocale
 * })
 * //=> '2-a de julio 2014'
 *
 * @example
 * // Escape string by single quote characters:
 * var result = format(new Date(2014, 6, 2, 15), "h 'o''clock'")
 * //=> "3 o'clock"
 */

function format(dirtyDate, dirtyFormatStr, dirtyOptions) {
  requiredArgs(2, arguments);
  var formatStr = String(dirtyFormatStr);
  var options = dirtyOptions || {};
  var locale = options.locale || defaultLocale;
  var localeFirstWeekContainsDate = locale.options && locale.options.firstWeekContainsDate;
  var defaultFirstWeekContainsDate = localeFirstWeekContainsDate == null ? 1 : toInteger(localeFirstWeekContainsDate);
  var firstWeekContainsDate = options.firstWeekContainsDate == null ? defaultFirstWeekContainsDate : toInteger(options.firstWeekContainsDate); // Test if weekStartsOn is between 1 and 7 _and_ is not NaN

  if (!(firstWeekContainsDate >= 1 && firstWeekContainsDate <= 7)) {
    throw new RangeError('firstWeekContainsDate must be between 1 and 7 inclusively');
  }

  var localeWeekStartsOn = locale.options && locale.options.weekStartsOn;
  var defaultWeekStartsOn = localeWeekStartsOn == null ? 0 : toInteger(localeWeekStartsOn);
  var weekStartsOn = options.weekStartsOn == null ? defaultWeekStartsOn : toInteger(options.weekStartsOn); // Test if weekStartsOn is between 0 and 6 _and_ is not NaN

  if (!(weekStartsOn >= 0 && weekStartsOn <= 6)) {
    throw new RangeError('weekStartsOn must be between 0 and 6 inclusively');
  }

  if (!locale.localize) {
    throw new RangeError('locale must contain localize property');
  }

  if (!locale.formatLong) {
    throw new RangeError('locale must contain formatLong property');
  }

  var originalDate = toDate(dirtyDate);

  if (!isValid(originalDate)) {
    throw new RangeError('Invalid time value');
  } // Convert the date in system timezone to the same date in UTC+00:00 timezone.
  // This ensures that when UTC functions will be implemented, locales will be compatible with them.
  // See an issue about UTC functions: https://github.com/date-fns/date-fns/issues/376


  var timezoneOffset = getTimezoneOffsetInMilliseconds(originalDate);
  var utcDate = subMilliseconds(originalDate, timezoneOffset);
  var formatterOptions = {
    firstWeekContainsDate: firstWeekContainsDate,
    weekStartsOn: weekStartsOn,
    locale: locale,
    _originalDate: originalDate
  };
  var result = formatStr.match(longFormattingTokensRegExp$1).map(function (substring) {
    var firstCharacter = substring[0];

    if (firstCharacter === 'p' || firstCharacter === 'P') {
      var longFormatter = longFormatters$1[firstCharacter];
      return longFormatter(substring, locale.formatLong, formatterOptions);
    }

    return substring;
  }).join('').match(formattingTokensRegExp$1).map(function (substring) {
    // Replace two single quote characters with one single quote character
    if (substring === "''") {
      return "'";
    }

    var firstCharacter = substring[0];

    if (firstCharacter === "'") {
      return cleanEscapedString$1(substring);
    }

    var formatter = formatters$1[firstCharacter];

    if (formatter) {
      if (!options.useAdditionalWeekYearTokens && isProtectedWeekYearToken(substring)) {
        throwProtectedError(substring, dirtyFormatStr, dirtyDate);
      }

      if (!options.useAdditionalDayOfYearTokens && isProtectedDayOfYearToken(substring)) {
        throwProtectedError(substring, dirtyFormatStr, dirtyDate);
      }

      return formatter(utcDate, substring, locale.localize, formatterOptions);
    }

    if (firstCharacter.match(unescapedLatinCharacterRegExp$1)) {
      throw new RangeError('Format string contains an unescaped latin alphabet character `' + firstCharacter + '`');
    }

    return substring;
  }).join('');
  return result;
}

function cleanEscapedString$1(input) {
  return input.match(escapedStringRegExp$1)[1].replace(doubleQuoteRegExp$1, "'");
}

function assign(target, dirtyObject) {
  if (target == null) {
    throw new TypeError('assign requires that input parameter not be null or undefined');
  }

  dirtyObject = dirtyObject || {};

  for (var property in dirtyObject) {
    if (Object.prototype.hasOwnProperty.call(dirtyObject, property)) {
      target[property] = dirtyObject[property];
    }
  }

  return target;
}

// See issue: https://github.com/date-fns/date-fns/issues/376

function setUTCDay(dirtyDate, dirtyDay, dirtyOptions) {
  requiredArgs(2, arguments);
  var options = dirtyOptions || {};
  var locale = options.locale;
  var localeWeekStartsOn = locale && locale.options && locale.options.weekStartsOn;
  var defaultWeekStartsOn = localeWeekStartsOn == null ? 0 : toInteger(localeWeekStartsOn);
  var weekStartsOn = options.weekStartsOn == null ? defaultWeekStartsOn : toInteger(options.weekStartsOn); // Test if weekStartsOn is between 0 and 6 _and_ is not NaN

  if (!(weekStartsOn >= 0 && weekStartsOn <= 6)) {
    throw new RangeError('weekStartsOn must be between 0 and 6 inclusively');
  }

  var date = toDate(dirtyDate);
  var day = toInteger(dirtyDay);
  var currentDay = date.getUTCDay();
  var remainder = day % 7;
  var dayIndex = (remainder + 7) % 7;
  var diff = (dayIndex < weekStartsOn ? 7 : 0) + day - currentDay;
  date.setUTCDate(date.getUTCDate() + diff);
  return date;
}

// See issue: https://github.com/date-fns/date-fns/issues/376

function setUTCISODay(dirtyDate, dirtyDay) {
  requiredArgs(2, arguments);
  var day = toInteger(dirtyDay);

  if (day % 7 === 0) {
    day = day - 7;
  }

  var weekStartsOn = 1;
  var date = toDate(dirtyDate);
  var currentDay = date.getUTCDay();
  var remainder = day % 7;
  var dayIndex = (remainder + 7) % 7;
  var diff = (dayIndex < weekStartsOn ? 7 : 0) + day - currentDay;
  date.setUTCDate(date.getUTCDate() + diff);
  return date;
}

// See issue: https://github.com/date-fns/date-fns/issues/376

function setUTCISOWeek(dirtyDate, dirtyISOWeek) {
  requiredArgs(2, arguments);
  var date = toDate(dirtyDate);
  var isoWeek = toInteger(dirtyISOWeek);
  var diff = getUTCISOWeek(date) - isoWeek;
  date.setUTCDate(date.getUTCDate() - diff * 7);
  return date;
}

// See issue: https://github.com/date-fns/date-fns/issues/376

function setUTCWeek(dirtyDate, dirtyWeek, options) {
  requiredArgs(2, arguments);
  var date = toDate(dirtyDate);
  var week = toInteger(dirtyWeek);
  var diff = getUTCWeek(date, options) - week;
  date.setUTCDate(date.getUTCDate() - diff * 7);
  return date;
}

var MILLISECONDS_IN_HOUR = 3600000;
var MILLISECONDS_IN_MINUTE$1 = 60000;
var MILLISECONDS_IN_SECOND = 1000;
var numericPatterns = {
  month: /^(1[0-2]|0?\d)/,
  // 0 to 12
  date: /^(3[0-1]|[0-2]?\d)/,
  // 0 to 31
  dayOfYear: /^(36[0-6]|3[0-5]\d|[0-2]?\d?\d)/,
  // 0 to 366
  week: /^(5[0-3]|[0-4]?\d)/,
  // 0 to 53
  hour23h: /^(2[0-3]|[0-1]?\d)/,
  // 0 to 23
  hour24h: /^(2[0-4]|[0-1]?\d)/,
  // 0 to 24
  hour11h: /^(1[0-1]|0?\d)/,
  // 0 to 11
  hour12h: /^(1[0-2]|0?\d)/,
  // 0 to 12
  minute: /^[0-5]?\d/,
  // 0 to 59
  second: /^[0-5]?\d/,
  // 0 to 59
  singleDigit: /^\d/,
  // 0 to 9
  twoDigits: /^\d{1,2}/,
  // 0 to 99
  threeDigits: /^\d{1,3}/,
  // 0 to 999
  fourDigits: /^\d{1,4}/,
  // 0 to 9999
  anyDigitsSigned: /^-?\d+/,
  singleDigitSigned: /^-?\d/,
  // 0 to 9, -0 to -9
  twoDigitsSigned: /^-?\d{1,2}/,
  // 0 to 99, -0 to -99
  threeDigitsSigned: /^-?\d{1,3}/,
  // 0 to 999, -0 to -999
  fourDigitsSigned: /^-?\d{1,4}/ // 0 to 9999, -0 to -9999

};
var timezonePatterns = {
  basicOptionalMinutes: /^([+-])(\d{2})(\d{2})?|Z/,
  basic: /^([+-])(\d{2})(\d{2})|Z/,
  basicOptionalSeconds: /^([+-])(\d{2})(\d{2})((\d{2}))?|Z/,
  extended: /^([+-])(\d{2}):(\d{2})|Z/,
  extendedOptionalSeconds: /^([+-])(\d{2}):(\d{2})(:(\d{2}))?|Z/
};

function parseNumericPattern(pattern, string, valueCallback) {
  var matchResult = string.match(pattern);

  if (!matchResult) {
    return null;
  }

  var value = parseInt(matchResult[0], 10);
  return {
    value: valueCallback ? valueCallback(value) : value,
    rest: string.slice(matchResult[0].length)
  };
}

function parseTimezonePattern(pattern, string) {
  var matchResult = string.match(pattern);

  if (!matchResult) {
    return null;
  } // Input is 'Z'


  if (matchResult[0] === 'Z') {
    return {
      value: 0,
      rest: string.slice(1)
    };
  }

  var sign = matchResult[1] === '+' ? 1 : -1;
  var hours = matchResult[2] ? parseInt(matchResult[2], 10) : 0;
  var minutes = matchResult[3] ? parseInt(matchResult[3], 10) : 0;
  var seconds = matchResult[5] ? parseInt(matchResult[5], 10) : 0;
  return {
    value: sign * (hours * MILLISECONDS_IN_HOUR + minutes * MILLISECONDS_IN_MINUTE$1 + seconds * MILLISECONDS_IN_SECOND),
    rest: string.slice(matchResult[0].length)
  };
}

function parseAnyDigitsSigned(string, valueCallback) {
  return parseNumericPattern(numericPatterns.anyDigitsSigned, string, valueCallback);
}

function parseNDigits(n, string, valueCallback) {
  switch (n) {
    case 1:
      return parseNumericPattern(numericPatterns.singleDigit, string, valueCallback);

    case 2:
      return parseNumericPattern(numericPatterns.twoDigits, string, valueCallback);

    case 3:
      return parseNumericPattern(numericPatterns.threeDigits, string, valueCallback);

    case 4:
      return parseNumericPattern(numericPatterns.fourDigits, string, valueCallback);

    default:
      return parseNumericPattern(new RegExp('^\\d{1,' + n + '}'), string, valueCallback);
  }
}

function parseNDigitsSigned(n, string, valueCallback) {
  switch (n) {
    case 1:
      return parseNumericPattern(numericPatterns.singleDigitSigned, string, valueCallback);

    case 2:
      return parseNumericPattern(numericPatterns.twoDigitsSigned, string, valueCallback);

    case 3:
      return parseNumericPattern(numericPatterns.threeDigitsSigned, string, valueCallback);

    case 4:
      return parseNumericPattern(numericPatterns.fourDigitsSigned, string, valueCallback);

    default:
      return parseNumericPattern(new RegExp('^-?\\d{1,' + n + '}'), string, valueCallback);
  }
}

function dayPeriodEnumToHours(enumValue) {
  switch (enumValue) {
    case 'morning':
      return 4;

    case 'evening':
      return 17;

    case 'pm':
    case 'noon':
    case 'afternoon':
      return 12;

    case 'am':
    case 'midnight':
    case 'night':
    default:
      return 0;
  }
}

function normalizeTwoDigitYear(twoDigitYear, currentYear) {
  var isCommonEra = currentYear > 0; // Absolute number of the current year:
  // 1 -> 1 AC
  // 0 -> 1 BC
  // -1 -> 2 BC

  var absCurrentYear = isCommonEra ? currentYear : 1 - currentYear;
  var result;

  if (absCurrentYear <= 50) {
    result = twoDigitYear || 100;
  } else {
    var rangeEnd = absCurrentYear + 50;
    var rangeEndCentury = Math.floor(rangeEnd / 100) * 100;
    var isPreviousCentury = twoDigitYear >= rangeEnd % 100;
    result = twoDigitYear + rangeEndCentury - (isPreviousCentury ? 100 : 0);
  }

  return isCommonEra ? result : 1 - result;
}

var DAYS_IN_MONTH = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
var DAYS_IN_MONTH_LEAP_YEAR = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31]; // User for validation

function isLeapYearIndex(year) {
  return year % 400 === 0 || year % 4 === 0 && year % 100 !== 0;
}
/*
 * |     | Unit                           |     | Unit                           |
 * |-----|--------------------------------|-----|--------------------------------|
 * |  a  | AM, PM                         |  A* | Milliseconds in day            |
 * |  b  | AM, PM, noon, midnight         |  B  | Flexible day period            |
 * |  c  | Stand-alone local day of week  |  C* | Localized hour w/ day period   |
 * |  d  | Day of month                   |  D  | Day of year                    |
 * |  e  | Local day of week              |  E  | Day of week                    |
 * |  f  |                                |  F* | Day of week in month           |
 * |  g* | Modified Julian day            |  G  | Era                            |
 * |  h  | Hour [1-12]                    |  H  | Hour [0-23]                    |
 * |  i! | ISO day of week                |  I! | ISO week of year               |
 * |  j* | Localized hour w/ day period   |  J* | Localized hour w/o day period  |
 * |  k  | Hour [1-24]                    |  K  | Hour [0-11]                    |
 * |  l* | (deprecated)                   |  L  | Stand-alone month              |
 * |  m  | Minute                         |  M  | Month                          |
 * |  n  |                                |  N  |                                |
 * |  o! | Ordinal number modifier        |  O* | Timezone (GMT)                 |
 * |  p  |                                |  P  |                                |
 * |  q  | Stand-alone quarter            |  Q  | Quarter                        |
 * |  r* | Related Gregorian year         |  R! | ISO week-numbering year        |
 * |  s  | Second                         |  S  | Fraction of second             |
 * |  t! | Seconds timestamp              |  T! | Milliseconds timestamp         |
 * |  u  | Extended year                  |  U* | Cyclic year                    |
 * |  v* | Timezone (generic non-locat.)  |  V* | Timezone (location)            |
 * |  w  | Local week of year             |  W* | Week of month                  |
 * |  x  | Timezone (ISO-8601 w/o Z)      |  X  | Timezone (ISO-8601)            |
 * |  y  | Year (abs)                     |  Y  | Local week-numbering year      |
 * |  z* | Timezone (specific non-locat.) |  Z* | Timezone (aliases)             |
 *
 * Letters marked by * are not implemented but reserved by Unicode standard.
 *
 * Letters marked by ! are non-standard, but implemented by date-fns:
 * - `o` modifies the previous token to turn it into an ordinal (see `parse` docs)
 * - `i` is ISO day of week. For `i` and `ii` is returns numeric ISO week days,
 *   i.e. 7 for Sunday, 1 for Monday, etc.
 * - `I` is ISO week of year, as opposed to `w` which is local week of year.
 * - `R` is ISO week-numbering year, as opposed to `Y` which is local week-numbering year.
 *   `R` is supposed to be used in conjunction with `I` and `i`
 *   for universal ISO week-numbering date, whereas
 *   `Y` is supposed to be used in conjunction with `w` and `e`
 *   for week-numbering date specific to the locale.
 */


var parsers = {
  // Era
  G: {
    priority: 140,
    parse: function (string, token, match, _options) {
      switch (token) {
        // AD, BC
        case 'G':
        case 'GG':
        case 'GGG':
          return match.era(string, {
            width: 'abbreviated'
          }) || match.era(string, {
            width: 'narrow'
          });
        // A, B

        case 'GGGGG':
          return match.era(string, {
            width: 'narrow'
          });
        // Anno Domini, Before Christ

        case 'GGGG':
        default:
          return match.era(string, {
            width: 'wide'
          }) || match.era(string, {
            width: 'abbreviated'
          }) || match.era(string, {
            width: 'narrow'
          });
      }
    },
    set: function (date, flags, value, _options) {
      flags.era = value;
      date.setUTCFullYear(value, 0, 1);
      date.setUTCHours(0, 0, 0, 0);
      return date;
    },
    incompatibleTokens: ['R', 'u', 't', 'T']
  },
  // Year
  y: {
    // From http://www.unicode.org/reports/tr35/tr35-31/tr35-dates.html#Date_Format_Patterns
    // | Year     |     y | yy |   yyy |  yyyy | yyyyy |
    // |----------|-------|----|-------|-------|-------|
    // | AD 1     |     1 | 01 |   001 |  0001 | 00001 |
    // | AD 12    |    12 | 12 |   012 |  0012 | 00012 |
    // | AD 123   |   123 | 23 |   123 |  0123 | 00123 |
    // | AD 1234  |  1234 | 34 |  1234 |  1234 | 01234 |
    // | AD 12345 | 12345 | 45 | 12345 | 12345 | 12345 |
    priority: 130,
    parse: function (string, token, match, _options) {
      var valueCallback = function (year) {
        return {
          year: year,
          isTwoDigitYear: token === 'yy'
        };
      };

      switch (token) {
        case 'y':
          return parseNDigits(4, string, valueCallback);

        case 'yo':
          return match.ordinalNumber(string, {
            unit: 'year',
            valueCallback: valueCallback
          });

        default:
          return parseNDigits(token.length, string, valueCallback);
      }
    },
    validate: function (_date, value, _options) {
      return value.isTwoDigitYear || value.year > 0;
    },
    set: function (date, flags, value, _options) {
      var currentYear = date.getUTCFullYear();

      if (value.isTwoDigitYear) {
        var normalizedTwoDigitYear = normalizeTwoDigitYear(value.year, currentYear);
        date.setUTCFullYear(normalizedTwoDigitYear, 0, 1);
        date.setUTCHours(0, 0, 0, 0);
        return date;
      }

      var year = !('era' in flags) || flags.era === 1 ? value.year : 1 - value.year;
      date.setUTCFullYear(year, 0, 1);
      date.setUTCHours(0, 0, 0, 0);
      return date;
    },
    incompatibleTokens: ['Y', 'R', 'u', 'w', 'I', 'i', 'e', 'c', 't', 'T']
  },
  // Local week-numbering year
  Y: {
    priority: 130,
    parse: function (string, token, match, _options) {
      var valueCallback = function (year) {
        return {
          year: year,
          isTwoDigitYear: token === 'YY'
        };
      };

      switch (token) {
        case 'Y':
          return parseNDigits(4, string, valueCallback);

        case 'Yo':
          return match.ordinalNumber(string, {
            unit: 'year',
            valueCallback: valueCallback
          });

        default:
          return parseNDigits(token.length, string, valueCallback);
      }
    },
    validate: function (_date, value, _options) {
      return value.isTwoDigitYear || value.year > 0;
    },
    set: function (date, flags, value, options) {
      var currentYear = getUTCWeekYear(date, options);

      if (value.isTwoDigitYear) {
        var normalizedTwoDigitYear = normalizeTwoDigitYear(value.year, currentYear);
        date.setUTCFullYear(normalizedTwoDigitYear, 0, options.firstWeekContainsDate);
        date.setUTCHours(0, 0, 0, 0);
        return startOfUTCWeek(date, options);
      }

      var year = !('era' in flags) || flags.era === 1 ? value.year : 1 - value.year;
      date.setUTCFullYear(year, 0, options.firstWeekContainsDate);
      date.setUTCHours(0, 0, 0, 0);
      return startOfUTCWeek(date, options);
    },
    incompatibleTokens: ['y', 'R', 'u', 'Q', 'q', 'M', 'L', 'I', 'd', 'D', 'i', 't', 'T']
  },
  // ISO week-numbering year
  R: {
    priority: 130,
    parse: function (string, token, _match, _options) {
      if (token === 'R') {
        return parseNDigitsSigned(4, string);
      }

      return parseNDigitsSigned(token.length, string);
    },
    set: function (_date, _flags, value, _options) {
      var firstWeekOfYear = new Date(0);
      firstWeekOfYear.setUTCFullYear(value, 0, 4);
      firstWeekOfYear.setUTCHours(0, 0, 0, 0);
      return startOfUTCISOWeek(firstWeekOfYear);
    },
    incompatibleTokens: ['G', 'y', 'Y', 'u', 'Q', 'q', 'M', 'L', 'w', 'd', 'D', 'e', 'c', 't', 'T']
  },
  // Extended year
  u: {
    priority: 130,
    parse: function (string, token, _match, _options) {
      if (token === 'u') {
        return parseNDigitsSigned(4, string);
      }

      return parseNDigitsSigned(token.length, string);
    },
    set: function (date, _flags, value, _options) {
      date.setUTCFullYear(value, 0, 1);
      date.setUTCHours(0, 0, 0, 0);
      return date;
    },
    incompatibleTokens: ['G', 'y', 'Y', 'R', 'w', 'I', 'i', 'e', 'c', 't', 'T']
  },
  // Quarter
  Q: {
    priority: 120,
    parse: function (string, token, match, _options) {
      switch (token) {
        // 1, 2, 3, 4
        case 'Q':
        case 'QQ':
          // 01, 02, 03, 04
          return parseNDigits(token.length, string);
        // 1st, 2nd, 3rd, 4th

        case 'Qo':
          return match.ordinalNumber(string, {
            unit: 'quarter'
          });
        // Q1, Q2, Q3, Q4

        case 'QQQ':
          return match.quarter(string, {
            width: 'abbreviated',
            context: 'formatting'
          }) || match.quarter(string, {
            width: 'narrow',
            context: 'formatting'
          });
        // 1, 2, 3, 4 (narrow quarter; could be not numerical)

        case 'QQQQQ':
          return match.quarter(string, {
            width: 'narrow',
            context: 'formatting'
          });
        // 1st quarter, 2nd quarter, ...

        case 'QQQQ':
        default:
          return match.quarter(string, {
            width: 'wide',
            context: 'formatting'
          }) || match.quarter(string, {
            width: 'abbreviated',
            context: 'formatting'
          }) || match.quarter(string, {
            width: 'narrow',
            context: 'formatting'
          });
      }
    },
    validate: function (_date, value, _options) {
      return value >= 1 && value <= 4;
    },
    set: function (date, _flags, value, _options) {
      date.setUTCMonth((value - 1) * 3, 1);
      date.setUTCHours(0, 0, 0, 0);
      return date;
    },
    incompatibleTokens: ['Y', 'R', 'q', 'M', 'L', 'w', 'I', 'd', 'D', 'i', 'e', 'c', 't', 'T']
  },
  // Stand-alone quarter
  q: {
    priority: 120,
    parse: function (string, token, match, _options) {
      switch (token) {
        // 1, 2, 3, 4
        case 'q':
        case 'qq':
          // 01, 02, 03, 04
          return parseNDigits(token.length, string);
        // 1st, 2nd, 3rd, 4th

        case 'qo':
          return match.ordinalNumber(string, {
            unit: 'quarter'
          });
        // Q1, Q2, Q3, Q4

        case 'qqq':
          return match.quarter(string, {
            width: 'abbreviated',
            context: 'standalone'
          }) || match.quarter(string, {
            width: 'narrow',
            context: 'standalone'
          });
        // 1, 2, 3, 4 (narrow quarter; could be not numerical)

        case 'qqqqq':
          return match.quarter(string, {
            width: 'narrow',
            context: 'standalone'
          });
        // 1st quarter, 2nd quarter, ...

        case 'qqqq':
        default:
          return match.quarter(string, {
            width: 'wide',
            context: 'standalone'
          }) || match.quarter(string, {
            width: 'abbreviated',
            context: 'standalone'
          }) || match.quarter(string, {
            width: 'narrow',
            context: 'standalone'
          });
      }
    },
    validate: function (_date, value, _options) {
      return value >= 1 && value <= 4;
    },
    set: function (date, _flags, value, _options) {
      date.setUTCMonth((value - 1) * 3, 1);
      date.setUTCHours(0, 0, 0, 0);
      return date;
    },
    incompatibleTokens: ['Y', 'R', 'Q', 'M', 'L', 'w', 'I', 'd', 'D', 'i', 'e', 'c', 't', 'T']
  },
  // Month
  M: {
    priority: 110,
    parse: function (string, token, match, _options) {
      var valueCallback = function (value) {
        return value - 1;
      };

      switch (token) {
        // 1, 2, ..., 12
        case 'M':
          return parseNumericPattern(numericPatterns.month, string, valueCallback);
        // 01, 02, ..., 12

        case 'MM':
          return parseNDigits(2, string, valueCallback);
        // 1st, 2nd, ..., 12th

        case 'Mo':
          return match.ordinalNumber(string, {
            unit: 'month',
            valueCallback: valueCallback
          });
        // Jan, Feb, ..., Dec

        case 'MMM':
          return match.month(string, {
            width: 'abbreviated',
            context: 'formatting'
          }) || match.month(string, {
            width: 'narrow',
            context: 'formatting'
          });
        // J, F, ..., D

        case 'MMMMM':
          return match.month(string, {
            width: 'narrow',
            context: 'formatting'
          });
        // January, February, ..., December

        case 'MMMM':
        default:
          return match.month(string, {
            width: 'wide',
            context: 'formatting'
          }) || match.month(string, {
            width: 'abbreviated',
            context: 'formatting'
          }) || match.month(string, {
            width: 'narrow',
            context: 'formatting'
          });
      }
    },
    validate: function (_date, value, _options) {
      return value >= 0 && value <= 11;
    },
    set: function (date, _flags, value, _options) {
      date.setUTCMonth(value, 1);
      date.setUTCHours(0, 0, 0, 0);
      return date;
    },
    incompatibleTokens: ['Y', 'R', 'q', 'Q', 'L', 'w', 'I', 'D', 'i', 'e', 'c', 't', 'T']
  },
  // Stand-alone month
  L: {
    priority: 110,
    parse: function (string, token, match, _options) {
      var valueCallback = function (value) {
        return value - 1;
      };

      switch (token) {
        // 1, 2, ..., 12
        case 'L':
          return parseNumericPattern(numericPatterns.month, string, valueCallback);
        // 01, 02, ..., 12

        case 'LL':
          return parseNDigits(2, string, valueCallback);
        // 1st, 2nd, ..., 12th

        case 'Lo':
          return match.ordinalNumber(string, {
            unit: 'month',
            valueCallback: valueCallback
          });
        // Jan, Feb, ..., Dec

        case 'LLL':
          return match.month(string, {
            width: 'abbreviated',
            context: 'standalone'
          }) || match.month(string, {
            width: 'narrow',
            context: 'standalone'
          });
        // J, F, ..., D

        case 'LLLLL':
          return match.month(string, {
            width: 'narrow',
            context: 'standalone'
          });
        // January, February, ..., December

        case 'LLLL':
        default:
          return match.month(string, {
            width: 'wide',
            context: 'standalone'
          }) || match.month(string, {
            width: 'abbreviated',
            context: 'standalone'
          }) || match.month(string, {
            width: 'narrow',
            context: 'standalone'
          });
      }
    },
    validate: function (_date, value, _options) {
      return value >= 0 && value <= 11;
    },
    set: function (date, _flags, value, _options) {
      date.setUTCMonth(value, 1);
      date.setUTCHours(0, 0, 0, 0);
      return date;
    },
    incompatibleTokens: ['Y', 'R', 'q', 'Q', 'M', 'w', 'I', 'D', 'i', 'e', 'c', 't', 'T']
  },
  // Local week of year
  w: {
    priority: 100,
    parse: function (string, token, match, _options) {
      switch (token) {
        case 'w':
          return parseNumericPattern(numericPatterns.week, string);

        case 'wo':
          return match.ordinalNumber(string, {
            unit: 'week'
          });

        default:
          return parseNDigits(token.length, string);
      }
    },
    validate: function (_date, value, _options) {
      return value >= 1 && value <= 53;
    },
    set: function (date, _flags, value, options) {
      return startOfUTCWeek(setUTCWeek(date, value, options), options);
    },
    incompatibleTokens: ['y', 'R', 'u', 'q', 'Q', 'M', 'L', 'I', 'd', 'D', 'i', 't', 'T']
  },
  // ISO week of year
  I: {
    priority: 100,
    parse: function (string, token, match, _options) {
      switch (token) {
        case 'I':
          return parseNumericPattern(numericPatterns.week, string);

        case 'Io':
          return match.ordinalNumber(string, {
            unit: 'week'
          });

        default:
          return parseNDigits(token.length, string);
      }
    },
    validate: function (_date, value, _options) {
      return value >= 1 && value <= 53;
    },
    set: function (date, _flags, value, options) {
      return startOfUTCISOWeek(setUTCISOWeek(date, value, options), options);
    },
    incompatibleTokens: ['y', 'Y', 'u', 'q', 'Q', 'M', 'L', 'w', 'd', 'D', 'e', 'c', 't', 'T']
  },
  // Day of the month
  d: {
    priority: 90,
    subPriority: 1,
    parse: function (string, token, match, _options) {
      switch (token) {
        case 'd':
          return parseNumericPattern(numericPatterns.date, string);

        case 'do':
          return match.ordinalNumber(string, {
            unit: 'date'
          });

        default:
          return parseNDigits(token.length, string);
      }
    },
    validate: function (date, value, _options) {
      var year = date.getUTCFullYear();
      var isLeapYear = isLeapYearIndex(year);
      var month = date.getUTCMonth();

      if (isLeapYear) {
        return value >= 1 && value <= DAYS_IN_MONTH_LEAP_YEAR[month];
      } else {
        return value >= 1 && value <= DAYS_IN_MONTH[month];
      }
    },
    set: function (date, _flags, value, _options) {
      date.setUTCDate(value);
      date.setUTCHours(0, 0, 0, 0);
      return date;
    },
    incompatibleTokens: ['Y', 'R', 'q', 'Q', 'w', 'I', 'D', 'i', 'e', 'c', 't', 'T']
  },
  // Day of year
  D: {
    priority: 90,
    subPriority: 1,
    parse: function (string, token, match, _options) {
      switch (token) {
        case 'D':
        case 'DD':
          return parseNumericPattern(numericPatterns.dayOfYear, string);

        case 'Do':
          return match.ordinalNumber(string, {
            unit: 'date'
          });

        default:
          return parseNDigits(token.length, string);
      }
    },
    validate: function (date, value, _options) {
      var year = date.getUTCFullYear();
      var isLeapYear = isLeapYearIndex(year);

      if (isLeapYear) {
        return value >= 1 && value <= 366;
      } else {
        return value >= 1 && value <= 365;
      }
    },
    set: function (date, _flags, value, _options) {
      date.setUTCMonth(0, value);
      date.setUTCHours(0, 0, 0, 0);
      return date;
    },
    incompatibleTokens: ['Y', 'R', 'q', 'Q', 'M', 'L', 'w', 'I', 'd', 'E', 'i', 'e', 'c', 't', 'T']
  },
  // Day of week
  E: {
    priority: 90,
    parse: function (string, token, match, _options) {
      switch (token) {
        // Tue
        case 'E':
        case 'EE':
        case 'EEE':
          return match.day(string, {
            width: 'abbreviated',
            context: 'formatting'
          }) || match.day(string, {
            width: 'short',
            context: 'formatting'
          }) || match.day(string, {
            width: 'narrow',
            context: 'formatting'
          });
        // T

        case 'EEEEE':
          return match.day(string, {
            width: 'narrow',
            context: 'formatting'
          });
        // Tu

        case 'EEEEEE':
          return match.day(string, {
            width: 'short',
            context: 'formatting'
          }) || match.day(string, {
            width: 'narrow',
            context: 'formatting'
          });
        // Tuesday

        case 'EEEE':
        default:
          return match.day(string, {
            width: 'wide',
            context: 'formatting'
          }) || match.day(string, {
            width: 'abbreviated',
            context: 'formatting'
          }) || match.day(string, {
            width: 'short',
            context: 'formatting'
          }) || match.day(string, {
            width: 'narrow',
            context: 'formatting'
          });
      }
    },
    validate: function (_date, value, _options) {
      return value >= 0 && value <= 6;
    },
    set: function (date, _flags, value, options) {
      date = setUTCDay(date, value, options);
      date.setUTCHours(0, 0, 0, 0);
      return date;
    },
    incompatibleTokens: ['D', 'i', 'e', 'c', 't', 'T']
  },
  // Local day of week
  e: {
    priority: 90,
    parse: function (string, token, match, options) {
      var valueCallback = function (value) {
        var wholeWeekDays = Math.floor((value - 1) / 7) * 7;
        return (value + options.weekStartsOn + 6) % 7 + wholeWeekDays;
      };

      switch (token) {
        // 3
        case 'e':
        case 'ee':
          // 03
          return parseNDigits(token.length, string, valueCallback);
        // 3rd

        case 'eo':
          return match.ordinalNumber(string, {
            unit: 'day',
            valueCallback: valueCallback
          });
        // Tue

        case 'eee':
          return match.day(string, {
            width: 'abbreviated',
            context: 'formatting'
          }) || match.day(string, {
            width: 'short',
            context: 'formatting'
          }) || match.day(string, {
            width: 'narrow',
            context: 'formatting'
          });
        // T

        case 'eeeee':
          return match.day(string, {
            width: 'narrow',
            context: 'formatting'
          });
        // Tu

        case 'eeeeee':
          return match.day(string, {
            width: 'short',
            context: 'formatting'
          }) || match.day(string, {
            width: 'narrow',
            context: 'formatting'
          });
        // Tuesday

        case 'eeee':
        default:
          return match.day(string, {
            width: 'wide',
            context: 'formatting'
          }) || match.day(string, {
            width: 'abbreviated',
            context: 'formatting'
          }) || match.day(string, {
            width: 'short',
            context: 'formatting'
          }) || match.day(string, {
            width: 'narrow',
            context: 'formatting'
          });
      }
    },
    validate: function (_date, value, _options) {
      return value >= 0 && value <= 6;
    },
    set: function (date, _flags, value, options) {
      date = setUTCDay(date, value, options);
      date.setUTCHours(0, 0, 0, 0);
      return date;
    },
    incompatibleTokens: ['y', 'R', 'u', 'q', 'Q', 'M', 'L', 'I', 'd', 'D', 'E', 'i', 'c', 't', 'T']
  },
  // Stand-alone local day of week
  c: {
    priority: 90,
    parse: function (string, token, match, options) {
      var valueCallback = function (value) {
        var wholeWeekDays = Math.floor((value - 1) / 7) * 7;
        return (value + options.weekStartsOn + 6) % 7 + wholeWeekDays;
      };

      switch (token) {
        // 3
        case 'c':
        case 'cc':
          // 03
          return parseNDigits(token.length, string, valueCallback);
        // 3rd

        case 'co':
          return match.ordinalNumber(string, {
            unit: 'day',
            valueCallback: valueCallback
          });
        // Tue

        case 'ccc':
          return match.day(string, {
            width: 'abbreviated',
            context: 'standalone'
          }) || match.day(string, {
            width: 'short',
            context: 'standalone'
          }) || match.day(string, {
            width: 'narrow',
            context: 'standalone'
          });
        // T

        case 'ccccc':
          return match.day(string, {
            width: 'narrow',
            context: 'standalone'
          });
        // Tu

        case 'cccccc':
          return match.day(string, {
            width: 'short',
            context: 'standalone'
          }) || match.day(string, {
            width: 'narrow',
            context: 'standalone'
          });
        // Tuesday

        case 'cccc':
        default:
          return match.day(string, {
            width: 'wide',
            context: 'standalone'
          }) || match.day(string, {
            width: 'abbreviated',
            context: 'standalone'
          }) || match.day(string, {
            width: 'short',
            context: 'standalone'
          }) || match.day(string, {
            width: 'narrow',
            context: 'standalone'
          });
      }
    },
    validate: function (_date, value, _options) {
      return value >= 0 && value <= 6;
    },
    set: function (date, _flags, value, options) {
      date = setUTCDay(date, value, options);
      date.setUTCHours(0, 0, 0, 0);
      return date;
    },
    incompatibleTokens: ['y', 'R', 'u', 'q', 'Q', 'M', 'L', 'I', 'd', 'D', 'E', 'i', 'e', 't', 'T']
  },
  // ISO day of week
  i: {
    priority: 90,
    parse: function (string, token, match, _options) {
      var valueCallback = function (value) {
        if (value === 0) {
          return 7;
        }

        return value;
      };

      switch (token) {
        // 2
        case 'i':
        case 'ii':
          // 02
          return parseNDigits(token.length, string);
        // 2nd

        case 'io':
          return match.ordinalNumber(string, {
            unit: 'day'
          });
        // Tue

        case 'iii':
          return match.day(string, {
            width: 'abbreviated',
            context: 'formatting',
            valueCallback: valueCallback
          }) || match.day(string, {
            width: 'short',
            context: 'formatting',
            valueCallback: valueCallback
          }) || match.day(string, {
            width: 'narrow',
            context: 'formatting',
            valueCallback: valueCallback
          });
        // T

        case 'iiiii':
          return match.day(string, {
            width: 'narrow',
            context: 'formatting',
            valueCallback: valueCallback
          });
        // Tu

        case 'iiiiii':
          return match.day(string, {
            width: 'short',
            context: 'formatting',
            valueCallback: valueCallback
          }) || match.day(string, {
            width: 'narrow',
            context: 'formatting',
            valueCallback: valueCallback
          });
        // Tuesday

        case 'iiii':
        default:
          return match.day(string, {
            width: 'wide',
            context: 'formatting',
            valueCallback: valueCallback
          }) || match.day(string, {
            width: 'abbreviated',
            context: 'formatting',
            valueCallback: valueCallback
          }) || match.day(string, {
            width: 'short',
            context: 'formatting',
            valueCallback: valueCallback
          }) || match.day(string, {
            width: 'narrow',
            context: 'formatting',
            valueCallback: valueCallback
          });
      }
    },
    validate: function (_date, value, _options) {
      return value >= 1 && value <= 7;
    },
    set: function (date, _flags, value, options) {
      date = setUTCISODay(date, value, options);
      date.setUTCHours(0, 0, 0, 0);
      return date;
    },
    incompatibleTokens: ['y', 'Y', 'u', 'q', 'Q', 'M', 'L', 'w', 'd', 'D', 'E', 'e', 'c', 't', 'T']
  },
  // AM or PM
  a: {
    priority: 80,
    parse: function (string, token, match, _options) {
      switch (token) {
        case 'a':
        case 'aa':
        case 'aaa':
          return match.dayPeriod(string, {
            width: 'abbreviated',
            context: 'formatting'
          }) || match.dayPeriod(string, {
            width: 'narrow',
            context: 'formatting'
          });

        case 'aaaaa':
          return match.dayPeriod(string, {
            width: 'narrow',
            context: 'formatting'
          });

        case 'aaaa':
        default:
          return match.dayPeriod(string, {
            width: 'wide',
            context: 'formatting'
          }) || match.dayPeriod(string, {
            width: 'abbreviated',
            context: 'formatting'
          }) || match.dayPeriod(string, {
            width: 'narrow',
            context: 'formatting'
          });
      }
    },
    set: function (date, _flags, value, _options) {
      date.setUTCHours(dayPeriodEnumToHours(value), 0, 0, 0);
      return date;
    },
    incompatibleTokens: ['b', 'B', 'H', 'k', 't', 'T']
  },
  // AM, PM, midnight
  b: {
    priority: 80,
    parse: function (string, token, match, _options) {
      switch (token) {
        case 'b':
        case 'bb':
        case 'bbb':
          return match.dayPeriod(string, {
            width: 'abbreviated',
            context: 'formatting'
          }) || match.dayPeriod(string, {
            width: 'narrow',
            context: 'formatting'
          });

        case 'bbbbb':
          return match.dayPeriod(string, {
            width: 'narrow',
            context: 'formatting'
          });

        case 'bbbb':
        default:
          return match.dayPeriod(string, {
            width: 'wide',
            context: 'formatting'
          }) || match.dayPeriod(string, {
            width: 'abbreviated',
            context: 'formatting'
          }) || match.dayPeriod(string, {
            width: 'narrow',
            context: 'formatting'
          });
      }
    },
    set: function (date, _flags, value, _options) {
      date.setUTCHours(dayPeriodEnumToHours(value), 0, 0, 0);
      return date;
    },
    incompatibleTokens: ['a', 'B', 'H', 'k', 't', 'T']
  },
  // in the morning, in the afternoon, in the evening, at night
  B: {
    priority: 80,
    parse: function (string, token, match, _options) {
      switch (token) {
        case 'B':
        case 'BB':
        case 'BBB':
          return match.dayPeriod(string, {
            width: 'abbreviated',
            context: 'formatting'
          }) || match.dayPeriod(string, {
            width: 'narrow',
            context: 'formatting'
          });

        case 'BBBBB':
          return match.dayPeriod(string, {
            width: 'narrow',
            context: 'formatting'
          });

        case 'BBBB':
        default:
          return match.dayPeriod(string, {
            width: 'wide',
            context: 'formatting'
          }) || match.dayPeriod(string, {
            width: 'abbreviated',
            context: 'formatting'
          }) || match.dayPeriod(string, {
            width: 'narrow',
            context: 'formatting'
          });
      }
    },
    set: function (date, _flags, value, _options) {
      date.setUTCHours(dayPeriodEnumToHours(value), 0, 0, 0);
      return date;
    },
    incompatibleTokens: ['a', 'b', 't', 'T']
  },
  // Hour [1-12]
  h: {
    priority: 70,
    parse: function (string, token, match, _options) {
      switch (token) {
        case 'h':
          return parseNumericPattern(numericPatterns.hour12h, string);

        case 'ho':
          return match.ordinalNumber(string, {
            unit: 'hour'
          });

        default:
          return parseNDigits(token.length, string);
      }
    },
    validate: function (_date, value, _options) {
      return value >= 1 && value <= 12;
    },
    set: function (date, _flags, value, _options) {
      var isPM = date.getUTCHours() >= 12;

      if (isPM && value < 12) {
        date.setUTCHours(value + 12, 0, 0, 0);
      } else if (!isPM && value === 12) {
        date.setUTCHours(0, 0, 0, 0);
      } else {
        date.setUTCHours(value, 0, 0, 0);
      }

      return date;
    },
    incompatibleTokens: ['H', 'K', 'k', 't', 'T']
  },
  // Hour [0-23]
  H: {
    priority: 70,
    parse: function (string, token, match, _options) {
      switch (token) {
        case 'H':
          return parseNumericPattern(numericPatterns.hour23h, string);

        case 'Ho':
          return match.ordinalNumber(string, {
            unit: 'hour'
          });

        default:
          return parseNDigits(token.length, string);
      }
    },
    validate: function (_date, value, _options) {
      return value >= 0 && value <= 23;
    },
    set: function (date, _flags, value, _options) {
      date.setUTCHours(value, 0, 0, 0);
      return date;
    },
    incompatibleTokens: ['a', 'b', 'h', 'K', 'k', 't', 'T']
  },
  // Hour [0-11]
  K: {
    priority: 70,
    parse: function (string, token, match, _options) {
      switch (token) {
        case 'K':
          return parseNumericPattern(numericPatterns.hour11h, string);

        case 'Ko':
          return match.ordinalNumber(string, {
            unit: 'hour'
          });

        default:
          return parseNDigits(token.length, string);
      }
    },
    validate: function (_date, value, _options) {
      return value >= 0 && value <= 11;
    },
    set: function (date, _flags, value, _options) {
      var isPM = date.getUTCHours() >= 12;

      if (isPM && value < 12) {
        date.setUTCHours(value + 12, 0, 0, 0);
      } else {
        date.setUTCHours(value, 0, 0, 0);
      }

      return date;
    },
    incompatibleTokens: ['h', 'H', 'k', 't', 'T']
  },
  // Hour [1-24]
  k: {
    priority: 70,
    parse: function (string, token, match, _options) {
      switch (token) {
        case 'k':
          return parseNumericPattern(numericPatterns.hour24h, string);

        case 'ko':
          return match.ordinalNumber(string, {
            unit: 'hour'
          });

        default:
          return parseNDigits(token.length, string);
      }
    },
    validate: function (_date, value, _options) {
      return value >= 1 && value <= 24;
    },
    set: function (date, _flags, value, _options) {
      var hours = value <= 24 ? value % 24 : value;
      date.setUTCHours(hours, 0, 0, 0);
      return date;
    },
    incompatibleTokens: ['a', 'b', 'h', 'H', 'K', 't', 'T']
  },
  // Minute
  m: {
    priority: 60,
    parse: function (string, token, match, _options) {
      switch (token) {
        case 'm':
          return parseNumericPattern(numericPatterns.minute, string);

        case 'mo':
          return match.ordinalNumber(string, {
            unit: 'minute'
          });

        default:
          return parseNDigits(token.length, string);
      }
    },
    validate: function (_date, value, _options) {
      return value >= 0 && value <= 59;
    },
    set: function (date, _flags, value, _options) {
      date.setUTCMinutes(value, 0, 0);
      return date;
    },
    incompatibleTokens: ['t', 'T']
  },
  // Second
  s: {
    priority: 50,
    parse: function (string, token, match, _options) {
      switch (token) {
        case 's':
          return parseNumericPattern(numericPatterns.second, string);

        case 'so':
          return match.ordinalNumber(string, {
            unit: 'second'
          });

        default:
          return parseNDigits(token.length, string);
      }
    },
    validate: function (_date, value, _options) {
      return value >= 0 && value <= 59;
    },
    set: function (date, _flags, value, _options) {
      date.setUTCSeconds(value, 0);
      return date;
    },
    incompatibleTokens: ['t', 'T']
  },
  // Fraction of second
  S: {
    priority: 30,
    parse: function (string, token, _match, _options) {
      var valueCallback = function (value) {
        return Math.floor(value * Math.pow(10, -token.length + 3));
      };

      return parseNDigits(token.length, string, valueCallback);
    },
    set: function (date, _flags, value, _options) {
      date.setUTCMilliseconds(value);
      return date;
    },
    incompatibleTokens: ['t', 'T']
  },
  // Timezone (ISO-8601. +00:00 is `'Z'`)
  X: {
    priority: 10,
    parse: function (string, token, _match, _options) {
      switch (token) {
        case 'X':
          return parseTimezonePattern(timezonePatterns.basicOptionalMinutes, string);

        case 'XX':
          return parseTimezonePattern(timezonePatterns.basic, string);

        case 'XXXX':
          return parseTimezonePattern(timezonePatterns.basicOptionalSeconds, string);

        case 'XXXXX':
          return parseTimezonePattern(timezonePatterns.extendedOptionalSeconds, string);

        case 'XXX':
        default:
          return parseTimezonePattern(timezonePatterns.extended, string);
      }
    },
    set: function (date, flags, value, _options) {
      if (flags.timestampIsSet) {
        return date;
      }

      return new Date(date.getTime() - value);
    },
    incompatibleTokens: ['t', 'T', 'x']
  },
  // Timezone (ISO-8601)
  x: {
    priority: 10,
    parse: function (string, token, _match, _options) {
      switch (token) {
        case 'x':
          return parseTimezonePattern(timezonePatterns.basicOptionalMinutes, string);

        case 'xx':
          return parseTimezonePattern(timezonePatterns.basic, string);

        case 'xxxx':
          return parseTimezonePattern(timezonePatterns.basicOptionalSeconds, string);

        case 'xxxxx':
          return parseTimezonePattern(timezonePatterns.extendedOptionalSeconds, string);

        case 'xxx':
        default:
          return parseTimezonePattern(timezonePatterns.extended, string);
      }
    },
    set: function (date, flags, value, _options) {
      if (flags.timestampIsSet) {
        return date;
      }

      return new Date(date.getTime() - value);
    },
    incompatibleTokens: ['t', 'T', 'X']
  },
  // Seconds timestamp
  t: {
    priority: 40,
    parse: function (string, _token, _match, _options) {
      return parseAnyDigitsSigned(string);
    },
    set: function (_date, _flags, value, _options) {
      return [new Date(value * 1000), {
        timestampIsSet: true
      }];
    },
    incompatibleTokens: '*'
  },
  // Milliseconds timestamp
  T: {
    priority: 20,
    parse: function (string, _token, _match, _options) {
      return parseAnyDigitsSigned(string);
    },
    set: function (_date, _flags, value, _options) {
      return [new Date(value), {
        timestampIsSet: true
      }];
    },
    incompatibleTokens: '*'
  }
};
var parsers$1 = parsers;

var TIMEZONE_UNIT_PRIORITY = 10; // This RegExp consists of three parts separated by `|`:
// - [yYQqMLwIdDecihHKkms]o matches any available ordinal number token
//   (one of the certain letters followed by `o`)
// - (\w)\1* matches any sequences of the same letter
// - '' matches two quote characters in a row
// - '(''|[^'])+('|$) matches anything surrounded by two quote characters ('),
//   except a single quote symbol, which ends the sequence.
//   Two quote characters do not end the sequence.
//   If there is no matching single quote
//   then the sequence will continue until the end of the string.
// - . matches any single character unmatched by previous parts of the RegExps

var formattingTokensRegExp = /[yYQqMLwIdDecihHKkms]o|(\w)\1*|''|'(''|[^'])+('|$)|./g; // This RegExp catches symbols escaped by quotes, and also
// sequences of symbols P, p, and the combinations like `PPPPPPPppppp`

var longFormattingTokensRegExp = /P+p+|P+|p+|''|'(''|[^'])+('|$)|./g;
var escapedStringRegExp = /^'([^]*?)'?$/;
var doubleQuoteRegExp = /''/g;
var notWhitespaceRegExp = /\S/;
var unescapedLatinCharacterRegExp = /[a-zA-Z]/;
/**
 * @name parse
 * @category Common Helpers
 * @summary Parse the date.
 *
 * @description
 * Return the date parsed from string using the given format string.
 *
 * > ⚠️ Please note that the `format` tokens differ from Moment.js and other libraries.
 * > See: https://git.io/fxCyr
 *
 * The characters in the format string wrapped between two single quotes characters (') are escaped.
 * Two single quotes in a row, whether inside or outside a quoted sequence, represent a 'real' single quote.
 *
 * Format of the format string is based on Unicode Technical Standard #35:
 * https://www.unicode.org/reports/tr35/tr35-dates.html#Date_Field_Symbol_Table
 * with a few additions (see note 5 below the table).
 *
 * Not all tokens are compatible. Combinations that don't make sense or could lead to bugs are prohibited
 * and will throw `RangeError`. For example usage of 24-hour format token with AM/PM token will throw an exception:
 *
 * ```javascript
 * parse('23 AM', 'HH a', new Date())
 * //=> RangeError: The format string mustn't contain `HH` and `a` at the same time
 * ```
 *
 * See the compatibility table: https://docs.google.com/spreadsheets/d/e/2PACX-1vQOPU3xUhplll6dyoMmVUXHKl_8CRDs6_ueLmex3SoqwhuolkuN3O05l4rqx5h1dKX8eb46Ul-CCSrq/pubhtml?gid=0&single=true
 *
 * Accepted format string patterns:
 * | Unit                            |Prior| Pattern | Result examples                   | Notes |
 * |---------------------------------|-----|---------|-----------------------------------|-------|
 * | Era                             | 140 | G..GGG  | AD, BC                            |       |
 * |                                 |     | GGGG    | Anno Domini, Before Christ        | 2     |
 * |                                 |     | GGGGG   | A, B                              |       |
 * | Calendar year                   | 130 | y       | 44, 1, 1900, 2017, 9999           | 4     |
 * |                                 |     | yo      | 44th, 1st, 1900th, 9999999th      | 4,5   |
 * |                                 |     | yy      | 44, 01, 00, 17                    | 4     |
 * |                                 |     | yyy     | 044, 001, 123, 999                | 4     |
 * |                                 |     | yyyy    | 0044, 0001, 1900, 2017            | 4     |
 * |                                 |     | yyyyy   | ...                               | 2,4   |
 * | Local week-numbering year       | 130 | Y       | 44, 1, 1900, 2017, 9000           | 4     |
 * |                                 |     | Yo      | 44th, 1st, 1900th, 9999999th      | 4,5   |
 * |                                 |     | YY      | 44, 01, 00, 17                    | 4,6   |
 * |                                 |     | YYY     | 044, 001, 123, 999                | 4     |
 * |                                 |     | YYYY    | 0044, 0001, 1900, 2017            | 4,6   |
 * |                                 |     | YYYYY   | ...                               | 2,4   |
 * | ISO week-numbering year         | 130 | R       | -43, 1, 1900, 2017, 9999, -9999   | 4,5   |
 * |                                 |     | RR      | -43, 01, 00, 17                   | 4,5   |
 * |                                 |     | RRR     | -043, 001, 123, 999, -999         | 4,5   |
 * |                                 |     | RRRR    | -0043, 0001, 2017, 9999, -9999    | 4,5   |
 * |                                 |     | RRRRR   | ...                               | 2,4,5 |
 * | Extended year                   | 130 | u       | -43, 1, 1900, 2017, 9999, -999    | 4     |
 * |                                 |     | uu      | -43, 01, 99, -99                  | 4     |
 * |                                 |     | uuu     | -043, 001, 123, 999, -999         | 4     |
 * |                                 |     | uuuu    | -0043, 0001, 2017, 9999, -9999    | 4     |
 * |                                 |     | uuuuu   | ...                               | 2,4   |
 * | Quarter (formatting)            | 120 | Q       | 1, 2, 3, 4                        |       |
 * |                                 |     | Qo      | 1st, 2nd, 3rd, 4th                | 5     |
 * |                                 |     | QQ      | 01, 02, 03, 04                    |       |
 * |                                 |     | QQQ     | Q1, Q2, Q3, Q4                    |       |
 * |                                 |     | QQQQ    | 1st quarter, 2nd quarter, ...     | 2     |
 * |                                 |     | QQQQQ   | 1, 2, 3, 4                        | 4     |
 * | Quarter (stand-alone)           | 120 | q       | 1, 2, 3, 4                        |       |
 * |                                 |     | qo      | 1st, 2nd, 3rd, 4th                | 5     |
 * |                                 |     | qq      | 01, 02, 03, 04                    |       |
 * |                                 |     | qqq     | Q1, Q2, Q3, Q4                    |       |
 * |                                 |     | qqqq    | 1st quarter, 2nd quarter, ...     | 2     |
 * |                                 |     | qqqqq   | 1, 2, 3, 4                        | 3     |
 * | Month (formatting)              | 110 | M       | 1, 2, ..., 12                     |       |
 * |                                 |     | Mo      | 1st, 2nd, ..., 12th               | 5     |
 * |                                 |     | MM      | 01, 02, ..., 12                   |       |
 * |                                 |     | MMM     | Jan, Feb, ..., Dec                |       |
 * |                                 |     | MMMM    | January, February, ..., December  | 2     |
 * |                                 |     | MMMMM   | J, F, ..., D                      |       |
 * | Month (stand-alone)             | 110 | L       | 1, 2, ..., 12                     |       |
 * |                                 |     | Lo      | 1st, 2nd, ..., 12th               | 5     |
 * |                                 |     | LL      | 01, 02, ..., 12                   |       |
 * |                                 |     | LLL     | Jan, Feb, ..., Dec                |       |
 * |                                 |     | LLLL    | January, February, ..., December  | 2     |
 * |                                 |     | LLLLL   | J, F, ..., D                      |       |
 * | Local week of year              | 100 | w       | 1, 2, ..., 53                     |       |
 * |                                 |     | wo      | 1st, 2nd, ..., 53th               | 5     |
 * |                                 |     | ww      | 01, 02, ..., 53                   |       |
 * | ISO week of year                | 100 | I       | 1, 2, ..., 53                     | 5     |
 * |                                 |     | Io      | 1st, 2nd, ..., 53th               | 5     |
 * |                                 |     | II      | 01, 02, ..., 53                   | 5     |
 * | Day of month                    |  90 | d       | 1, 2, ..., 31                     |       |
 * |                                 |     | do      | 1st, 2nd, ..., 31st               | 5     |
 * |                                 |     | dd      | 01, 02, ..., 31                   |       |
 * | Day of year                     |  90 | D       | 1, 2, ..., 365, 366               | 7     |
 * |                                 |     | Do      | 1st, 2nd, ..., 365th, 366th       | 5     |
 * |                                 |     | DD      | 01, 02, ..., 365, 366             | 7     |
 * |                                 |     | DDD     | 001, 002, ..., 365, 366           |       |
 * |                                 |     | DDDD    | ...                               | 2     |
 * | Day of week (formatting)        |  90 | E..EEE  | Mon, Tue, Wed, ..., Sun           |       |
 * |                                 |     | EEEE    | Monday, Tuesday, ..., Sunday      | 2     |
 * |                                 |     | EEEEE   | M, T, W, T, F, S, S               |       |
 * |                                 |     | EEEEEE  | Mo, Tu, We, Th, Fr, Sa, Su        |       |
 * | ISO day of week (formatting)    |  90 | i       | 1, 2, 3, ..., 7                   | 5     |
 * |                                 |     | io      | 1st, 2nd, ..., 7th                | 5     |
 * |                                 |     | ii      | 01, 02, ..., 07                   | 5     |
 * |                                 |     | iii     | Mon, Tue, Wed, ..., Sun           | 5     |
 * |                                 |     | iiii    | Monday, Tuesday, ..., Sunday      | 2,5   |
 * |                                 |     | iiiii   | M, T, W, T, F, S, S               | 5     |
 * |                                 |     | iiiiii  | Mo, Tu, We, Th, Fr, Sa, Su        | 5     |
 * | Local day of week (formatting)  |  90 | e       | 2, 3, 4, ..., 1                   |       |
 * |                                 |     | eo      | 2nd, 3rd, ..., 1st                | 5     |
 * |                                 |     | ee      | 02, 03, ..., 01                   |       |
 * |                                 |     | eee     | Mon, Tue, Wed, ..., Sun           |       |
 * |                                 |     | eeee    | Monday, Tuesday, ..., Sunday      | 2     |
 * |                                 |     | eeeee   | M, T, W, T, F, S, S               |       |
 * |                                 |     | eeeeee  | Mo, Tu, We, Th, Fr, Sa, Su        |       |
 * | Local day of week (stand-alone) |  90 | c       | 2, 3, 4, ..., 1                   |       |
 * |                                 |     | co      | 2nd, 3rd, ..., 1st                | 5     |
 * |                                 |     | cc      | 02, 03, ..., 01                   |       |
 * |                                 |     | ccc     | Mon, Tue, Wed, ..., Sun           |       |
 * |                                 |     | cccc    | Monday, Tuesday, ..., Sunday      | 2     |
 * |                                 |     | ccccc   | M, T, W, T, F, S, S               |       |
 * |                                 |     | cccccc  | Mo, Tu, We, Th, Fr, Sa, Su        |       |
 * | AM, PM                          |  80 | a..aaa  | AM, PM                            |       |
 * |                                 |     | aaaa    | a.m., p.m.                        | 2     |
 * |                                 |     | aaaaa   | a, p                              |       |
 * | AM, PM, noon, midnight          |  80 | b..bbb  | AM, PM, noon, midnight            |       |
 * |                                 |     | bbbb    | a.m., p.m., noon, midnight        | 2     |
 * |                                 |     | bbbbb   | a, p, n, mi                       |       |
 * | Flexible day period             |  80 | B..BBB  | at night, in the morning, ...     |       |
 * |                                 |     | BBBB    | at night, in the morning, ...     | 2     |
 * |                                 |     | BBBBB   | at night, in the morning, ...     |       |
 * | Hour [1-12]                     |  70 | h       | 1, 2, ..., 11, 12                 |       |
 * |                                 |     | ho      | 1st, 2nd, ..., 11th, 12th         | 5     |
 * |                                 |     | hh      | 01, 02, ..., 11, 12               |       |
 * | Hour [0-23]                     |  70 | H       | 0, 1, 2, ..., 23                  |       |
 * |                                 |     | Ho      | 0th, 1st, 2nd, ..., 23rd          | 5     |
 * |                                 |     | HH      | 00, 01, 02, ..., 23               |       |
 * | Hour [0-11]                     |  70 | K       | 1, 2, ..., 11, 0                  |       |
 * |                                 |     | Ko      | 1st, 2nd, ..., 11th, 0th          | 5     |
 * |                                 |     | KK      | 01, 02, ..., 11, 00               |       |
 * | Hour [1-24]                     |  70 | k       | 24, 1, 2, ..., 23                 |       |
 * |                                 |     | ko      | 24th, 1st, 2nd, ..., 23rd         | 5     |
 * |                                 |     | kk      | 24, 01, 02, ..., 23               |       |
 * | Minute                          |  60 | m       | 0, 1, ..., 59                     |       |
 * |                                 |     | mo      | 0th, 1st, ..., 59th               | 5     |
 * |                                 |     | mm      | 00, 01, ..., 59                   |       |
 * | Second                          |  50 | s       | 0, 1, ..., 59                     |       |
 * |                                 |     | so      | 0th, 1st, ..., 59th               | 5     |
 * |                                 |     | ss      | 00, 01, ..., 59                   |       |
 * | Seconds timestamp               |  40 | t       | 512969520                         |       |
 * |                                 |     | tt      | ...                               | 2     |
 * | Fraction of second              |  30 | S       | 0, 1, ..., 9                      |       |
 * |                                 |     | SS      | 00, 01, ..., 99                   |       |
 * |                                 |     | SSS     | 000, 001, ..., 999                |       |
 * |                                 |     | SSSS    | ...                               | 2     |
 * | Milliseconds timestamp          |  20 | T       | 512969520900                      |       |
 * |                                 |     | TT      | ...                               | 2     |
 * | Timezone (ISO-8601 w/ Z)        |  10 | X       | -08, +0530, Z                     |       |
 * |                                 |     | XX      | -0800, +0530, Z                   |       |
 * |                                 |     | XXX     | -08:00, +05:30, Z                 |       |
 * |                                 |     | XXXX    | -0800, +0530, Z, +123456          | 2     |
 * |                                 |     | XXXXX   | -08:00, +05:30, Z, +12:34:56      |       |
 * | Timezone (ISO-8601 w/o Z)       |  10 | x       | -08, +0530, +00                   |       |
 * |                                 |     | xx      | -0800, +0530, +0000               |       |
 * |                                 |     | xxx     | -08:00, +05:30, +00:00            | 2     |
 * |                                 |     | xxxx    | -0800, +0530, +0000, +123456      |       |
 * |                                 |     | xxxxx   | -08:00, +05:30, +00:00, +12:34:56 |       |
 * | Long localized date             |  NA | P       | 05/29/1453                        | 5,8   |
 * |                                 |     | PP      | May 29, 1453                      |       |
 * |                                 |     | PPP     | May 29th, 1453                    |       |
 * |                                 |     | PPPP    | Sunday, May 29th, 1453            | 2,5,8 |
 * | Long localized time             |  NA | p       | 12:00 AM                          | 5,8   |
 * |                                 |     | pp      | 12:00:00 AM                       |       |
 * | Combination of date and time    |  NA | Pp      | 05/29/1453, 12:00 AM              |       |
 * |                                 |     | PPpp    | May 29, 1453, 12:00:00 AM         |       |
 * |                                 |     | PPPpp   | May 29th, 1453 at ...             |       |
 * |                                 |     | PPPPpp  | Sunday, May 29th, 1453 at ...     | 2,5,8 |
 * Notes:
 * 1. "Formatting" units (e.g. formatting quarter) in the default en-US locale
 *    are the same as "stand-alone" units, but are different in some languages.
 *    "Formatting" units are declined according to the rules of the language
 *    in the context of a date. "Stand-alone" units are always nominative singular.
 *    In `format` function, they will produce different result:
 *
 *    `format(new Date(2017, 10, 6), 'do LLLL', {locale: cs}) //=> '6. listopad'`
 *
 *    `format(new Date(2017, 10, 6), 'do MMMM', {locale: cs}) //=> '6. listopadu'`
 *
 *    `parse` will try to match both formatting and stand-alone units interchangably.
 *
 * 2. Any sequence of the identical letters is a pattern, unless it is escaped by
 *    the single quote characters (see below).
 *    If the sequence is longer than listed in table:
 *    - for numerical units (`yyyyyyyy`) `parse` will try to match a number
 *      as wide as the sequence
 *    - for text units (`MMMMMMMM`) `parse` will try to match the widest variation of the unit.
 *      These variations are marked with "2" in the last column of the table.
 *
 * 3. `QQQQQ` and `qqqqq` could be not strictly numerical in some locales.
 *    These tokens represent the shortest form of the quarter.
 *
 * 4. The main difference between `y` and `u` patterns are B.C. years:
 *
 *    | Year | `y` | `u` |
 *    |------|-----|-----|
 *    | AC 1 |   1 |   1 |
 *    | BC 1 |   1 |   0 |
 *    | BC 2 |   2 |  -1 |
 *
 *    Also `yy` will try to guess the century of two digit year by proximity with `referenceDate`:
 *
 *    `parse('50', 'yy', new Date(2018, 0, 1)) //=> Sat Jan 01 2050 00:00:00`
 *
 *    `parse('75', 'yy', new Date(2018, 0, 1)) //=> Wed Jan 01 1975 00:00:00`
 *
 *    while `uu` will just assign the year as is:
 *
 *    `parse('50', 'uu', new Date(2018, 0, 1)) //=> Sat Jan 01 0050 00:00:00`
 *
 *    `parse('75', 'uu', new Date(2018, 0, 1)) //=> Tue Jan 01 0075 00:00:00`
 *
 *    The same difference is true for local and ISO week-numbering years (`Y` and `R`),
 *    except local week-numbering years are dependent on `options.weekStartsOn`
 *    and `options.firstWeekContainsDate` (compare [setISOWeekYear]{@link https://date-fns.org/docs/setISOWeekYear}
 *    and [setWeekYear]{@link https://date-fns.org/docs/setWeekYear}).
 *
 * 5. These patterns are not in the Unicode Technical Standard #35:
 *    - `i`: ISO day of week
 *    - `I`: ISO week of year
 *    - `R`: ISO week-numbering year
 *    - `o`: ordinal number modifier
 *    - `P`: long localized date
 *    - `p`: long localized time
 *
 * 6. `YY` and `YYYY` tokens represent week-numbering years but they are often confused with years.
 *    You should enable `options.useAdditionalWeekYearTokens` to use them. See: https://git.io/fxCyr
 *
 * 7. `D` and `DD` tokens represent days of the year but they are ofthen confused with days of the month.
 *    You should enable `options.useAdditionalDayOfYearTokens` to use them. See: https://git.io/fxCyr
 *
 * 8. `P+` tokens do not have a defined priority since they are merely aliases to other tokens based
 *    on the given locale.
 *
 *    using `en-US` locale: `P` => `MM/dd/yyyy`
 *    using `en-US` locale: `p` => `hh:mm a`
 *    using `pt-BR` locale: `P` => `dd/MM/yyyy`
 *    using `pt-BR` locale: `p` => `HH:mm`
 *
 * Values will be assigned to the date in the descending order of its unit's priority.
 * Units of an equal priority overwrite each other in the order of appearance.
 *
 * If no values of higher priority are parsed (e.g. when parsing string 'January 1st' without a year),
 * the values will be taken from 3rd argument `referenceDate` which works as a context of parsing.
 *
 * `referenceDate` must be passed for correct work of the function.
 * If you're not sure which `referenceDate` to supply, create a new instance of Date:
 * `parse('02/11/2014', 'MM/dd/yyyy', new Date())`
 * In this case parsing will be done in the context of the current date.
 * If `referenceDate` is `Invalid Date` or a value not convertible to valid `Date`,
 * then `Invalid Date` will be returned.
 *
 * The result may vary by locale.
 *
 * If `formatString` matches with `dateString` but does not provides tokens, `referenceDate` will be returned.
 *
 * If parsing failed, `Invalid Date` will be returned.
 * Invalid Date is a Date, whose time value is NaN.
 * Time value of Date: http://es5.github.io/#x15.9.1.1
 *
 * ### v2.0.0 breaking changes:
 *
 * - [Changes that are common for the whole library](https://github.com/date-fns/date-fns/blob/master/docs/upgradeGuide.md#Common-Changes).
 *
 * - Old `parse` was renamed to `toDate`.
 *   Now `parse` is a new function which parses a string using a provided format.
 *
 *   ```javascript
 *   // Before v2.0.0
 *   parse('2016-01-01')
 *
 *   // v2.0.0 onward (toDate no longer accepts a string)
 *   toDate(1392098430000) // Unix to timestamp
 *   toDate(new Date(2014, 1, 11, 11, 30, 30)) // Cloning the date
 *   parse('2016-01-01', 'yyyy-MM-dd', new Date())
 *   ```
 *
 * @param {String} dateString - the string to parse
 * @param {String} formatString - the string of tokens
 * @param {Date|Number} referenceDate - defines values missing from the parsed dateString
 * @param {Object} [options] - an object with options.
 * @param {Locale} [options.locale=defaultLocale] - the locale object. See [Locale]{@link https://date-fns.org/docs/Locale}
 * @param {0|1|2|3|4|5|6} [options.weekStartsOn=0] - the index of the first day of the week (0 - Sunday)
 * @param {1|2|3|4|5|6|7} [options.firstWeekContainsDate=1] - the day of January, which is always in the first week of the year
 * @param {Boolean} [options.useAdditionalWeekYearTokens=false] - if true, allows usage of the week-numbering year tokens `YY` and `YYYY`;
 *   see: https://git.io/fxCyr
 * @param {Boolean} [options.useAdditionalDayOfYearTokens=false] - if true, allows usage of the day of year tokens `D` and `DD`;
 *   see: https://git.io/fxCyr
 * @returns {Date} the parsed date
 * @throws {TypeError} 3 arguments required
 * @throws {RangeError} `options.weekStartsOn` must be between 0 and 6
 * @throws {RangeError} `options.firstWeekContainsDate` must be between 1 and 7
 * @throws {RangeError} `options.locale` must contain `match` property
 * @throws {RangeError} use `yyyy` instead of `YYYY` for formatting years using [format provided] to the input [input provided]; see: https://git.io/fxCyr
 * @throws {RangeError} use `yy` instead of `YY` for formatting years using [format provided] to the input [input provided]; see: https://git.io/fxCyr
 * @throws {RangeError} use `d` instead of `D` for formatting days of the month using [format provided] to the input [input provided]; see: https://git.io/fxCyr
 * @throws {RangeError} use `dd` instead of `DD` for formatting days of the month using [format provided] to the input [input provided]; see: https://git.io/fxCyr
 * @throws {RangeError} format string contains an unescaped latin alphabet character
 *
 * @example
 * // Parse 11 February 2014 from middle-endian format:
 * var result = parse('02/11/2014', 'MM/dd/yyyy', new Date())
 * //=> Tue Feb 11 2014 00:00:00
 *
 * @example
 * // Parse 28th of February in Esperanto locale in the context of 2010 year:
 * import eo from 'date-fns/locale/eo'
 * var result = parse('28-a de februaro', "do 'de' MMMM", new Date(2010, 0, 1), {
 *   locale: eo
 * })
 * //=> Sun Feb 28 2010 00:00:00
 */

function parse(dirtyDateString, dirtyFormatString, dirtyReferenceDate, dirtyOptions) {
  requiredArgs(3, arguments);
  var dateString = String(dirtyDateString);
  var formatString = String(dirtyFormatString);
  var options = dirtyOptions || {};
  var locale = options.locale || defaultLocale;

  if (!locale.match) {
    throw new RangeError('locale must contain match property');
  }

  var localeFirstWeekContainsDate = locale.options && locale.options.firstWeekContainsDate;
  var defaultFirstWeekContainsDate = localeFirstWeekContainsDate == null ? 1 : toInteger(localeFirstWeekContainsDate);
  var firstWeekContainsDate = options.firstWeekContainsDate == null ? defaultFirstWeekContainsDate : toInteger(options.firstWeekContainsDate); // Test if weekStartsOn is between 1 and 7 _and_ is not NaN

  if (!(firstWeekContainsDate >= 1 && firstWeekContainsDate <= 7)) {
    throw new RangeError('firstWeekContainsDate must be between 1 and 7 inclusively');
  }

  var localeWeekStartsOn = locale.options && locale.options.weekStartsOn;
  var defaultWeekStartsOn = localeWeekStartsOn == null ? 0 : toInteger(localeWeekStartsOn);
  var weekStartsOn = options.weekStartsOn == null ? defaultWeekStartsOn : toInteger(options.weekStartsOn); // Test if weekStartsOn is between 0 and 6 _and_ is not NaN

  if (!(weekStartsOn >= 0 && weekStartsOn <= 6)) {
    throw new RangeError('weekStartsOn must be between 0 and 6 inclusively');
  }

  if (formatString === '') {
    if (dateString === '') {
      return toDate(dirtyReferenceDate);
    } else {
      return new Date(NaN);
    }
  }

  var subFnOptions = {
    firstWeekContainsDate: firstWeekContainsDate,
    weekStartsOn: weekStartsOn,
    locale: locale
  }; // If timezone isn't specified, it will be set to the system timezone

  var setters = [{
    priority: TIMEZONE_UNIT_PRIORITY,
    subPriority: -1,
    set: dateToSystemTimezone,
    index: 0
  }];
  var i;
  var tokens = formatString.match(longFormattingTokensRegExp).map(function (substring) {
    var firstCharacter = substring[0];

    if (firstCharacter === 'p' || firstCharacter === 'P') {
      var longFormatter = longFormatters$1[firstCharacter];
      return longFormatter(substring, locale.formatLong, subFnOptions);
    }

    return substring;
  }).join('').match(formattingTokensRegExp);
  var usedTokens = [];

  for (i = 0; i < tokens.length; i++) {
    var token = tokens[i];

    if (!options.useAdditionalWeekYearTokens && isProtectedWeekYearToken(token)) {
      throwProtectedError(token, formatString, dirtyDateString);
    }

    if (!options.useAdditionalDayOfYearTokens && isProtectedDayOfYearToken(token)) {
      throwProtectedError(token, formatString, dirtyDateString);
    }

    var firstCharacter = token[0];
    var parser = parsers$1[firstCharacter];

    if (parser) {
      var incompatibleTokens = parser.incompatibleTokens;

      if (Array.isArray(incompatibleTokens)) {
        var incompatibleToken = void 0;

        for (var _i = 0; _i < usedTokens.length; _i++) {
          var usedToken = usedTokens[_i].token;

          if (incompatibleTokens.indexOf(usedToken) !== -1 || usedToken === firstCharacter) {
            incompatibleToken = usedTokens[_i];
            break;
          }
        }

        if (incompatibleToken) {
          throw new RangeError("The format string mustn't contain `".concat(incompatibleToken.fullToken, "` and `").concat(token, "` at the same time"));
        }
      } else if (parser.incompatibleTokens === '*' && usedTokens.length) {
        throw new RangeError("The format string mustn't contain `".concat(token, "` and any other token at the same time"));
      }

      usedTokens.push({
        token: firstCharacter,
        fullToken: token
      });
      var parseResult = parser.parse(dateString, token, locale.match, subFnOptions);

      if (!parseResult) {
        return new Date(NaN);
      }

      setters.push({
        priority: parser.priority,
        subPriority: parser.subPriority || 0,
        set: parser.set,
        validate: parser.validate,
        value: parseResult.value,
        index: setters.length
      });
      dateString = parseResult.rest;
    } else {
      if (firstCharacter.match(unescapedLatinCharacterRegExp)) {
        throw new RangeError('Format string contains an unescaped latin alphabet character `' + firstCharacter + '`');
      } // Replace two single quote characters with one single quote character


      if (token === "''") {
        token = "'";
      } else if (firstCharacter === "'") {
        token = cleanEscapedString(token);
      } // Cut token from string, or, if string doesn't match the token, return Invalid Date


      if (dateString.indexOf(token) === 0) {
        dateString = dateString.slice(token.length);
      } else {
        return new Date(NaN);
      }
    }
  } // Check if the remaining input contains something other than whitespace


  if (dateString.length > 0 && notWhitespaceRegExp.test(dateString)) {
    return new Date(NaN);
  }

  var uniquePrioritySetters = setters.map(function (setter) {
    return setter.priority;
  }).sort(function (a, b) {
    return b - a;
  }).filter(function (priority, index, array) {
    return array.indexOf(priority) === index;
  }).map(function (priority) {
    return setters.filter(function (setter) {
      return setter.priority === priority;
    }).sort(function (a, b) {
      return b.subPriority - a.subPriority;
    });
  }).map(function (setterArray) {
    return setterArray[0];
  });
  var date = toDate(dirtyReferenceDate);

  if (isNaN(date)) {
    return new Date(NaN);
  } // Convert the date in system timezone to the same date in UTC+00:00 timezone.
  // This ensures that when UTC functions will be implemented, locales will be compatible with them.
  // See an issue about UTC functions: https://github.com/date-fns/date-fns/issues/37


  var utcDate = subMilliseconds(date, getTimezoneOffsetInMilliseconds(date));
  var flags = {};

  for (i = 0; i < uniquePrioritySetters.length; i++) {
    var setter = uniquePrioritySetters[i];

    if (setter.validate && !setter.validate(utcDate, setter.value, subFnOptions)) {
      return new Date(NaN);
    }

    var result = setter.set(utcDate, flags, setter.value, subFnOptions); // Result is tuple (date, flags)

    if (result[0]) {
      utcDate = result[0];
      assign(flags, result[1]); // Result is date
    } else {
      utcDate = result;
    }
  }

  return utcDate;
}

function dateToSystemTimezone(date, flags) {
  if (flags.timestampIsSet) {
    return date;
  }

  var convertedDate = new Date(0);
  convertedDate.setFullYear(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
  convertedDate.setHours(date.getUTCHours(), date.getUTCMinutes(), date.getUTCSeconds(), date.getUTCMilliseconds());
  return convertedDate;
}

function cleanEscapedString(input) {
  return input.match(escapedStringRegExp)[1].replace(doubleQuoteRegExp, "'");
}

/**
 * @name addDays
 * @category Day Helpers
 * @summary Add the specified number of days to the given date.
 *
 * @description
 * Add the specified number of days to the given date.
 *
 * ### v2.0.0 breaking changes:
 *
 * - [Changes that are common for the whole library](https://github.com/date-fns/date-fns/blob/master/docs/upgradeGuide.md#Common-Changes).
 *
 * @param {Date|Number} date - the date to be changed
 * @param {Number} amount - the amount of days to be added. Positive decimals will be rounded using `Math.floor`, decimals less than zero will be rounded using `Math.ceil`.
 * @returns {Date} - the new date with the days added
 * @throws {TypeError} - 2 arguments required
 *
 * @example
 * // Add 10 days to 1 September 2014:
 * const result = addDays(new Date(2014, 8, 1), 10)
 * //=> Thu Sep 11 2014 00:00:00
 */

function addDays(dirtyDate, dirtyAmount) {
  requiredArgs(2, arguments);
  var date = toDate(dirtyDate);
  var amount = toInteger(dirtyAmount);

  if (isNaN(amount)) {
    return new Date(NaN);
  }

  if (!amount) {
    // If 0 days, no-op to avoid changing times in the hour before end of DST
    return date;
  }

  date.setDate(date.getDate() + amount);
  return date;
}

/**
 * @name differenceInMilliseconds
 * @category Millisecond Helpers
 * @summary Get the number of milliseconds between the given dates.
 *
 * @description
 * Get the number of milliseconds between the given dates.
 *
 * ### v2.0.0 breaking changes:
 *
 * - [Changes that are common for the whole library](https://github.com/date-fns/date-fns/blob/master/docs/upgradeGuide.md#Common-Changes).
 *
 * @param {Date|Number} dateLeft - the later date
 * @param {Date|Number} dateRight - the earlier date
 * @returns {Number} the number of milliseconds
 * @throws {TypeError} 2 arguments required
 *
 * @example
 * // How many milliseconds are between
 * // 2 July 2014 12:30:20.600 and 2 July 2014 12:30:21.700?
 * const result = differenceInMilliseconds(
 *   new Date(2014, 6, 2, 12, 30, 21, 700),
 *   new Date(2014, 6, 2, 12, 30, 20, 600)
 * )
 * //=> 1100
 */

function differenceInMilliseconds(dateLeft, dateRight) {
  requiredArgs(2, arguments);
  return toDate(dateLeft).getTime() - toDate(dateRight).getTime();
}

/**
 * Regex to match a time value in the format of `HH:mm` (e.g. 14:30, 2:0).
 */
const time$1 = /^\d{1,2}:\d{1,2}$/;
/**
 * Regex to match each component of a time value.
 *
 * @example
 * // returns ['14', '30']
 * const [hours, minutes] = '14:30'.match(regex.timeComponents);
 */

const timeComponents = /\w{1,2}/g;

// TODO: make sure to add tests that handle dates before 1900 to ensure we use `getAccurateDate`

/**
 * Get an "accurate" date given a `year`, `month` and optionally a `day`. The built in Date
 * constructor for Javascript would convert `new Date(1, 0, 1)` to be 1/1/1900. In order to more
 * accurately work with Dates prior to 1900, we need to preformat the arguments going into the Date
 * constructor to generate dates prior to 1900.
 *
 * **NOTE 1**: This function will not work for dates from before the year 1. Currently the Calendar
 * component does not support these dates.
 *
 * **NOTE 2**: Despite generating dates closer to what you may expect, there are still problems
 * associated with this. Dates prior to the year 1900 are not reliable. e.g. in the year 1582,
 * October 5th - 14th were skipped. Javascript will not recognize this.
 *
 * @param {number} year the year to create a new date with
 * @param {number} month the month to create a new date with
 * @param {number} [day=1] the day to create a new date with
 * @param {number} [hours=0] the hours to create a new date with
 * @param {number} [minutes=0] the minutes to create a new date with
 * @returns {Date} a new date created with `year`, `month` and `day`
 */

const getAccurateDate = (year, month, day = 1, hours = 0, minutes = 0) => {
  const accurateDate = new Date('0000', month, day, hours, minutes);
  accurateDate.setFullYear(year);
  return accurateDate;
};
/**
 * Given a date, create a new date based off of the given date, but with the given time. If no time
 * is supplied, the time will be set to 00:00 (12:00 AM)
 *
 * 12/01/2022, 4:31PM => 12/01/2022, 12:00AM.
 *
 * @param {Date | null} date Date with a time value that should be sanitized
 * @returns {Date | null} Date without a custom time value
 */

const getDateWithTime = (date, time = '00:00') => {
  const [hours = '', minutes = ''] = time.match(timeComponents) || [];
  return date && date.getTime() ? getAccurateDate(date.getFullYear(), date.getMonth(), date.getDate(), hours, minutes) : null;
};
/**
 * Based on a set of criteria, pick a date to use as the default date in the Calendar component.
 *
 * If the calendar is supplied with a default date (`selectedDate`, `startDate` or `endDate`), we
 * need to pick a default date using this date to make sure that the selected date is visible in the
 * calendar when the component loads. If the user selects a date in the `TimeSelect` before choosing
 * a date, we need a default date to add the time to and set that as the `selectedDate`
 * (or `startDate`/`endDate`).
 *
 * **Date Priority**
 * 1. `selectedDate`
 * 2. `startDate`
 * 3. `endDate`
 * 4. today
 * 5. `minDate` || `maxDate` - depending on which date is closer to today
 *
 * @param {Date | null} currentDate The day selected in the calendar component: `selectedDay`,
 * `startDate` or `endDate`
 * @param {Date} minDate The maximum valid date
 * @param {Date} maxDate The minimum valid date
 * @returns {Date} A date that should be used as the default date in a calendar
 */

const pickDefaultDate = (currentDate, minDate, maxDate) => {
  const today = new Date();
  let dateToUse = currentDate && currentDate.getTime() ? currentDate : today; // if `dateToUse` is outside of the min/max range, pick the end of the range (either `minDate` or
  // `maxDate`) that is closest to today

  if (dateToUse < minDate || dateToUse > maxDate) {
    dateToUse = differenceInMilliseconds(today, minDate) < differenceInMilliseconds(maxDate, today) ? minDate : maxDate;
  }

  return dateToUse;
};
/**
 * Given a Javascript date object, determine whether or not the date object is within a valid range.
 * If ut is, return true, if not, return false. `null` is considered valid.
 *
 * @param {Date} date A Javascript date object
 * @param {Date} minDate A Javascript date object signifying the start of the range
 * @param {Date} maxDate A Javascript date object signifying the end of the range
 * @returns {boolean} A flag that indicates whether or not the date is within range.
 */

const isDateWithinRange = (date, minDate, maxDate) => {
  // null is ok
  if (!date) return true;
  const sanitizedTimeStamp = date >= minDate && date <= maxDate ? date?.getTime() : null;
  return date.getTime() === sanitizedTimeStamp;
};

// TODO: css on focused day when focused day is over selected date
// TODO: broken on showCalendarOnly
// TODO: go over jsdocs and make sure that its correct

/**
 * manually handle tabbing through the calendar for accessibility... // ! /////
 *
 * @param {e: Event} e // ! ..e.
 * @param {{
 *   calendar: { current: HTMLElement | null },
 *   days: Object<string, { current: HTMLElement | null }>,
 *   hoverDayRange: { current: [{ current: HTMLElement | null }, { current: HTMLElement | null }] },
 *   menu: [{ current: HTMLElement | null }, { current: HTMLElement | null }],
 *   timeIntervals: Object<string, { current: HTMLElement | null }>,
 * }} refs An object containing React refs to a number of different parts of the Calendar component
 * @param {{ shiftTab: (e: Event) => void, tab: (e: Event) => void }} callback An object containing
 * a callback function for handling shift + tabbing and tabbing
 * @returns {void}
 */
// TODO: pass refs here too? --- seems like every func uses it...

const handleKeyDown = (e, refs, callback) => {
  // select item being focused on
  if (e.key === 'Enter' || e.key === 'Space') {
    if (callback.enter) callback.enter(e, refs); // shift + tab backwards
  } else if (e.shiftKey && e.key === 'Tab') {
    if (callback.shiftTab) callback.shiftTab(e, refs); // tab forward
  } else if (e.key === 'Tab') {
    if (callback.tab) callback.tab(e, refs); // shift focus up, down, left or right according to the key pressed
  } else if (e.key === 'ArrowUp') {
    if (callback.arrowUp) callback.arrowUp(e, refs);
  } else if (e.key === 'ArrowDown') {
    if (callback.arrowDown) callback.arrowDown(e, refs);
  } else if (e.key === 'ArrowLeft') {
    if (callback.arrowLeft) callback.arrowLeft(e, refs);
  } else if (e.key === 'ArrowRight') {
    if (callback.arrowRight) callback.arrowRight(e, refs);
  }
};
const navigate = {
  /**
   * Given a current date and the number of days of difference between the current day and the next
   * date, shift focus to the next date, if the next date is within range.
   *
   * @param {Date} currentDate The day selected in the calendar component: `selectedDay`,
   * `startDate` or `endDate`
   * @param {number} nextDateDiff The number of days to add to the current day to shift focus to the
   * next day
   * @returns {void}
   */
  dates(currentDate, nextDateDiff) {
    return function callback(e, refs) {
      const nextDate = addDays(currentDate, nextDateDiff); // TODO: prevent default??

      if (refs.days[nextDate] && refs.days[nextDate].current) {
        refs.days[nextDate].current.focus();
      }
    };
  }

};
const pivotFocus = {
  /**
   * For accessability, we want to be able to shift the user's focus to the dates in the calendar
   * when the user hits the `Tab` key. If we have any selected dates, shift the user's focus to the
   * selected date, and if not shift it to a default calendar date.
   *
   * @param {Date} currentDate The day selected in the calendar component: `selectedDay`,
   * `startDate` or `endDate`
   * @param {Date} minDate The maximum valid date
   * @param {Date} maxDate The minimum valid date
   * @returns {void}
   */
  calendarDate(currentDate, minDate, maxDate) {
    return (e, refs) => {
      const defaultDate = pickDefaultDate(currentDate, minDate, maxDate);
      const dateToFocusOn = getDateWithTime(currentDate || defaultDate); // since we manually shift focus, prevent default behavior of original tab

      e.preventDefault();

      if (refs.days[dateToFocusOn] && refs.days[dateToFocusOn].current) {
        refs.days[dateToFocusOn].current.focus();
      }
    };
  },

  /**
   * TODO: this
   *
   * @param {Date} currentDate The day selected in the calendar component: `selectedDay`,
   * `startDate` or `endDate`
   * @param {Date} minDate The maximum valid date
   * @param {Date} maxDate The minimum valid date
   * @returns {*} // ! ...
   */
  // ! shift only?
  menuOrCalendarDate(currentDate, minDate, maxDate) {
    return (e, refs) => {
      // TODO: prevent default here?
      // if we have a ref to the previous month button, go there
      if (refs.menu[0] && refs.menu[0].current) {
        refs.menu[0].current.focus(); // if we have a ref to the next month button, go there
      } else if (refs.menu[1] && refs.menu[1].current) {
        refs.menu[1].current.focus(); // if prev and next month buttons are both not available, go straight to the calendar
      } else {
        pivotFocus.calendarDate(refs.days, currentDate, minDate, maxDate);
      }
    };
  },

  /**
   * TODO: this
   *
   * @returns {*} // ! ...
   */
  // ! shift tab only??
  menuOrInput() {
    return (e, refs) => {
      // TODO: prevent default?
      if (refs.menu[1] && refs.menu[1].current) {
        refs.menu[1].current.focus();
      } else if (refs.menu[0] && refs.menu[0].current) {
        refs.menu[0].current.focus();
      } else if (refs.input && refs.input.current) {
        refs.input.current.focus();
      } // else ; // ! how to exit calendar???

    };
  }

};

const MONTHS = {
  LOWERCASE: ['january', 'february', 'march', 'april', 'may', 'june', 'july', 'august', 'september', 'october', 'november', 'december'],
  STARTCASE: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
};
const WEEKDAYS = {
  LOWERCASE: ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'],
  STARTCASE: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
};
const NUMBER_OF_MINUTES_IN_A_DAY = 1440;
const DEFAULT_DATE_FORMAT = 'P';
const DEFAULT_MAX_DATE = new Date(9999, 11, 31); // 12/31/9999

const DEFAULT_MAX_TIME = '23:59'; // 11:59 AM

const DEFAULT_MIN_DATE = new Date(0, 0, 1); // 01/01/1900

const DEFAULT_MIN_TIME = '00:00'; // 12:00 AM

/* eslint-disable import/prefer-default-export */

const time = (props, propName, componentName) => {
  const [hours = '', minutes = ''] = props[propName] && props[propName].match(timeComponents) || [];
  const isTimeValid = Number(hours) > -1 && Number(hours) < 24 && Number(minutes) > -1 && Number(minutes) < 61;

  if (props[propName] && (!time$1.test(props[propName]) || !isTimeValid)) {
    return new Error("Invalid prop " + propName + " passed to " + componentName + ". Expected a valid time value, instead \"" + props[propName] + "\" was passed. Please use `HH:mm` time format (e.g. '14:30').");
  }

  return null;
};

const START_DATE = 'startDate';
const START_TIME = 'startTime';
const END_DATE = 'endDate';
const END_TIME = 'endTime';
const SELECTED_DATE = 'selectedDate';
const SELECTED_TIME = 'selectedTime';
/**
 * Given state setting functions and state, set state while ensuring the correct order of startDate
 * and endDate.
 *
 * @param {(startDate: Date) => void} setStartDate A state setting function for startDate
 * @param {(endDate: Date) => void} setEndDate A state setting function for endDate
 * @returns {(startDate: Date, endDate: Date) => void}
 */

const handleSettingDateState = (setStartDate, setEndDate) => (startDate, endDate) => {
  // at least one date is null, set both
  if (!startDate || !endDate) {
    ReactDOM.unstable_batchedUpdates(() => {
      setStartDate(startDate);
      setEndDate(endDate);
    }); // both dates are present, but out of order
  } else if (startDate > endDate) {
    ReactDOM.unstable_batchedUpdates(() => {
      setStartDate(endDate);
      setEndDate(startDate);
    }); // both dates are present and in order
  } else {
    ReactDOM.unstable_batchedUpdates(() => {
      setStartDate(startDate);
      setEndDate(endDate);
    });
  }
};
/**
 * Given `date` and `time`, return a new Javascript date object with `time` added to it. If no time
 * is provided, set the time to 12:00 AM.
 *
 * @param {Date} date A Javascript date object
 * @param {string} time A 24 hour time formatted hh:mm
 * @returns {Date} A Javascript date object with `time` value merged in
 */


const mergeDateAndTime = (date, time = '00:00') => {
  if (!date) return null;
  const [hours = 0, minutes = 0] = time.match(timeComponents) || [];
  return new Date(date.getFullYear(), date.getMonth(), date.getDate(), hours, minutes);
};
/**
 * Based on newState, set the calendar's date state accordingly.
 *
 * @param {(startDate: Date) => void} setStartDate A state setting function for startDate
 * @param {(endDate: Date) => void} setEndDate A state setting function for endDate
 * @returns {(startDate: Date, endDate: Date) => void}
 */

const handleCalendarDateSelect = (setStartDate, setEndDate) => (currentStartDate, currentEndDate) => newState => {
  const hasNewStartDate = (START_DATE in newState);
  const hasNewEndDate = (END_DATE in newState);
  const setState = handleSettingDateState(setStartDate, setEndDate);

  if (hasNewStartDate && hasNewEndDate) {
    setState(newState[START_DATE], newState[END_DATE]);
  } else if (hasNewStartDate) {
    if (!currentEndDate) setState(newState[START_DATE], currentEndDate);else setState(newState[START_DATE], null);
  } else if (hasNewEndDate) {
    setState(currentStartDate, newState[END_DATE]);
  }
};
/**
 * Based on newState, set the calendar's time state accordingly.
 *
 * @param {(startTime: Date) => void} setStartTime A state setting function for startTime
 * @param {(endTime: Date) => void} setEndTime A state setting function for endTime
 * @returns {(newState: { startDate: Date, endDate: Date }) => void}
 */

const handleCalendarTimeSelect = (setStartTime, setEndTime) => newState => {
  const hasNewStartTime = (START_TIME in newState);
  const hasNewEndTime = (END_TIME in newState);
  if (hasNewStartTime) setStartTime(newState[START_TIME]);
  if (hasNewEndTime) setEndTime(newState[END_TIME]);
};

// TODO: validate incorrect keys in newState

/**
 * A React hook for Screentone's `Calendar` component to handle individual dates. This hook manages
 * the state for `selectedDate`, the date that is currently selected by the `Calendar` component and
 * `onSelect` a function for updating the state of `selectedDate`. Both of these values should be
 * passed to the `Calendar` component as props of the same name.
 *
 * @param {Date | null} [defaultSelectedDate=null] - default value for the calendar's `selectedDate`
 * @returns {{
 *   onSelect: (newDate: Date) => void,
 *   selectedDate: Date | null,
 * }} object containing props to be passed to Screentone's `Calendar` component
 */

const useCalendar = (defaultSelectedDate = null) => {
  let sanitizedDefault = defaultSelectedDate;

  if (sanitizedDefault && !(sanitizedDefault instanceof Date)) {
    /* eslint-disable-next-line no-console */
    console.warn("useCalendar can only accept parameters of type Date as default values for selectedDate. A value of type " + typeof defaultSelectedDate + " was supplied.");
    sanitizedDefault = null;
  }

  const [selectedDate, setSelectedDate] = useState(sanitizedDefault || null);

  const onCalendarSelect = newState => {
    if (SELECTED_DATE in newState) setSelectedDate(newState[SELECTED_DATE]);else setSelectedDate(newState);
  };

  return {
    onCalendarSelect,
    selectedDate
  };
};
/**
 * A React hook for Screentone's `Calendar` component to handle date ranges. This hook manages the
 * state for `startDate`, the start of a date range, and `endDate`, the end of a date range. This
 * hook also provides three different state setting functions for `startDate` and `endDate`.
 * `onSelectStart` and `onSelectEnd` handle setting state for `startDate` and `endDate`
 * respectively. `OnSelectStart` should be passed to a `Calendar` that handles the start of a date
 * range as the prop `onSelect` and `onSelectEnd` should be passed to a `Calendar` that handles the
 * end of a date range as the prop `onSelect`. **Be mindful to handle possible overlaps with
 * `startDate` and `endDate`. If one `Calendar` component is handling **BOTH** the start and end of
 * a date range, the `onSelect` function provided by this hook should be passed to the `Calendar` as
 * the prop of the same name. This function handles overlaps in date ranges correctly.
 *
 * @param {Date | null} [defaultStartDate=null] - default value for the calendar's `startDate`
 * @param {Date | null} [defaultEndDate=null] - default value for the calendar's `endDate`
 * @returns {{
 *   endDate: Date | null,
 *   onSelect: (newDate: Date | null) => void,
 *   onSelectEnd: (newDate: Date | null) => void,
 *   onSelectStart: (newDate: Date | null) => void,
 *   startDate: Date | null,
 * }} object containing props to be passed to Screentone's `Calendar` component
 */

const useCalendarRange = (defaultStartDate = null, defaultEndDate = null) => {
  let sanitizedStartDefault = defaultStartDate;
  let sanitizedEndDefault = defaultEndDate;

  if (sanitizedStartDefault && !(sanitizedStartDefault instanceof Date)) {
    /* eslint-disable-next-line no-console */
    console.warn("useCalendarRange can only accept parameters of type Date as default values for startDate. A value of type " + typeof defaultStartDate + " was supplied.");
    sanitizedStartDefault = null;
  }

  if (sanitizedEndDefault && !(sanitizedEndDefault instanceof Date)) {
    /* eslint-disable-next-line no-console */
    console.warn("useCalendarRange can only accept parameters of type Date as default values for endDate. A value of type " + typeof defaultEndDate + " was supplied.");
    sanitizedEndDefault = null;
  }

  const [startDate, setStartDate] = useState(sanitizedStartDefault || null);
  const [endDate, setEndDate] = useState(sanitizedEndDefault || null);
  const [nextDate, setNextDate] = useState(START_DATE); // key name of the next date to be set

  const onCalendarSelect = newState => {
    const formattedNewState = newState instanceof Date ? {
      [nextDate]: newState
    } : newState;
    ReactDOM.unstable_batchedUpdates(() => {
      handleCalendarDateSelect(setStartDate, setEndDate)(startDate, endDate)(formattedNewState);
      setNextDate(nextDate === START_DATE ? END_DATE : START_DATE);
    });
  };

  return {
    endDate,
    onCalendarSelect,
    startDate
  };
};
const useCalendarAndTime = (defaultSelectedDate = null, defaultSelectedTime = '') => {
  let sanitizedDefaultDate = defaultSelectedDate;
  let sanitizedDefaultTime = defaultSelectedTime;

  if (sanitizedDefaultDate && !(sanitizedDefaultDate instanceof Date)) {
    /* eslint-disable-next-line no-console */
    console.warn("useCalendarAndTime can only accept parameters of type Date as default values for selectedDate. A value of type " + typeof defaultSelectedDate + " was supplied.");
    sanitizedDefaultDate = null;
  } //


  if (sanitizedDefaultTime && typeof sanitizedDefaultTime !== 'string') {
    /* eslint-disable-next-line no-console */
    console.warn("useCalendarAndTime can only accept parameters of type string as default values for selectedTime. A value of type " + typeof defaultSelectedTime + " was supplied.");
    sanitizedDefaultTime = '';
  }

  const [selectedDate, setSelectedDate] = useState(sanitizedDefaultDate || null);
  const [selectedTime, setSelectedTime] = useState(sanitizedDefaultTime || '');

  const onCalendarSelect = newState => {
    ReactDOM.unstable_batchedUpdates(() => {
      const shouldUpdateDate = SELECTED_DATE in newState || SELECTED_TIME in newState && selectedDate || SELECTED_DATE in newState && selectedTime;

      if (shouldUpdateDate) {
        const date = SELECTED_DATE in newState ? newState[SELECTED_DATE] : selectedDate;
        setSelectedDate(mergeDateAndTime(date, newState.selectedTime || selectedTime));
      }

      if (SELECTED_TIME in newState) setSelectedTime(newState.selectedTime);
    });
  };

  return {
    onCalendarSelect,
    selectedDate,
    selectedTime
  };
};
/**
 * ! ...
 *
 * @param {Date} defaultStartDate // ! ...
 * @param {Date} defaultEndDate // ! ...
 * @param {string} defaultStartTime // ! ...
 * @param {string} defaultEndTime // ! ...
 * @returns {}
 */

const useCalendarAndTimeRange = (defaultStartDate = null, defaultEndDate = null, defaultStartTime = '', defaultEndTime = '') => {
  let sanitizedDefaultStartDate = defaultStartDate;
  let sanitizedDefaultEndDate = defaultEndDate;
  let sanitizedDefaultStartTime = defaultStartTime;
  let sanitizedDefaultEndTime = defaultEndTime;

  if (sanitizedDefaultStartDate && !(sanitizedDefaultStartDate instanceof Date)) {
    /* eslint-disable-next-line no-console */
    console.warn("useCalendarAndTimeRange can only accept parameters of type Date as default values for startDate. A value of type " + typeof sanitizedDefaultStartDate + " was supplied.");
    sanitizedDefaultStartDate = null;
  }

  if (sanitizedDefaultEndDate && !(sanitizedDefaultEndDate instanceof Date)) {
    /* eslint-disable-next-line no-console */
    console.warn("useCalendarAndTimeRange can only accept parameters of type Date as default values for endDate. A value of type " + typeof sanitizedDefaultEndDate + " was supplied.");
    sanitizedDefaultEndDate = null;
  }

  if (sanitizedDefaultStartTime && typeof sanitizedDefaultStartTime !== 'string') {
    /* eslint-disable-next-line no-console */
    console.warn("useCalendarAndTimeRange can only accept parameters of type string as default values for startTime. A value of type " + typeof sanitizedDefaultStartTime + " was supplied.");
    sanitizedDefaultStartTime = '';
  }

  if (sanitizedDefaultStartTime && typeof sanitizedDefaultStartTime !== 'string') {
    /* eslint-disable-next-line no-console */
    console.warn("useCalendarAndTimeRange can only accept parameters of type string as default values for endTime. A value of type " + typeof sanitizedDefaultEndTime + " was supplied.");
    sanitizedDefaultEndTime = '';
  }

  const [startDate, setStartDate] = useState(sanitizedDefaultStartDate || null);
  const [endDate, setEndDate] = useState(sanitizedDefaultEndDate || null);
  const [startTime, setStartTime] = useState(sanitizedDefaultStartTime || '');
  const [endTime, setEndTime] = useState(sanitizedDefaultEndTime || ''); // to be used on blur by the time inputs

  const onBlur = () => {
    if (startDate && endDate && startDate > endDate) {
      setStartDate(endDate);
      setEndDate(startDate);
      setStartTime(endTime);
      setEndTime(startTime);
    }
  }; // ...


  const handleCalendarSelect = newState => {
    const newStartDate = START_DATE in newState ? newState.startDate : startDate;
    const newStartTime = START_TIME in newState ? newState.startTime : startTime;
    const newEndDate = END_DATE in newState ? newState.endDate : endDate;
    const newEndTime = END_TIME in newState ? newState.endTime : endTime;

    const updatedNewState = _extends({}, newState, {
      startDate: mergeDateAndTime(newStartDate, newStartTime),
      endDate: mergeDateAndTime(newEndDate, newEndTime)
    });

    ReactDOM.unstable_batchedUpdates(() => {
      handleCalendarDateSelect(setStartDate, setEndDate)(startDate, endDate)(updatedNewState);
      handleCalendarTimeSelect(setStartTime, setEndTime)(updatedNewState);
    });
  };

  return {
    endDate,
    endTime,
    onBlur,
    onCalendarSelect: handleCalendarSelect,
    startDate,
    startTime
  };
};

/**
 * @name addMonths
 * @category Month Helpers
 * @summary Add the specified number of months to the given date.
 *
 * @description
 * Add the specified number of months to the given date.
 *
 * ### v2.0.0 breaking changes:
 *
 * - [Changes that are common for the whole library](https://github.com/date-fns/date-fns/blob/master/docs/upgradeGuide.md#Common-Changes).
 *
 * @param {Date|Number} date - the date to be changed
 * @param {Number} amount - the amount of months to be added. Positive decimals will be rounded using `Math.floor`, decimals less than zero will be rounded using `Math.ceil`.
 * @returns {Date} the new date with the months added
 * @throws {TypeError} 2 arguments required
 *
 * @example
 * // Add 5 months to 1 September 2014:
 * const result = addMonths(new Date(2014, 8, 1), 5)
 * //=> Sun Feb 01 2015 00:00:00
 */

function addMonths(dirtyDate, dirtyAmount) {
  requiredArgs(2, arguments);
  var date = toDate(dirtyDate);
  var amount = toInteger(dirtyAmount);

  if (isNaN(amount)) {
    return new Date(NaN);
  }

  if (!amount) {
    // If 0 months, no-op to avoid changing times in the hour before end of DST
    return date;
  }

  var dayOfMonth = date.getDate(); // The JS Date object supports date math by accepting out-of-bounds values for
  // month, day, etc. For example, new Date(2020, 0, 0) returns 31 Dec 2019 and
  // new Date(2020, 13, 1) returns 1 Feb 2021.  This is *almost* the behavior we
  // want except that dates will wrap around the end of a month, meaning that
  // new Date(2020, 13, 31) will return 3 Mar 2021 not 28 Feb 2021 as desired. So
  // we'll default to the end of the desired month by adding 1 to the desired
  // month and using a date of 0 to back up one day to the end of the desired
  // month.

  var endOfDesiredMonth = new Date(date.getTime());
  endOfDesiredMonth.setMonth(date.getMonth() + amount + 1, 0);
  var daysInMonth = endOfDesiredMonth.getDate();

  if (dayOfMonth >= daysInMonth) {
    // If we're already at the end of the month, then this is the correct date
    // and we're done.
    return endOfDesiredMonth;
  } else {
    // Otherwise, we now know that setting the original day-of-month value won't
    // cause an overflow, so set the desired day-of-month. Note that we can't
    // just set the date of `endOfDesiredMonth` because that object may have had
    // its time changed in the unusual case where where a DST transition was on
    // the last day of the month and its local time was in the hour skipped or
    // repeated next to a DST transition.  So we use `date` instead which is
    // guaranteed to still have the original time.
    date.setFullYear(endOfDesiredMonth.getFullYear(), endOfDesiredMonth.getMonth(), dayOfMonth);
    return date;
  }
}

/**
 * @name differenceInCalendarMonths
 * @category Month Helpers
 * @summary Get the number of calendar months between the given dates.
 *
 * @description
 * Get the number of calendar months between the given dates.
 *
 * ### v2.0.0 breaking changes:
 *
 * - [Changes that are common for the whole library](https://github.com/date-fns/date-fns/blob/master/docs/upgradeGuide.md#Common-Changes).
 *
 * @param {Date|Number} dateLeft - the later date
 * @param {Date|Number} dateRight - the earlier date
 * @returns {Number} the number of calendar months
 * @throws {TypeError} 2 arguments required
 *
 * @example
 * // How many calendar months are between 31 January 2014 and 1 September 2014?
 * var result = differenceInCalendarMonths(
 *   new Date(2014, 8, 1),
 *   new Date(2014, 0, 31)
 * )
 * //=> 8
 */

function differenceInCalendarMonths(dirtyDateLeft, dirtyDateRight) {
  requiredArgs(2, arguments);
  var dateLeft = toDate(dirtyDateLeft);
  var dateRight = toDate(dirtyDateRight);
  var yearDiff = dateLeft.getFullYear() - dateRight.getFullYear();
  var monthDiff = dateLeft.getMonth() - dateRight.getMonth();
  return yearDiff * 12 + monthDiff;
}

/**
 * @name startOfDay
 * @category Day Helpers
 * @summary Return the start of a day for the given date.
 *
 * @description
 * Return the start of a day for the given date.
 * The result will be in the local timezone.
 *
 * ### v2.0.0 breaking changes:
 *
 * - [Changes that are common for the whole library](https://github.com/date-fns/date-fns/blob/master/docs/upgradeGuide.md#Common-Changes).
 *
 * @param {Date|Number} date - the original date
 * @returns {Date} the start of a day
 * @throws {TypeError} 1 argument required
 *
 * @example
 * // The start of a day for 2 September 2014 11:55:00:
 * const result = startOfDay(new Date(2014, 8, 2, 11, 55, 0))
 * //=> Tue Sep 02 2014 00:00:00
 */

function startOfDay(dirtyDate) {
  requiredArgs(1, arguments);
  var date = toDate(dirtyDate);
  date.setHours(0, 0, 0, 0);
  return date;
}

/**
 * @name isSameDay
 * @category Day Helpers
 * @summary Are the given dates in the same day (and year and month)?
 *
 * @description
 * Are the given dates in the same day (and year and month)?
 *
 * ### v2.0.0 breaking changes:
 *
 * - [Changes that are common for the whole library](https://github.com/date-fns/date-fns/blob/master/docs/upgradeGuide.md#Common-Changes).
 *
 * @param {Date|Number} dateLeft - the first date to check
 * @param {Date|Number} dateRight - the second date to check
 * @returns {Boolean} the dates are in the same day (and year and month)
 * @throws {TypeError} 2 arguments required
 *
 * @example
 * // Are 4 September 06:00:00 and 4 September 18:00:00 in the same day?
 * var result = isSameDay(new Date(2014, 8, 4, 6, 0), new Date(2014, 8, 4, 18, 0))
 * //=> true
 * 
 * @example
 * // Are 4 September and 4 October in the same day?
 * var result = isSameDay(new Date(2014, 8, 4), new Date(2014, 9, 4))
 * //=> false
 * 
 * @example
 * // Are 4 September, 2014 and 4 September, 2015 in the same day?
 * var result = isSameDay(new Date(2014, 8, 4), new Date(2015, 8, 4))
 * //=> false
 */

function isSameDay(dirtyDateLeft, dirtyDateRight) {
  requiredArgs(2, arguments);
  var dateLeftStartOfDay = startOfDay(dirtyDateLeft);
  var dateRightStartOfDay = startOfDay(dirtyDateRight);
  return dateLeftStartOfDay.getTime() === dateRightStartOfDay.getTime();
}

/* eslint-disable import/prefer-default-export */

/**
 * Generate warning text for a given prop.
 *
 * This function is for the purpose of DRYing up warning text for props.
 *
 * @param {string} propName The name of the prop to create warning text for
 * @returns {string} Text to be printed to the console as a warning for using invalid Props
 */
const getPropWarningText = propName => "The `" + propName + "` prop passed to the Screentone Calendar component is not valid.";

/**
 * Given a range of dates, `minDate` and `maxDate`, check if `time` falls within the given range.
 * If `time` falls within the range, return `time`, otherwise return null.
 *
 * @param {Date} min A minimum date
 * @param {Date} time A date to be checked against a range of dates
 * @param {Date} max A maximum date
 * @param {number} timeInterval A number representing the amount of time (in minutes) between
 * selectable times
 * @returns {Date | null}
 */

const sanitizeDateBounds = (minDate, date, maxDate, timeInterval) => {
  const isTimeWithinBounds = date >= minDate && date <= maxDate;
  const isSelectingTime = timeInterval < NUMBER_OF_MINUTES_IN_A_DAY; // if `isSelectingTime` is false, the minimum unit of useful time for the calendar is a day. If
  // time is the same day as `minDate` or `maxDate` even if `time` is marginally outside of the
  // `minDate`/`maxDate` range, it is irrelevant and we can ignore that.

  if (!isSelectingTime) {
    return isTimeWithinBounds || isSameDay(date, minDate) || isSameDay(date, maxDate) ? date : null;
  } // since `isSelectingTime` is true, we care about the times and `time` must be strictly in between
  // `minDate` and `maxDate`


  return date >= minDate && date <= maxDate ? date : null;
};
/**
 * Given a value, convert that value into a date, if possible. If not, return null.
 *
 * @param {Date | number | string} date the current date in any data type being sanitized
 * @param {string} dateFormat format for converting date into strings
 * @returns {Date | null} a valid date representing `date` or null
 */


const sanitizeDate = (date, dateFormat) => {
  if (typeof date === 'number') {
    const cleanDate = new Date(date);
    return !Number.isNaN(cleanDate.getTime()) ? cleanDate : null;
  }

  if (!date) return null;

  if (date && typeof date.getTime === 'function' && !Number.isNaN(date.getTime())) {
    return date;
  }

  if (typeof date === 'string') {
    try {
      const cleanDate = parse(date, dateFormat, new Date());
      if (!Number.isNaN(cleanDate.getTime())) return cleanDate;
    } catch (err) {
      /* eslint-disable-next-line no-console */
      console.error('Invalid Date format supplied to Screentone Calendar component:', err);
      return null;
    }
  } // not a type that can be converted into valid date


  return null;
};
/**
 * Get a default date that minimizes the number of out of range months displayed in the calendar.
 *
 * @param {Date} defaultDate default date in case month is invalid
 * @param {Date} minDate the minimum selectable date for the calendar
 * @param {Date} maxDate the maximum selectable date for the calendar
 * @param {1|2} numberOfMonths the number of months to display in the calendar
 * @returns {Date} a new default date
 */


const getDefaultDateInNumberOfMonthsRange = (defaultDate, minDate, maxDate, numberOfMonths) => {
  const sanitizedDefault = getAccurateDate(defaultDate.getFullYear(), defaultDate.getMonth());
  let outOfRangeMonthIndex = 0;

  for (let i = numberOfMonths - 1; i > 0; i--) {
    const currentMonth = addMonths(sanitizedDefault, i);

    if (currentMonth > maxDate) {
      outOfRangeMonthIndex = i;
      break;
    }
  } // no visible months are completely out of range


  if (outOfRangeMonthIndex === 0) return defaultDate;
  const newDefaultDate = addMonths(sanitizedDefault, -outOfRangeMonthIndex); // the new default date is less than `minDate`, return `minDate` instead

  if (newDefaultDate < minDate) return minDate;
  return newDefaultDate;
};
/**
 * Given a month (as either a string or a number), make sure that `month` is an integer that falls
 * within the `minDate` and `maxDate` range. If `month` is not valid, return a valid default month.
 *
 * @param {number | string} month the month passed to the calendar component via props
 * @param {Date} minDate the minimum selectable date for the calendar
 * @param {Date} maxDate the maximum selectable date for the calendar
 * @param {Date} defaultCalendarDate default date in case month is invalid
 * @param {1|2} numberOfMonths the number of months to display in the calendar
 * @returns {number} number representing a month
 */


const sanitizeMonth = (month, minDate, maxDate, defaultCalendarDate, numberOfMonths) => {
  // because `props.month` represents the earliest month in the calendar, we run the risk of having
  // the subsequent months be out of range. Update the default date we use for month to avoid
  // displaying disabled months unnecessarily
  const defaultDate = getDefaultDateInNumberOfMonthsRange(defaultCalendarDate, minDate, maxDate, numberOfMonths); // `month` not passed as props

  if (month === null) return defaultDate.getMonth();
  const monthAsNumber = Number(month);
  const monthIndex = MONTHS.LOWERCASE.indexOf(typeof month === 'string' && month.toLowerCase()); // month is not a number and not a valid string

  if (Number.isNaN(monthAsNumber) && typeof month === 'string' && monthIndex === -1) {
    /* eslint-disable-next-line no-console */
    console.warn(getPropWarningText('month') + " The value provided to `month` is not a valid month name.");
    return defaultDate.getMonth();
  }

  const partiallySanitizedMonth = monthIndex === -1 ? Number(month) : monthIndex; // invalid number

  if (Number.isNaN(partiallySanitizedMonth) || !Number.isInteger(partiallySanitizedMonth)) {
    /* eslint-disable-next-line no-console */
    console.warn(getPropWarningText('month') + " The value provided to `month` is not a valid number.");
    return defaultDate.getMonth();
  } // out of valid month range


  if (month < 0 || month > 11) {
    /* eslint-disable-next-line no-console */
    console.warn(getPropWarningText('month') + " The value provided to `month` is less than 0 or greater than 11. `month` must be an integer from 0 (January) to 11 (december).");
    return defaultDate.getMonth();
  } // out of minDate and maxDate range


  if (differenceInCalendarMonths(maxDate, minDate) < 12 && (partiallySanitizedMonth < minDate.getMonth() || partiallySanitizedMonth > maxDate.getMonth())) {
    /* eslint-disable-next-line no-console */
    console.warn(getPropWarningText('month') + " The value provided to `month` is not within the `minDate` and `maxDate` range. Please make sure that `month` is within range.");
    return defaultDate.getMonth();
  } // valid


  return partiallySanitizedMonth;
};
/**
 * Validate the `numberOfMonths` prop. If `numberOfMonths` is anything other than 1 or 2, return 1.
 *
 * @param {number} numberOfMonths a number representing the number of months the calendar component
 * should display
 * @returns {1 | 2} a valid number representing the number of months the calendar component should
 * display
 */


const sanitizeNumberOfMonths = numberOfMonths => {
  const numberOfMonthsAsNumber = Number(numberOfMonths); // not valid

  if (numberOfMonthsAsNumber !== 1 && numberOfMonthsAsNumber !== 2) {
    /* eslint-disable-next-line no-console */
    console.warn(getPropWarningText('numberOfMonths') + " `numberOfMonths` should be either 1 or 2. The value that was passed to `numberOfMonths` was " + numberOfMonths + ".");
    return 1;
  } // valid


  return numberOfMonthsAsNumber;
};
/**
 * Validate the timeInterval passed as props. If the interval does not evenly divide into one day,
 * is smaller than the minimum allowed interval or greater than the maximum allowed time interval,
 * return the default interval.
 *
 * @param {number} timeInterval the interval between times in the time select menu (in minutes) -
 * passed as props to the Calendar component
 * @returns {number} the interval between times in the time select menu (in minutes)
 */


const sanitizeTimeInterval = timeInterval => {
  // return default time interval for empty value
  if (timeInterval === null || timeInterval === undefined) return 30;
  const DEFAULT_INTERVAL = NUMBER_OF_MINUTES_IN_A_DAY;
  const MAXIMUM_TIME_INTERVAL = NUMBER_OF_MINUTES_IN_A_DAY;
  const isInvalidTimeInterval = typeof timeInterval !== 'number' || NUMBER_OF_MINUTES_IN_A_DAY % timeInterval // interval doesn't fit into one day
  || timeInterval > MAXIMUM_TIME_INTERVAL; // interval is less than or equal to 1 day

  if (isInvalidTimeInterval) {
    /* eslint-disable-next-line no-console */
    console.warn(getPropWarningText('timeInterval') + " `timeInterval` must be a number that is divisible by " + NUMBER_OF_MINUTES_IN_A_DAY + " and less than " + MAXIMUM_TIME_INTERVAL + ".");
    return DEFAULT_INTERVAL;
  }

  return timeInterval;
};
/**
 * Validate the `weekStartsOn` prop. `weekStartsOn` should be an integer between 0 and 6
 * (inclusive) or a string representing a day of the week. 0 represents Sunday and 6 represents
 * Saturday. If any value is passed that is not valid, return 0.
 *
 * @param {number | string} weekStartsOn the first day of the week to be displayed in the calendar
 * @returns {0 | 1 | 2 | 3 | 4 | 5 | 6} a number representing a day of the week from 0 (Sunday) to 6
 * (Saturday)
 */


const sanitizeWeekStartsOn = weekStartsOn => {
  const weekStartsOnAsNumber = Number(weekStartsOn);
  const weekStartsOnIndex = WEEKDAYS.LOWERCASE.indexOf(weekStartsOn); // weekStartsOn is not a number and not a valid string

  if (Number.isNaN(weekStartsOnAsNumber) && typeof weekStartsOn === 'string' && weekStartsOnIndex === -1) {
    /* eslint-disable-next-line no-console */
    console.warn(getPropWarningText('weekStartsOn') + " The value provided to `weekStartsOn` is not a valid day of the week.");
    return 0;
  }

  const partiallySanitizedWeekStartsOn = weekStartsOnIndex === -1 ? weekStartsOnAsNumber : weekStartsOnIndex; // invalid number

  if (Number.isNaN(partiallySanitizedWeekStartsOn) || !Number.isInteger(partiallySanitizedWeekStartsOn)) {
    /* eslint-disable-next-line no-console */
    console.warn(getPropWarningText('weekStartsOn') + " The value provided to `weekStartsOn` is not a valid number.");
    return 0;
  } // is not in valid number range


  if (partiallySanitizedWeekStartsOn < 0 || partiallySanitizedWeekStartsOn > 6) {
    /* eslint-disable-next-line no-console */
    console.warn(getPropWarningText('weekStartsOn') + " The value provided to `weekStartsOn` is less than 0 or greater than 6. `weekStartsOn` must be an integer from 0 (Sunday) to 6 (Saturday).");
    return 0;
  } // valid


  return partiallySanitizedWeekStartsOn;
};
/**
 * Given a year (as either a string or a number), make sure that `year` is an integer that falls
 * within the `minDate` and `maxDate` range. If `year` is not valid, return a valid default year.
 *
 * **NOTE**: despite specifying that `year` must be a number in propTypes, we sanitize year if it is
 * a string anyway. During development passing the incorrect propTypes will output console warnings
 * so the consumer of this component should know to use year as a number, but in the off-chance that
 * in production `year` is passed as a string, we want to handle this as expected.
 *
 * @param {number | string} year the year passed to the calendar component via props
 * @param {Date} minDate the minimum selectable date for the calendar
 * @param {Date} maxDate the maximum selectable date for the calendar
 * @param {Date} defaultCalendarDate default date in case year is invalid
 * @returns {number} number representing a year
 */


const sanitizeYear = (year, month, minDate, maxDate, defaultCalendarDate) => {
  // `year` not passed as props
  if (year === null) return defaultCalendarDate.getFullYear();
  const yearAsNumber = Number(year); // invalid year

  if (Number.isNaN(yearAsNumber) || !Number.isInteger(yearAsNumber)) {
    /* eslint-disable-next-line no-console */
    console.warn(getPropWarningText('year') + " The value provided to `year` is not a valid number.");
    return defaultCalendarDate.getFullYear();
  }

  const monthAndYearDate = getAccurateDate(yearAsNumber, month); // outside of `minDate`/`maxDate` range

  if (monthAndYearDate < minDate || monthAndYearDate > maxDate) {
    /* eslint-disable-next-line no-console */
    console.warn(getPropWarningText('year') + " The value provided to `year` is not within the `minDate` and `maxDate` range. Please make sure that `month` and `year` are within range.");
    return defaultCalendarDate.getFullYear();
  } // `year` creates an invalid date. The year is likely way too large.


  if (!monthAndYearDate.getTime()) {
    /* eslint-disable-next-line no-console */
    console.warn(getPropWarningText('year') + " The value provided to `year`, " + year + ", is not a valid year. Its likely that `year` is a number that is way too large.");
    return defaultCalendarDate.getFullYear();
  } // valid


  return yearAsNumber;
};
/**
 * Given the `disabledDates` prop, sanitize the prop by ensuring that all of the properties exist on
 * the `disabledDates` object and are of the correct type. Furthermore, each property should only
 * contain values of the correct type.
 *
 * @param {{ dates: Date[], weekDays: number[] }} disabledDates An object containing information
 * about which dates should be disabled in the calendar
 * @returns {{ dates: Date[], weekDays: number[] }} A sanitized object containing information about
 * which dates should be disabled in the calendar
 */


const sanitizeDisabledDates = disabledDates => {
  const sanitizedDisabledDates = {
    dates: [],
    weekDays: []
  }; // sanitize array of dates

  if (disabledDates.dates) {
    const isDatesValid = Array.isArray(disabledDates.dates) && disabledDates.dates.every(date => date instanceof Date);

    if (isDatesValid) {
      sanitizedDisabledDates.dates = disabledDates.dates;
    } else {
      /* eslint-disable-next-line no-console */
      console.warn(getPropWarningText('disabledDates.dates') + " The value passed to `disabledDates.dates` must be an array containing only Javascript Date objects.");
    }
  } // sanitize array of numbers


  if (disabledDates.weekDays) {
    const isWeekDaysValid = Array.isArray(disabledDates.weekDays) && disabledDates.weekDays.every(weekDay => typeof weekDay === 'number' && weekDay > -1 && weekDay < 7);

    if (isWeekDaysValid) {
      sanitizedDisabledDates.weekDays = disabledDates.weekDays;
    } else {
      /* eslint-disable-next-line no-console */
      console.warn(getPropWarningText('disabledDates.weekDays') + " The value passed to `disabledDates.weekDays` must be an array containing only integers between 0 and 6 (inclusive).");
    }
  }

  return sanitizedDisabledDates;
};
/**
 * Given a time value, sanitize and return the time value.
 *
 * @param {string} time A time value represented by a string in the hh:mm format
 * @returns {string} A time value represented by a string in the hh:mm format
 */


const sanitizeTime = time => {
  if (!time) return '';
  const [hours, minutes] = time.match(timeComponents);
  const isTimeInvalid = typeof time !== 'string' || hours < 0 || hours > 23 || minutes < 0 || minutes > 61;

  if (isTimeInvalid) {
    /* eslint-disable-next-line no-console */
    console.warn("A time value passed to the Screentone Calendar.TimeInput component is not valid. The value expected was a string in the hh:mm format. The value received was `" + time + "`.");
    return '';
  }

  return time;
};
/**
 * Given a number, `step`, sanitize step and convert it from a number that represents minutes to a
 * number that represents seconds.
 *
 * @param {number} step A number in minutes
 * @returns {number} A number is seconds
 */


const sanitizeStep = step => {
  const stepIsInvalid = Number.isNaN(Number(step)) || step < 1 || step > 60;

  if (stepIsInvalid) {
    /* eslint-disable-next-line no-console */
    console.warn(getPropWarningText('step') + " The value passed to `step` must be a number between 1 and 60 (inclusive). Instead, the value " + step + " was passed to `step`");
    return 60;
  }

  return Number(step) * 60;
};

const useSanitizeCalendarPropsHook = ({
  dateFormat,
  dateRangeEnd,
  dateRangeStart,
  disabledDates,
  endDate,
  maxDate,
  minDate,
  month,
  numberOfMonths,
  selectedDate,
  showCalendarOnly,
  startDate,
  timeInterval,
  weekStartsOn,
  year
}) => useMemo(() => {
  const cleanMax = sanitizeDate(maxDate, dateFormat) || DEFAULT_MAX_DATE;
  const cleanMin = sanitizeDate(minDate, dateFormat) || DEFAULT_MIN_DATE;
  const cleanTimeInterval = sanitizeTimeInterval(timeInterval);
  const cleanEndDate = sanitizeDateBounds(cleanMin, sanitizeDate(endDate, dateFormat), cleanMax, cleanTimeInterval);
  const cleanStartDate = sanitizeDateBounds(cleanMin, sanitizeDate(startDate, dateFormat), cleanMax, cleanTimeInterval);
  const cleanSelectedDate = sanitizeDate(selectedDate, dateFormat);
  const cleanNumberOfMonths = sanitizeNumberOfMonths(numberOfMonths);
  const selected = selectedDate || dateRangeStart && startDate || dateRangeEnd && endDate || startDate || endDate;
  const defaultCalendarDate = pickDefaultDate(selected, cleanMin, cleanMax);
  const cleanMonth = sanitizeMonth(month, cleanMin, cleanMax, defaultCalendarDate, cleanNumberOfMonths);
  const cleanYear = sanitizeYear(year, cleanMonth, cleanMin, cleanMax, defaultCalendarDate);
  const isOneCalendarBeingUsedForADateRange = dateRangeStart && dateRangeEnd;
  return {
    dateFormat: dateFormat || DEFAULT_DATE_FORMAT,
    disabledDates: sanitizeDisabledDates(disabledDates),
    endDate: cleanEndDate,
    maxDate: cleanMax,
    minDate: cleanMin,
    month: cleanMonth,
    numberOfMonths: cleanNumberOfMonths,
    selectedDate: cleanSelectedDate,
    // do not show input if one calendar is being used for a date range
    showCalendarOnly: isOneCalendarBeingUsedForADateRange || showCalendarOnly,
    startDate: cleanStartDate,
    timeInterval: cleanTimeInterval,
    weekStartsOn: sanitizeWeekStartsOn(weekStartsOn),
    year: cleanYear
  };
}, [dateFormat, dateRangeEnd, dateRangeStart, disabledDates, endDate, maxDate, minDate, month, numberOfMonths, selectedDate, showCalendarOnly, startDate, timeInterval, weekStartsOn, year]);

var useSanitizeCalendarPropsHook$1 = useSanitizeCalendarPropsHook;

/**
 * Given the total number of minutes in the selected day, `totalMinutes`, return an object
 * containing various pieces of data related to the current day.
 *
 * @param {Date} intervalDate date containing information for generating time string
 * @returns {string} formatted string representing a time in the time select menu
 */

function getTimeString(intervalDate) {
  const hours24 = intervalDate.getHours();
  const hours12 = hours24 % 12;
  const minutes = intervalDate.getMinutes();
  const formattedHours = hours12 > 0 && hours12 < 10 ? "0" + hours12 : hours12 || 12;
  const formattedMinutes = minutes < 10 ? "0" + minutes : minutes;
  return formattedHours + ":" + formattedMinutes + " " + (hours24 < 12 ? 'AM' : 'PM');
}
/**
 * Given a date, convert the date to the number of minutes in the given date.
 *
 * @param {string} time date to convert to minutes
 * @returns {number} number of minutes in given date
 */

const getTimeInMinutes = time => {
  if (!time) return 0;
  const [hours = '', minutes = ''] = time.match(timeComponents) || [];
  return Number(hours) * 60 + Number(minutes);
};
/**
 * Get the starting and ending time interval given the `minTime` and `maxTime`.
 *
 * @param {number} timeInterval The number of minutes between each selectable time
 * @param {string} minTime The minimum time that can be selected in the time interval menu
 * @param {string} maxTime The maximum time that can be selected in the time interval menu
 * @returns {[number, number]} ...
 */

const getTimeIntervalRange = (timeInterval, minTime, maxTime) => {
  const minTimeTotalMinutes = getTimeInMinutes(minTime);
  const maxTimeTotalMinutes = maxTime ? getTimeInMinutes(maxTime) : NUMBER_OF_MINUTES_IN_A_DAY;
  const totalIntervals = NUMBER_OF_MINUTES_IN_A_DAY / timeInterval;
  const startingInterval = timeInterval ? Math.floor(minTimeTotalMinutes / timeInterval) : 0;
  const endingInterval = timeInterval ? Math.ceil(maxTimeTotalMinutes / timeInterval) : totalIntervals;
  return [startingInterval, endingInterval];
};
/**
 * Given a selectedDate and a selectedTime, see if the combined date and time fall within the
 * minDate/maxDate range. If so, the date is valid, if not it is invalid.
 *
 * @param {Date} selectedDate The selected Date
 * @param {string} selectedTime The selected time in hh:mm format
 * @param {Date} minDate The minimum valid date in the calendar
 * @param {Date} maxDate The maximum valid date in the calendar
 * @returns {boolean} A flag that indicates whether or not selectedDate + time is within the valid
 * minDate/maxDate range
 */

const isValidDate = (selectedDate, selectedTime, minDate, maxDate) => {
  // we don't have a date, so its valid
  if (!selectedDate) return true;
  if (!selectedTime) return true;
  const [hours = '', minutes = ''] = selectedTime.match(timeComponents) || [];
  const dateAndTime = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate(), hours, minutes);
  return dateAndTime >= minDate && dateAndTime <= maxDate;
};
/**
 * Given a string, `time`, return a flag that indicates whether or not the time is valid. A time's
 * validity is based on whether or not the time is within the `minTime` and `maxTime` range and if
 * it is compatible with `step`.
 *
 * @param {string} time A string representing a time in hh:mm format
 * @param {string} minTime A string representing a time in hh:mm format
 * @param {string} maxTime A string representing a time in hh:mm format
 * @param {number} step A number in minutes representing the valid intervals for times
 * @returns {boolean}
 */

const isValidTime = (time, minTime, maxTime, step) => {
  const [hours = '', minutes = ''] = time.match(timeComponents) || [];
  const inputTimeInMinutes = Number(hours) * 60 + Number(minutes);
  const minTimeInMinutes = minTime ? getTimeInMinutes(minTime) : 0;
  const maxTimeInMinutes = maxTime ? getTimeInMinutes(maxTime) : NUMBER_OF_MINUTES_IN_A_DAY;
  const newDateIsInsideOfMinMaxTimeRange = inputTimeInMinutes >= minTimeInMinutes && inputTimeInMinutes <= maxTimeInMinutes;
  const isValidStep = !(inputTimeInMinutes % (step / 60));
  return newDateIsInsideOfMinMaxTimeRange && isValidStep;
};

const _excluded$2 = ["dateFormat", "error", "maxDate", "maxTime", "minDate", "minTime", "selectedDate", "selectedTime", "step", "timeInterval"];
/**
 * A React hook for Validating/Sanitizing the props of the `Calendar.TimeInput` component.
 *
 * @param {*} props Props passed to the `Calendar.TimeInput` component
 */

const useSanitizeTimeInputPropsHook = _ref => {
  let {
    dateFormat,
    error,
    maxDate,
    maxTime,
    minDate,
    minTime,
    selectedDate,
    selectedTime,
    step,
    timeInterval
  } = _ref,
      otherProps = _objectWithoutPropertiesLoose(_ref, _excluded$2);

  return useMemo(() => {
    const cleanMaxDate = sanitizeDate(maxDate, dateFormat) || DEFAULT_MAX_DATE;
    const cleanMinDate = sanitizeDate(minDate, dateFormat) || DEFAULT_MIN_DATE;
    const cleanTimeInterval = sanitizeTimeInterval(timeInterval);
    let cleanMinTime = sanitizeTime(minTime) || DEFAULT_MIN_TIME;
    let cleanMaxTime = sanitizeTime(maxTime) || DEFAULT_MAX_TIME; // handle overlapping min and max times

    if (getTimeInMinutes(cleanMaxTime) < getTimeInMinutes(cleanMinTime)) {
      cleanMinTime = DEFAULT_MIN_TIME;
      cleanMaxTime = DEFAULT_MAX_TIME;
      /* eslint-disable-next-line no-console */

      console.warn('The `minTime` and `maxTime` props supplied to the `Calendar.TimeInput` are overlapping. `minTime` should be less than `maxTime`.');
    }

    const cleanStep = sanitizeStep(step);
    const cleanSelectedTime = sanitizeTime(selectedTime);
    return _extends({
      // dateFormat, // * we don't need `dateFormat` outside of this function
      error: error || !isValidTime(cleanSelectedTime, cleanMinTime, cleanMaxTime, cleanStep),
      maxDate: cleanMaxDate,
      minDate: cleanMinDate,
      maxTime: cleanMaxTime,
      minTime: cleanMinTime,
      selectedDate: sanitizeDate(selectedDate, dateFormat),
      selectedTime: cleanSelectedTime,
      step: cleanStep,
      timeInterval: cleanTimeInterval
    }, otherProps);
  }, [dateFormat, error, maxDate, maxTime, minDate, minTime, selectedDate, selectedTime, step, timeInterval, otherProps]);
};

var useSanitizeTimeInputPropsHook$1 = useSanitizeTimeInputPropsHook;

/**
 * @name getDaysInMonth
 * @category Month Helpers
 * @summary Get the number of days in a month of the given date.
 *
 * @description
 * Get the number of days in a month of the given date.
 *
 * ### v2.0.0 breaking changes:
 *
 * - [Changes that are common for the whole library](https://github.com/date-fns/date-fns/blob/master/docs/upgradeGuide.md#Common-Changes).
 *
 * @param {Date|Number} date - the given date
 * @returns {Number} the number of days in a month
 * @throws {TypeError} 1 argument required
 *
 * @example
 * // How many days are in February 2000?
 * const result = getDaysInMonth(new Date(2000, 1))
 * //=> 29
 */

function getDaysInMonth(dirtyDate) {
  requiredArgs(1, arguments);
  var date = toDate(dirtyDate);
  var year = date.getFullYear();
  var monthIndex = date.getMonth();
  var lastDayOfMonth = new Date(0);
  lastDayOfMonth.setFullYear(year, monthIndex + 1, 0);
  lastDayOfMonth.setHours(0, 0, 0, 0);
  return lastDayOfMonth.getDate();
}

/**
 * @name startOfMonth
 * @category Month Helpers
 * @summary Return the start of a month for the given date.
 *
 * @description
 * Return the start of a month for the given date.
 * The result will be in the local timezone.
 *
 * ### v2.0.0 breaking changes:
 *
 * - [Changes that are common for the whole library](https://github.com/date-fns/date-fns/blob/master/docs/upgradeGuide.md#Common-Changes).
 *
 * @param {Date|Number} date - the original date
 * @returns {Date} the start of a month
 * @throws {TypeError} 1 argument required
 *
 * @example
 * // The start of a month for 2 September 2014 11:55:00:
 * const result = startOfMonth(new Date(2014, 8, 2, 11, 55, 0))
 * //=> Mon Sep 01 2014 00:00:00
 */

function startOfMonth(dirtyDate) {
  requiredArgs(1, arguments);
  var date = toDate(dirtyDate);
  date.setDate(1);
  date.setHours(0, 0, 0, 0);
  return date;
}

function createCalendarMatrix(month, year, numberOfMonths = 1, weekStartsOn = 0) {
  const matrix = [];

  for (let m = month; m < month + numberOfMonths; m++) {
    const currentMonthForPanel = m % 12;
    const currentYearForPanel = year + Math.floor(m / 12);
    const currentDateForPanel = getAccurateDate(currentYearForPanel, currentMonthForPanel);
    const dayOfWeekOfFirstDayOfMonth = startOfMonth(currentDateForPanel, {
      weekStartsOn
    }).getDay();
    const prevMonthOffset = dayOfWeekOfFirstDayOfMonth - weekStartsOn < 0 ? 6 + (dayOfWeekOfFirstDayOfMonth - weekStartsOn) : dayOfWeekOfFirstDayOfMonth - weekStartsOn - 1;
    matrix.push([[], [], [], [], [], []]); // fixed at 6 weeks per panel

    let currentDate = addMonths(currentDateForPanel, -1);
    let numberOfDaysInCurrentMonth = getDaysInMonth(currentDate);
    let day = numberOfDaysInCurrentMonth - prevMonthOffset;
    let limit = day + 7;

    for (let week = 0; week < 6; week++, limit += 7) {
      for (; day < limit; day++) {
        if (day > numberOfDaysInCurrentMonth) {
          currentDate = addMonths(currentDate, 1);
          numberOfDaysInCurrentMonth = getDaysInMonth(currentDate);
          limit -= day - 1;
          day = 1;
        }

        matrix[matrix.length - 1][week].push(day);
      }
    }
  }

  return matrix;
}

var MILLISECONDS_IN_DAY = 86400000;
/**
 * @name differenceInCalendarDays
 * @category Day Helpers
 * @summary Get the number of calendar days between the given dates.
 *
 * @description
 * Get the number of calendar days between the given dates. This means that the times are removed
 * from the dates and then the difference in days is calculated.
 *
 * ### v2.0.0 breaking changes:
 *
 * - [Changes that are common for the whole library](https://github.com/date-fns/date-fns/blob/master/docs/upgradeGuide.md#Common-Changes).
 *
 * @param {Date|Number} dateLeft - the later date
 * @param {Date|Number} dateRight - the earlier date
 * @returns {Number} the number of calendar days
 * @throws {TypeError} 2 arguments required
 *
 * @example
 * // How many calendar days are between
 * // 2 July 2011 23:00:00 and 2 July 2012 00:00:00?
 * const result = differenceInCalendarDays(
 *   new Date(2012, 6, 2, 0, 0),
 *   new Date(2011, 6, 2, 23, 0)
 * )
 * //=> 366
 * // How many calendar days are between
 * // 2 July 2011 23:59:00 and 3 July 2011 00:01:00?
 * const result = differenceInCalendarDays(
 *   new Date(2011, 6, 3, 0, 1),
 *   new Date(2011, 6, 2, 23, 59)
 * )
 * //=> 1
 */

function differenceInCalendarDays(dirtyDateLeft, dirtyDateRight) {
  requiredArgs(2, arguments);
  var startOfDayLeft = startOfDay(dirtyDateLeft);
  var startOfDayRight = startOfDay(dirtyDateRight);
  var timestampLeft = startOfDayLeft.getTime() - getTimezoneOffsetInMilliseconds(startOfDayLeft);
  var timestampRight = startOfDayRight.getTime() - getTimezoneOffsetInMilliseconds(startOfDayRight); // Round the number of days to the nearest integer
  // because the number of milliseconds in a day is not constant
  // (e.g. it's different in the day of the daylight saving time clock shift)

  return Math.round((timestampLeft - timestampRight) / MILLISECONDS_IN_DAY);
}

const DAY_CLASS_PREFIX = 'st_calendar__day';
const DAY_CLASSES = Object.freeze({
  DAY: DAY_CLASS_PREFIX,
  END_OF_RANGE: DAY_CLASS_PREFIX + "--end",
  IN_RANGE: DAY_CLASS_PREFIX + "--range",
  OUTSIDE: DAY_CLASS_PREFIX + "--outside",
  RANGE: DAY_CLASS_PREFIX + "--range",
  SELECTED: DAY_CLASS_PREFIX + "--selected",
  START_OF_RANGE: DAY_CLASS_PREFIX + "--start",
  TODAY: DAY_CLASS_PREFIX + "--today"
});

function getCSSForRange(currentDate, startDate, endDate) {
  const isDayInSelectedDayRange = startDate && endDate && currentDate > startDate && currentDate < endDate; // start in range

  if (isSameDay(currentDate, startDate)) {
    return DAY_CLASSES.START_OF_RANGE;
  } // end in range


  if (isSameDay(currentDate, endDate)) {
    return DAY_CLASSES.END_OF_RANGE;
  } // middle of range


  if (isDayInSelectedDayRange) {
    return DAY_CLASSES.RANGE;
  }

  return '';
}

function getDayCSS(currentDate, selectedDate, startDate, endDate, hoverDayRange, isDayDisabled) {
  const isSelectedDay = startDate || endDate ? isSameDay(startDate, currentDate) || isSameDay(endDate, currentDate) : isSameDay(selectedDate, currentDate);
  const isToday = isSameDay(currentDate, new Date());
  const isCompleteDayRange = startDate && endDate;
  const isHoverDayRange = (startDate || endDate) && hoverDayRange.length;
  const classNames = [DAY_CLASSES.DAY];

  if (isDayDisabled) {
    classNames.push(DAY_CLASSES.OUTSIDE); // complete date range (when the start and end aren't the same day)
  } else if (isCompleteDayRange && !isSameDay(startDate, endDate)) {
    classNames.push(getCSSForRange(currentDate, startDate, endDate)); // incomplete date range (when the start and end aren't the same day)
  } else if (isHoverDayRange && !isSameDay(hoverDayRange[0], hoverDayRange[1])) {
    classNames.push(getCSSForRange(currentDate, hoverDayRange[0], hoverDayRange[1]));
  } else if (isSelectedDay) {
    classNames.push(DAY_CLASSES.SELECTED);
  } // add class to today, even if outside


  if (isToday) classNames.push(DAY_CLASSES.TODAY);
  return classNames.join(' ');
} // if a month with range classes goes out of view (e.g. user navigates to new month), the day ref
// will be null

function doesDayRefExist(day, refs) {
  // `refs.days[day]` will be undefined if `refs.days` does not include `day`. This can happen if
  // `day` has not yet been shown in the calendar (e.g. if somehow the user navigated to June from
  // January directly without the calendar rendering February through May).
  return !!refs.days[day] && !!refs.days[day].current;
}

function addClassToRange(range, refs) {
  const sizeOfNewRange = differenceInCalendarDays(range[1], range[0]);

  for (let i = 0; i < sizeOfNewRange; i++) {
    const current = addDays(range[0], i); // add `i` days to the first day in range

    if (doesDayRefExist(current, refs)) {
      refs.days[current].current.classList.add(DAY_CLASSES.IN_RANGE);
    }
  }
}
function removeClassFromRange(range, refs) {
  const sizeOfOldRange = differenceInCalendarDays(range[1], range[0]);

  for (let i = 0; i < sizeOfOldRange; i++) {
    const current = addDays(range[0], i); // add `i` days to the first day in range

    if (doesDayRefExist(current, refs)) {
      refs.days[current].current.classList.remove(DAY_CLASSES.START_OF_RANGE);
      refs.days[current].current.classList.remove(DAY_CLASSES.IN_RANGE);
      refs.days[current].current.classList.remove(DAY_CLASSES.END_OF_RANGE);
    }
  }
}
function handleOverlappingRanges(oldRange, newRange, refs) {
  const beginningOfOldRangeIsNotInNewRange = oldRange[0] < newRange[0];
  const endOfOldRangeIsNotInNewRange = newRange[1] < oldRange[1]; // remove classes

  if (beginningOfOldRangeIsNotInNewRange) removeClassFromRange([oldRange[0], newRange[0]], refs);
  if (endOfOldRangeIsNotInNewRange) removeClassFromRange([newRange[1], oldRange[1]], refs);
  const beginningOfNewRangeIsNotInOldRange = newRange[0] < oldRange[0];
  const endOfNewRangeIsNotInOldRange = oldRange[1] < newRange[1]; // add classes

  if (beginningOfNewRangeIsNotInOldRange) addClassToRange([newRange[0], oldRange[0]], refs);
  if (endOfNewRangeIsNotInOldRange) addClassToRange([oldRange[1], newRange[1]], refs);
}
function addClassesToEndsOfRange(oldRange, newRange, refs) {
  // remove old class and add new class if the ref is still in range
  if (oldRange[0] && doesDayRefExist(oldRange[0], refs)) {
    refs.days[oldRange[0]].current.classList.remove(DAY_CLASSES.START_OF_RANGE);
  }

  if (oldRange[1] && doesDayRefExist(oldRange[1], refs)) {
    refs.days[oldRange[1]].current.classList.remove(DAY_CLASSES.END_OF_RANGE);
  }

  const isNewRangeOneDayLong = isSameDay(newRange[0], newRange[1]); // add the new class

  if (doesDayRefExist(newRange[0], refs) && !isNewRangeOneDayLong) {
    refs.days[newRange[0]].current.classList.add(DAY_CLASSES.START_OF_RANGE);
  }

  if (doesDayRefExist(newRange[1], refs) && !isNewRangeOneDayLong) {
    refs.days[newRange[1]].current.classList.add(DAY_CLASSES.END_OF_RANGE);
  }
}

const _excluded$1 = ["children", "onSelect"];
// ! seems that context runs, then setState is activated then context runs again

const CalendarContext = React.createContext();

const CalendarProvider = _ref => {
  let {
    children,
    onSelect
  } = _ref,
      props = _objectWithoutPropertiesLoose(_ref, _excluded$1);

  const refRef = useRef({
    calendar: {
      current: null
    },
    currentTime: {
      current: null
    },
    days: {},
    hoverDayRange: {
      current: []
    },
    menu: {
      current: []
    },
    timeIntervals: {}
  });
  const selected = props.selectedDate || props.dateRangeStart && props.startDate || props.dateRangeEnd && props.endDate || null;
  const defaultInputValue = selected && selected instanceof Date && format(selected, props.dateFormat);
  const [inputDate, setInputDate] = useState(selected); // used to keep input in sync with selected

  const [inputValue, setInputValue] = useState(defaultInputValue || '');
  const [isCalendarOpen, setIsCalendarOpen] = useState(props.showCalendarOnly || false);
  const [month, setMonth] = useState(props.month);
  const [year, setYear] = useState(props.year);
  const isSelectedDateInvalid = !isDateWithinRange(selected, props.minDate, props.maxDate);
  const isInputDateInvalid = !isDateWithinRange(inputDate, getDateWithTime(props.minDate), getDateWithTime(props.maxDate, '23:59'));
  const isInputValueDateInvalid = !isDateWithinRange(sanitizeDate(inputValue, props.dateFormat), getDateWithTime(props.minDate), getDateWithTime(props.maxDate, '23:59'));
  const err = props.error || isSelectedDateInvalid || isInputDateInvalid || isInputValueDateInvalid;
  const [error, setError] = useState(err);
  const [oldProps, setOldProps] = useState({
    dateFormat: props.dateFormat,
    error: err,
    month: props.month,
    year: props.year
  });
  const propsHaveChanged = oldProps.dateFormat !== props.dateFormat || oldProps.error !== err || oldProps.month !== props.month || oldProps.year !== props.year; // derived state from props

  if (inputDate !== selected || propsHaveChanged) {
    ReactDOM.unstable_batchedUpdates(() => {
      if (inputDate !== selected || oldProps.dateFormat !== props.dateFormat) {
        // the input value can become out of sync with the actual selected date if we need to swap
        // `selectedDate`, `startDate` or `endDate`. (e.g. if `startDate` and `endDate` overlap).
        // Update `inputValue` with the correct value if the dates become out of sync.
        setInputValue(defaultInputValue || '');
        setInputDate(selected); // if one end of a range has been cleared remotely (e.g. user cleared a controlled input
        // with selectedDate that doesn't directly belong to the calendar), we need to manually
        // clear the range CSS

        if ((props.dateRangeStart || props.dateRangeEnd) && !selected) {
          removeClassFromRange(refRef.current.hoverDayRange.current, refRef.current);
          refRef.current.hoverDayRange.current = [];
        }
      } // if the month/year props have been changed, update


      if (oldProps.month !== props.month) setMonth(props.month);
      if (oldProps.year !== props.year) setYear(props.year); // update error internal error state if error prop has changed

      if (oldProps.error !== err) {
        setError(err);
      } // these are the props that are related to the internal state of the calendar, we need to
      // track these props and update them when they change


      setOldProps({
        dateFormat: props.dateFormat,
        error: err,
        month: props.month,
        year: props.year
      });
    });
  }

  const weekFormat = useMemo(() => WEEKDAYS.STARTCASE.slice(props.weekStartsOn).concat(WEEKDAYS.STARTCASE.slice(0, props.weekStartsOn)), [props.weekStartsOn]);
  const calendarMatrix = useMemo(() => createCalendarMatrix(month, year, props.numberOfMonths, props.weekStartsOn), [month, props.numberOfMonths, props.weekStartsOn, year]);
  return /*#__PURE__*/React.createElement(CalendarContext.Provider, {
    value: {
      props,
      refs: refRef.current,
      state: {
        calendarMatrix,
        // inputDate, // * since we don't use this anywhere else we don't need to export it
        error,
        inputValue,
        isCalendarOpen,
        month,
        weekFormat,
        year
      },
      setState: {
        inputDate: setInputDate,
        error: setError,
        inputValue: setInputValue,
        isCalendarOpen: setIsCalendarOpen,
        month: setMonth,
        onSelect,
        year: setYear
      }
    }
  }, children);
};

CalendarProvider.propTypes = {
  /** A flag indicating whether or not buttons for month navigation are displayed, default true */
  canChangeMonth: PropTypes.bool,

  /**
   * An object containing props to pass directly to the Calendar component. **NOTE:** use
   * `inputProps` to pass `className`, `style`, etc.
   */
  calendarProps: PropTypes.shape(Box.propTypes),

  /**
   * A string representing the format returned for dates. The default `dateFormat` is `yyyy-MM-dd`.
   * **NOTE:** `dateFormat` should not include time values. If you wish to use time values, import
   * `Calendar.TimeInput`.
   */
  dateFormat: PropTypes.string,

  /**
   * Indicates the end of a date range. The same component that uses `dateRangeEnd` should also use
   * `endDate`. If the same component uses both `dateRangeStart` and `dateRangeEnd`,
   * `showCalendarOnly` will implicitly be true.
   */
  dateRangeEnd: PropTypes.bool,

  /**
   * Indicates the start of a date range. The same component that uses `dateRangeStart` should also
   * use `startDate`. If the same component uses both `dateRangeStart` and `dateRangeEnd`,
   * `showCalendarOnly` will implicitly be true.
   */
  dateRangeStart: PropTypes.bool,

  /** A flag that indicates whether or not calendar and input is disabled. */
  disabled: PropTypes.bool,

  /**
   * An object containing information about which dates in the calendar should be disabled. The
   * property `dates` should be an array full of date objects which represents dates that should
   * not be selectable by the user. The property `weekDays` should be an array that contains numbers
   * ranging from 0 to 6 which represent a day of the week that should be disabled. The
   * `weekStartsOn` prop will not effect this prop.
   */
  disabledDates: PropTypes.shape({
    dates: PropTypes.arrayOf(PropTypes.instanceOf(Date)),
    weekDays: PropTypes.arrayOf(PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6]))
  }),

  /**
   * The ending date for a range of dates. Invalid dates are converted to null. MUST be paired
   * with `dateRangeEnd` to function as an ending date range. In order for
   * the date range to be complete, `dateRangeStart` and `startDate` must be passed
   * to either the same calendar or a separate calendar to handle the start of the range. If a
   * calendar has both `dateRangeStart` and `dateRangeEnd` props passed to it, `showCalendarOnly`
   * will implicitly be true.
   */
  endDate: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string, PropTypes.number]),

  /** Whether input is invalid, e.g. error state */
  error: PropTypes.bool,

  /**
   * A flag indicating whether or not calendar should be hidden after day selection, default true.
   */
  hideOnDayClick: PropTypes.bool,

  /**
   * An object containing props to pass directly to the input component. The default input component
   * is a regular Screentone input. A different component may be used for the input if a value is
   * passed to `inputEl`. **NOTE:** use `inputProps` to pass `className`, `componentEl`,
   * `componentRef`, etc. to the input if desired.
   */
  inputProps: PropTypes.shape(Input.propTypes),

  /** Margin for component, if any */
  margin: utils.spacing.marginProps,

  /**
   * The maximum valid date for the calendar (exclusive). Disables calendar days after this date,
   * and prevents month navigation beyond this month. Invalid dates are converted to null. The
   * default `maxDate` is 12/31/9999.
   */
  maxDate: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string, PropTypes.number]),

  /**
   * The minimum valid date for the calendar (exclusive). Disables calendar days before this date,
   * and prevents month navigation beyond this month. Invalid dates are converted to null. The
   * default `minDate` is 01/01/1900. **It should be noted that Javascript dates will not always act
   * as expected prior to the year 1900**.
   */
  minDate: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string, PropTypes.number]),

  /**
   * Starting month for calendar to be displayed at, defaults to current month. Invalid dates are
   * converted to null.
   */
  month: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11]), PropTypes.oneOf(MONTHS.LOWERCASE)]),

  /**
   * The number of months to be displayed in the calendar, either 1 or 2. Defaults to 1, any value
   * other than 1 or 2 will result in one month being displayed.
   */
  numberOfMonths: PropTypes.oneOf([1, 2]),

  /**
   * A function to handle a new date/time. If an invalid date is passed in, the date will be set to
   * null.
   */
  onSelect: PropTypes.func.isRequired,

  /** Placeholder text for the input trigger for the calendar */
  placeholder: PropTypes.string,

  /** The currently selected date. Invalid dates are converted to null */
  selectedDate: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string, PropTypes.number]),

  /**
   * A flag that indicates whether or not to show only the calendar component and not the input.
   * This prop will be implicitly true if both `dateRangeStart` and `dateRangeEnd` are passed to the
   * same calendar component.
   */
  showCalendarOnly: PropTypes.bool,

  /**
   * The starting date for a range of dates. Invalid dates are converted to null. MUST be paired
   * with `dateRangeStart` to function as a starting date range. In order for the date range to be
   * complete, `dateRangeEnd` and `endDate` must be passed to either the same calendar or a separate
   * calendar to handle the start of the range. If a calendar has both `dateRangeStart` and
   * `dateRangeEnd` props passed to it, `showCalendarOnly` will implicitly be true.
   */
  startDate: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string, PropTypes.number]),

  /**
   * An integer representing the number of minutes apart each time should be on time "time" box
   * adjacent to the calendar (must divide 1440 (minutes in a day) evenly, be divisible by 5 and 120
   * is max)
   */
  timeInterval: PropTypes.number,

  /**
   * The day of the week that should represent the first day of the week in the calendar. Sunday is
   * 0, Monday is 1, all of the way up to Saturday which is 6. Defaults to 0 (Sunday).
   */
  weekStartsOn: PropTypes.oneOfType([PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6]), PropTypes.oneOf(WEEKDAYS.LOWERCASE)]),

  /**
   * Starting `year` for calendar to be displayed at, defaults to current year. Invalid dates are
   * converted to null. `year` must be a number between 1 and 9999.
   */
  year: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string, PropTypes.number])
};
CalendarProvider.defaultProps = {
  canChangeMonth: true,
  calendarProps: {},
  dateFormat: null,
  dateRangeEnd: false,
  dateRangeStart: false,
  disabled: false,
  disabledDates: {
    dates: [],
    weekDays: [],
    holidays: false
  },
  error: false,
  endDate: null,
  hideOnDayClick: true,
  inputProps: {},
  margin: null,
  maxDate: DEFAULT_MAX_DATE,
  minDate: DEFAULT_MIN_DATE,
  month: null,
  numberOfMonths: 1,
  placeholder: null,
  selectedDate: null,
  showCalendarOnly: false,
  startDate: null,
  timeInterval: 30,
  weekStartsOn: 0,
  year: null
};
var CalendarProvider$1 = CalendarProvider;

const CalendarInput = () => {
  const {
    props: {
      dateFormat,
      dateRangeEnd,
      dateRangeStart,
      disabled,
      disabledDates,
      endDate,
      inputProps,
      maxDate,
      minDate,
      numberOfMonths,
      placeholder,
      selectedDate,
      startDate
    },
    refs,
    setState,
    state: {
      error,
      inputValue,
      month,
      year
    }
  } = useContext(CalendarContext);
  return /*#__PURE__*/React.createElement(Input, _extends({}, inputProps, {
    disabled: disabled,
    error: error,
    icon: IconCalendar,
    onBlur: e => {
      const dateFromInputValue = parse(inputValue, dateFormat, new Date()); // add an error class to the input if the input does not contain a valid date

      if (inputValue && Number.isNaN(dateFromInputValue.getDate())) {
        setState.error(true);
      } // close the calendar if the focus didn't enter the calendar


      if (!refs.calendar.current.contains(e.relatedTarget)) {
        setState.isCalendarOpen(false);
      } // the definition of this event handler shadows any on `inputProps`, make sure to call
      // inputProps.onBlur if it exists


      if (inputProps.onBlur) inputProps.onBlur(e);
    },
    onChange: e => {
      const currentCalendarDate = getAccurateDate(year, month);
      const dateFromInputValue = parse(e.target.value, dateFormat, new Date());
      const inputDateIsDisabled = disabledDates.dates.some(date => date.getTime() === dateFromInputValue.getTime()) || disabledDates.weekDays.includes(dateFromInputValue.getDay()); // once the input is valid set selectedDay

      if (!Number.isNaN(dateFromInputValue.getDate())) {
        // if dateFromInputValue is outside of the minDate maxDate range,
        // clear selectedDate and add an error.
        if (dateFromInputValue < minDate || dateFromInputValue > maxDate || inputDateIsDisabled) {
          setState.error(true);
          setState.onSelect(null);
          setState.inputDate(null);
        } else {
          setState.error(false);
          setState.onSelect(dateFromInputValue);
          setState.inputDate(dateFromInputValue);
        } // only update month and year if they aren't visible on the calendar currently


        if (dateFromInputValue < currentCalendarDate || dateFromInputValue > addDays(addMonths(currentCalendarDate, numberOfMonths), -1)) {
          setState.month(dateFromInputValue.getMonth());
          setState.year(dateFromInputValue.getFullYear());
        } // if the input was valid but isn't anymore, clear selectedDay

      } else if (selectedDate || dateRangeStart && startDate || dateRangeEnd && endDate) {
        setState.onSelect(null);
        setState.inputDate(null);
      }

      setState.inputValue(e.target.value); // the definition of this event handler shadows any on `inputProps`, make sure to call
      // inputProps.onChange if it exists

      if (inputProps.onChange) inputProps.onChange(e);
    },
    onFocus: e => {
      setState.isCalendarOpen(true); // the definition of this event handler shadows any on `inputProps`, make sure to call
      // inputProps.onFocus if it exists

      if (inputProps.onFocus) inputProps.onFocus(e);
    },
    onKeyDown: e => {
      const selected = selectedDate || startDate || endDate || null;
      handleKeyDown(e, refs, {
        shiftTab: () => {},
        // let tab function as normal
        tab: pivotFocus.menuOrCalendarDate(selected, minDate, maxDate)
      }); // the definition of this event handler shadows any on `inputProps`, make sure to call
      // inputProps.onKeyDown if it exists

      if (inputProps.onKeyDown) inputProps.onKeyDown(e);
    },
    placeholder: placeholder,
    value: inputValue
  }));
};

var CalendarInput$1 = CalendarInput;

function CalendarHeader({
  matrixMonthNumber
}) {
  const {
    props: {
      disabled
    },
    state: {
      month,
      year
    }
  } = useContext(CalendarContext);
  const monthText = addMonths(getAccurateDate(year, month), matrixMonthNumber).toLocaleString('default', {
    month: 'long'
  });
  const yearText = month + matrixMonthNumber > 11 ? year + 1 : year;
  return /*#__PURE__*/React.createElement(Typography, {
    "aria-live": "polite",
    className: "st_calendar__month_heading" + (disabled ? ' st_calendar__month_heading--disabled' : ''),
    role: "heading",
    size: "lg"
  }, monthText, ' ', yearText);
}
CalendarHeader.propTypes = {
  /**
   * A number representing which month of the matrix we are currently displaying
   */
  matrixMonthNumber: PropTypes.number.isRequired
};

function Day({
  currentDate,
  day,
  isDateDisabledByProps,
  isOutside
}) {
  const {
    props: {
      dateFormat,
      dateRangeEnd,
      dateRangeStart,
      disabled,
      endDate,
      hideOnDayClick,
      maxDate,
      minDate,
      selectedDate,
      startDate
    },
    refs,
    setState
  } = useContext(CalendarContext);
  const dayRef = useRef(null);
  const dateIsOutsideOfMinMaxRange = (isSameDay(currentDate, minDate) ? false : currentDate < minDate) || (isSameDay(currentDate, maxDate) ? false : currentDate > maxDate);
  const isDayDisabled = isOutside || isDateDisabledByProps || dateIsOutsideOfMinMaxRange || disabled; // put dayRef on `refs` if not already there

  if (!isDayDisabled) {
    refs.days[currentDate] = dayRef;
  } // used for onClick and onKeyPress


  const handleDaySelect = e => {
    // disable behavior for outside dates or dates outside of min and max range
    if (isDayDisabled) return;
    setState.inputValue(format(currentDate, dateFormat));
    setState.onSelect(currentDate);
    setState.error(false); // close the calendar when the user clicks the time

    if (hideOnDayClick) {
      setState.isCalendarOpen(false); // * prevents any possible label that wraps this component from blocking hiding the calendar
      // * by sending focus to the CalendarInput

      e.preventDefault();
    }
  }; // used for onMouseEnter and onMouseOver


  const handleHover = () => {
    // the date for this side of the range has already been selected, don't do anything
    if (startDate && dateRangeStart && !dateRangeEnd || endDate && dateRangeEnd && !dateRangeStart) {
      // TODO: does this do anything?
      return;
    } // partially complete range


    if ((startDate || endDate) && (!startDate || !endDate)) {
      // don't update classes for outside days, we can update the classes when we are back in range
      if (isDayDisabled) {
        // TODO: should we remove the range after leaving the calendar?
        return;
      } // remove time from range start and end for compatibility with refs.days key names


      const start = getDateWithTime(startDate || currentDate);
      const end = getDateWithTime(endDate || currentDate);
      const oldRange = refs.hoverDayRange.current;
      const newRange = start < end ? [start, end] : [end, start]; // there is no currently existing range, add a complete range

      if (!oldRange.length) {
        addClassToRange(newRange, refs); // if the hover-start of the range is greater than the selected-end of the range or the
        // hover-end of the range is less than the selected-start of the range (if the range is moving
        // in the opposite direction)
      } else if (startDate && newRange[0] < startDate || endDate && newRange[1] > endDate) {
        removeClassFromRange(oldRange, refs);
        addClassToRange(newRange, refs); // the range is either expanding or shrinking, but not pivoting over the selected day
      } else {
        handleOverlappingRanges(oldRange, newRange, refs);
      } // add the bookend classes to the ends of the new range and remove it from the ends of the old
      // range


      addClassesToEndsOfRange(oldRange, newRange, refs); // update the range

      refs.hoverDayRange.current = newRange; // reset hoverDayRange when the range is completely selected
    } else if (startDate && endDate) {
      refs.hoverDayRange.current = [];
    }
  };

  return /*#__PURE__*/React.createElement("div", {
    "aria-label": currentDate.getMonth() + "/" + day + "/" + currentDate.getFullYear(),
    "aria-disabled": String(isOutside || disabled),
    "aria-selected": String(isSameDay(currentDate, selectedDate || startDate || endDate)),
    className: getDayCSS(currentDate, selectedDate, startDate, endDate, refs.hoverDayRange.current, isDayDisabled),
    onClick: handleDaySelect,
    onFocus: handleHover,
    onMouseOver: handleHover,
    onKeyDown: e => {
      const currentDateWithoutTime = getDateWithTime(currentDate);
      handleKeyDown(e, refs, {
        arrowDown: navigate.dates(currentDateWithoutTime, 7),
        arrowLeft: navigate.dates(currentDateWithoutTime, -1),
        arrowRight: navigate.dates(currentDateWithoutTime, 1),
        arrowUp: navigate.dates(currentDateWithoutTime, -7),
        enter: handleDaySelect,
        shiftTab: pivotFocus.menuOrInput()
      });
    },
    ref: !isOutside ? dayRef : null,
    role: "gridcell",
    tabIndex: isOutside ? '-1' : '0',
    type: "button"
  }, day);
}
Day.propTypes = {
  /** The date representing this day. */
  currentDate: PropTypes.instanceOf(Date).isRequired,

  /** The day of the month represented by a number */
  day: PropTypes.number.isRequired,

  /**
   * A flag that indicates whether or not the current date was disabled by the `disabledDates` prop.
   */
  isDateDisabledByProps: PropTypes.bool.isRequired,

  /** A flag that indicates whether or not the given day is padding for the current month. */
  isOutside: PropTypes.bool.isRequired
};

var MILLISECONDS_IN_MINUTE = 60000;
/**
 * @name addMinutes
 * @category Minute Helpers
 * @summary Add the specified number of minutes to the given date.
 *
 * @description
 * Add the specified number of minutes to the given date.
 *
 * ### v2.0.0 breaking changes:
 *
 * - [Changes that are common for the whole library](https://github.com/date-fns/date-fns/blob/master/docs/upgradeGuide.md#Common-Changes).
 *
 * @param {Date|Number} date - the date to be changed
 * @param {Number} amount - the amount of minutes to be added. Positive decimals will be rounded using `Math.floor`, decimals less than zero will be rounded using `Math.ceil`.
 * @returns {Date} the new date with the minutes added
 * @throws {TypeError} 2 arguments required
 *
 * @example
 * // Add 30 minutes to 10 July 2014 12:00:00:
 * const result = addMinutes(new Date(2014, 6, 10, 12, 0), 30)
 * //=> Thu Jul 10 2014 12:30:00
 */

function addMinutes(dirtyDate, dirtyAmount) {
  requiredArgs(2, arguments);
  var amount = toInteger(dirtyAmount);
  return addMilliseconds(dirtyDate, amount * MILLISECONDS_IN_MINUTE);
}

function MonthNavigationButtons() {
  const {
    props: {
      disabled,
      endDate,
      maxDate,
      minDate,
      numberOfMonths,
      selectedDate,
      startDate,
      timeInterval
    },
    refs,
    setState,
    state: {
      month,
      year
    }
  } = useContext(CalendarContext); // TODO: handle overlapping times...
  // we need to shift focus here from the overlay for accessability

  const menuRefPrevious = useRef(null);
  const menuRefNext = useRef(null);
  refs.menu = [menuRefPrevious, menuRefNext];
  const currentDate = getAccurateDate(year, month); // We want to see if the next unit of time in either direction is valid for selection.
  // `timeInterval` defaults to one day (in minutes), but smaller intervals can be used with
  // `Calendar.TimeInput`. If the next unit of time in either direction is outside of the
  // `minDate`/`maxDate` range, then disable navigation in that direction.

  const previousSelectableInterval = addMinutes(currentDate, timeInterval);
  const nextSelectableInterval = addMonths(currentDate, numberOfMonths);
  const canNavigateBackwards = previousSelectableInterval >= minDate;
  const canNavigateForwards = nextSelectableInterval <= maxDate;

  const handleFocus = e => {
    const selected = selectedDate || startDate || endDate || null;
    handleKeyDown(e, refs, {
      // TODO: probably need a shiftTab func
      shiftTab: () => {},
      // let tab function as normal
      tab: pivotFocus.calendarDate(selected, minDate, maxDate)
    });
  };

  return /*#__PURE__*/React.createElement(React.Fragment, null, canNavigateBackwards && /*#__PURE__*/React.createElement(Button, {
    "aria-label": "Previous Month",
    className: "st_calendar__navbar_button",
    componentRef: menuRefPrevious,
    disabled: disabled,
    margin: {
      right: 'lg',
      top: 'sm'
    },
    onClick: () => {
      const prevMonth = month - 1 < 0 ? 11 : month - 1;
      setState.month(prevMonth);
      if (month - 1 < 0) setState.year(year - 1);
    },
    onKeyDown: canNavigateForwards ? () => {} : handleFocus,
    tertiary: true
  }, /*#__PURE__*/React.createElement(Icon, {
    type: "caret-left"
  })), canNavigateForwards && /*#__PURE__*/React.createElement(Button, {
    "aria-label": "Next Month",
    className: "st_calendar__navbar_button",
    componentRef: menuRefNext,
    disabled: disabled,
    margin: {
      right: 'sm',
      top: 'sm'
    },
    onClick: () => {
      const nextMonth = month + 1 > 11 ? 0 : month + 1;
      setState.month(nextMonth);
      if (month + 1 > 11) setState.year(year + 1);
    },
    onKeyDown: handleFocus,
    tertiary: true
  }, /*#__PURE__*/React.createElement(Icon, {
    type: "caret-right"
  })));
}

function CalendarBody() {
  const {
    props: {
      canChangeMonth,
      disabled,
      disabledDates
    },
    state: {
      calendarMatrix,
      month,
      weekFormat,
      year
    }
  } = useContext(CalendarContext);
  return /*#__PURE__*/React.createElement("div", {
    className: "st_calendar__calendar_body"
  }, canChangeMonth && /*#__PURE__*/React.createElement(MonthNavigationButtons, null), /*#__PURE__*/React.createElement("div", {
    className: "st_calendar__month_wrapper"
  }, calendarMatrix.map((m, i) => {
    let currentMonth = month + i - 1 > 11 ? 0 : month + i - 1;
    let currentYear = !currentMonth && i > 0 ? year + 1 : year;
    let isOutside = true;
    return /*#__PURE__*/React.createElement("div", {
      key: currentMonth,
      className: "st_calendar__month",
      role: "grid"
    }, /*#__PURE__*/React.createElement(CalendarHeader, {
      matrixMonthNumber: i
    }), /*#__PURE__*/React.createElement("div", {
      className: "st_calendar__weekday_heading",
      role: "rowgroup"
    }, /*#__PURE__*/React.createElement("div", {
      className: "st_calendar__week_row",
      role: "row"
    }, weekFormat.map(dayOfWeek => /*#__PURE__*/React.createElement("div", {
      className: "st_calendar__weekday" + (disabled ? ' st_calendar__weekday--disabled' : ''),
      key: dayOfWeek,
      role: "columnheader"
    }, /*#__PURE__*/React.createElement("abbr", {
      className: "st_calendar__weekday_abbr",
      title: dayOfWeek
    }, dayOfWeek.slice(0, 2)))))), /*#__PURE__*/React.createElement("div", {
      className: "st_calendar__body",
      role: "rowgroup"
    }, m.map((week, j) =>
    /*#__PURE__*/

    /* eslint-disable-next-line react/no-array-index-key */
    React.createElement("div", {
      key: currentYear + "-" + currentMonth + "-" + j,
      className: "st_calendar__week_row",
      role: "row"
    }, week.map((day, k) => {
      if (day === 1) {
        isOutside = !isOutside;
        currentMonth = currentMonth + 1 > 11 ? 0 : currentMonth + 1;
        currentYear = !currentMonth && i > 0 ? year + 1 : year;
      }

      const currentDate = getAccurateDate(currentYear, currentMonth, day);
      const isDateDisabledByProps = disabledDates.weekDays.includes(k) || disabledDates.dates.some(date => date.getTime() === currentDate.getTime());
      return /*#__PURE__*/React.createElement(Day, {
        key: day,
        currentDate: currentDate,
        day: day,
        isDateDisabledByProps: isDateDisabledByProps,
        isOutside: isOutside
      });
    })))));
  })));
}

function CalendarWrapper() {
  const {
    props: {
      calendarProps,
      endDate,
      margin,
      maxDate,
      minDate,
      selectedDate,
      showCalendarOnly,
      startDate
    },
    refs,
    setState,
    state: {
      isCalendarOpen
    }
  } = useContext(CalendarContext);
  let cssOverlayWrapperClasses = ['st_calendar']; // apply user's css directly to overlay

  if (calendarProps.className) {
    cssOverlayWrapperClasses = utils.classNames.addClassNames(calendarProps.className, cssOverlayWrapperClasses);
  } // position calendar absolutely relative to input


  if (!showCalendarOnly) {
    cssOverlayWrapperClasses = utils.classNames.addClassNames('st_calendar--with_calendar_input', cssOverlayWrapperClasses);
  } // allows calendar to be closed


  if (!isCalendarOpen && !showCalendarOnly) return null;
  return /*#__PURE__*/React.createElement(Box, _extends({}, calendarProps, {
    arrow: showCalendarOnly ? null : 'top' // // className={overlayWrapperClasses}
    ,
    className: cssOverlayWrapperClasses.join(' '),
    componentRef: refs.calendar,
    margin: showCalendarOnly ? margin : {
      left: 'md',
      top: 'md'
    },
    onBlur: e => {
      if (!refs.calendar.current.contains(e.relatedTarget)) {
        setState.isCalendarOpen(false);
      } // if `calendarProps` had an `onBlur` event handler, make sure to call it here


      if (calendarProps.onBlur) calendarProps.onBlur(e);
    },
    onFocus: e => {
      setState.isCalendarOpen(true);

      if (showCalendarOnly) {
        const selected = selectedDate || startDate || endDate || null;
        handleKeyDown(e, refs, {
          shiftTab: () => {},
          // let tab function as normal
          tab: pivotFocus.menuOrCalendarDate(selected, minDate, maxDate)
        });
      } // if `calendarProps` had an `onFocus` event handler, make sure to call it here


      if (calendarProps.onFocus) calendarProps.onFocus(e);
    },
    tabIndex: "0" // * allows this element to become e.target onBlur in the input

  }), /*#__PURE__*/React.createElement(CalendarBody, null));
}

const IntervalList = ({
  disabled,
  handleTimeSelect,
  maxDate,
  maxTime,
  minDate,
  minTime,
  selectedDate,
  selectedTime,
  timeInterval
}) => {
  const dateInUse = selectedDate || new Date();
  const dateInUseWithoutTime = new Date(dateInUse.getFullYear(), dateInUse.getMonth(), dateInUse.getDate());
  const [startingInterval, endingInterval] = getTimeIntervalRange(timeInterval, minTime, maxTime);
  const intervals = [];

  for (let i = startingInterval; i < endingInterval; i++) {
    const totalMinutes = i * timeInterval;
    const intervalDate = addMinutes(dateInUseWithoutTime, totalMinutes);
    const shouldDisable = disabled || selectedDate && (intervalDate < minDate || intervalDate > maxDate);
    const selectedTimeInMinutes = selectedTime && getTimeInMinutes(selectedTime);
    const isSelected = totalMinutes === selectedTimeInMinutes;
    intervals.push( /*#__PURE__*/React.createElement(Button, {
      className: "st_calendar__interval-button " + (isSelected ? 'st_calendar__interval_button--selected' : ''),
      disabled: shouldDisable,
      key: i,
      onClick: () => {
        let minutes = intervalDate.getMinutes();
        if (minutes < 10) minutes = "0" + minutes;
        let hours = intervalDate.getHours();
        if (hours >= 0 && hours < 10) hours = "0" + hours;
        handleTimeSelect(hours + ":" + minutes);
      },
      tabIndex: "-1" // disables tabbing into interval list menu
      ,
      tertiary: true,
      title: shouldDisable ? 'This time is out of range.' : ''
    }, getTimeString(intervalDate)));
  }

  return intervals;
};

IntervalList.propTypes = {
  disabled: PropTypes.bool.isRequired,
  handleTimeSelect: PropTypes.func.isRequired,
  maxDate: PropTypes.instanceOf(Date).isRequired,
  maxTime: time,
  minDate: PropTypes.instanceOf(Date).isRequired,
  minTime: time,
  selectedDate: PropTypes.instanceOf(Date),
  selectedTime: time,
  timeInterval: PropTypes.number.isRequired
};
var IntervalList$1 = IntervalList;

const _excluded = ["className", "componentRef", "disabled", "error", "inputProps", "margin", "maxDate", "maxTime", "minDate", "minTime", "onSelect", "selectedDate", "selectedTime", "step", "timeInterval"];

const TimeInput = props => {
  const _useSanitizeTimeInput = useSanitizeTimeInputPropsHook$1(props),
        {
    className,
    componentRef,
    disabled,
    error: err,
    inputProps,
    margin,
    maxDate,
    maxTime,
    minDate,
    minTime,
    onSelect,
    selectedDate,
    selectedTime,
    step,
    timeInterval
  } = _useSanitizeTimeInput,
        otherProps = _objectWithoutPropertiesLoose(_useSanitizeTimeInput, _excluded);

  const [error, setError] = useState(err);
  const [isOpen, setIsOpen] = useState(false);
  const [oldProps, setOldProps] = useState({
    error: err,
    selectedDate,
    selectedTime
  });
  const [timeInputValue, setTimeInputValue] = useState(selectedTime);
  const timeInputWrapperRef = useRef(null);
  const propsHaveChanged = oldProps.error !== err || oldProps.selectedDate !== selectedDate || oldProps.selectedTime !== selectedTime; // derived state from props

  if (propsHaveChanged) {
    ReactDOM.unstable_batchedUpdates(() => {
      if (oldProps.error !== error) {
        setError(err); // based on selectedDate change, do we need to add/clear error
      } else if (selectedDate !== oldProps.selectedDate) {
        const nextErrorState = !isValidDate(selectedDate, selectedTime, minDate, maxDate);
        setError(nextErrorState);
      }

      if (selectedTime !== oldProps.selectedTime) {
        setTimeInputValue(selectedTime);
      } // these are the props that are related to the internal state of the calendar, we need to
      // track these props and update them when they change


      setOldProps({
        error: err,
        selectedDate,
        selectedTime
      });
    });
  }

  const handleTimeSelect = time => {
    const timeIsValid = isValidTime(time, minTime, maxTime, step);
    const dateIsValid = isValidDate(selectedDate, time, minDate, maxDate);
    setTimeInputValue(time);
    setIsOpen(false); // input is valid --- update selectedDate and clear error

    if (timeIsValid && dateIsValid) {
      onSelect(time);
      setOldProps(_extends({}, oldProps, {
        selectedTime: time
      }));
      setError(false); // input value is outside of min/max range
    } else {
      setError(true);
    }
  };

  let cssWrapperClasses = ['st_calender__time_input_wrapper'];

  if (className) {
    cssWrapperClasses = utils.classNames.addClassNames(className, cssWrapperClasses);
  }

  if (margin) {
    cssWrapperClasses = utils.spacing.addSpacingClasses({
      margin,
      cssClasses: cssWrapperClasses
    });
  }

  let cssInputClasses = ['st_calender__time_input'];

  if (inputProps.className) {
    cssInputClasses = utils.classNames.addClassNames(inputProps.className, cssInputClasses);
  }

  return /*#__PURE__*/React.createElement("div", _extends({}, otherProps, {
    className: cssWrapperClasses.join(' '),
    ref: timeInputWrapperRef
  }), /*#__PURE__*/React.createElement(Input, _extends({
    className: cssInputClasses.join(' '),
    componentRef: componentRef,
    disabled: disabled,
    error: error,
    icon: IconClock,
    type: "time",
    min: minTime // ! ?????? does this work?
    ,
    max: maxTime // ! ?????? does this work?
    ,
    step: step,
    value: timeInputValue
  }, inputProps, {
    onBlur: e => {
      if (!timeInputWrapperRef.current.contains(e.relatedTarget)) {
        setIsOpen(false);
      }

      if (inputProps.onBlur) inputProps.onBlur(e);
    },
    onChange: e => {
      handleTimeSelect(e.target.value);
      if (inputProps.onChange) inputProps.onChange(e);
    },
    onFocus: e => {
      setIsOpen(true);
      if (inputProps.onFocus) inputProps.onFocus(e);
    }
  })), isOpen ? /*#__PURE__*/React.createElement("div", {
    className: "st_calender__time_input_interval_container"
  }, /*#__PURE__*/React.createElement(IntervalList$1, {
    disabled: disabled,
    handleTimeSelect: handleTimeSelect,
    maxDate: maxDate,
    maxTime: maxTime,
    minDate: minDate,
    minTime: minTime,
    selectedDate: selectedDate,
    selectedTime: selectedTime,
    timeInterval: timeInterval
  })) : '');
};

TimeInput.propTypes = {
  componentRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({
    current: PropTypes.any
  })]),

  /**
   * A string representing the format returned for dates. The default `dateFormat` is `yyyy-MM-dd`.
   * **NOTE:** `dateFormat` should not include time values. If you wish to use time values, import
   * `Calendar.TimeInput`.
   *
   * **This prop is only necessary if you wish to pass a date string to the time input.**
   */
  dateFormat: PropTypes.string,
  disabled: PropTypes.bool,
  error: PropTypes.bool,
  inputProps: PropTypes.shape(Input.propTypes),
  maxDate: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string, PropTypes.number]),
  maxTime: time,
  minDate: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string, PropTypes.number]),
  minTime: time,
  onSelect: PropTypes.func.isRequired,
  selectedDate: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string, PropTypes.number]),
  selectedTime: time,

  /**
   * A number (in minutes) that represents what valid increments can be "stepped" through via
   * navigation with arrow keys. Defaults to `1`.
   */
  step: PropTypes.number,
  timeInterval: PropTypes.number
};
TimeInput.defaultProps = {
  componentRef: null,
  dateFormat: null,
  disabled: false,
  error: false,
  inputProps: {},
  maxDate: DEFAULT_MAX_DATE,
  maxTime: null,
  minDate: DEFAULT_MIN_DATE,
  minTime: null,
  selectedDate: null,
  selectedTime: '',
  step: 1,
  timeInterval: 30
};
var TimeInput$1 = TimeInput;

const Calendar = props => {
  // validate props, and clear their values if not valid. Also reformat props if necessary
  const sanitizedProps = useSanitizeCalendarPropsHook$1(props); // Tell the developer to use `dateRangeStart` or `dateRangeEnd` if they are trying to use the
  // calendar to select a range.

  if ((sanitizedProps.startDate || sanitizedProps.endDate) && !props.dateRangeStart && !props.dateRangeEnd) {
    const errorText = 'Missing prop `dateRangeStart` and/or `dateRangeEnd`! One or both of these props is necessary for the Screentone Calendar component to select a date range.';
    console.error(errorText);
    /* eslint-disable-line no-console */

    throw new Error(errorText);
  }

  let cssClasses = []; // ! ['st_calendar__wrapper'];

  if (props.margin) {
    cssClasses = utils.spacing.addSpacingClasses({
      margin: props.margin,
      cssClasses
    });
  }

  return /*#__PURE__*/React.createElement(CalendarProvider$1, _extends({}, props, sanitizedProps), /*#__PURE__*/React.createElement("div", {
    className: cssClasses.join(' ')
  }, !sanitizedProps.showCalendarOnly && /*#__PURE__*/React.createElement(CalendarInput$1, null), /*#__PURE__*/React.createElement(CalendarWrapper, null)));
};

Calendar.propTypes = PropTypes.shape(CalendarProvider$1.propTypes).isRequired;
Calendar.defaultProps = CalendarProvider$1.defaultProps;
Calendar.TimeInput = TimeInput$1;
var Calendar$1 = Calendar;

export { Calendar$1 as default, format, parse, useCalendar, useCalendarAndTime, useCalendarAndTimeRange, useCalendarRange };
